/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Project Builder — the 9-step (×2-phase) wizard. Ported from `project-builder.jsx`.
 * Includes the local `computeQuotation` live-preview engine and the default-timeline
 * generator. All `window.HERO_DATA` reads now go through `useData()` / a passed `D`.
 */
import { useEffect, useMemo, useState } from 'react';
import React from 'react';
import type { HeroData } from '../lib/seed';
import { useData, useDataActions } from '../context/AppDataContext';
import { useCan, useAuth } from '../context/AuthContext';
import { apiClient } from '../lib/apiClient';
import { Ico, Avatar, fmt, STATUS_META, useToast, Modal, useRowDrag } from '../components/ui';
import { DM_CATALOG, computeDmSection, defaultDmSelections, dmItemCost, LAB_SETS, searchLabRegistry, expandLabSet, labSetCategories, allSingleLabs, VAR_GROUPS, computeQuotation as sharedComputeQuotation } from '@heros/shared';
import type { DataManagementSelection, SubgroupCountMode } from '@heros/shared';

/** Per-project subgroup variable-counting options (senior request). */
const SUBGROUP_COUNT_MODES: { value: SubgroupCountMode; label: string; hint: string }[] = [
  { value: 'perVar', label: '+1 per subgrouped variable', hint: 'Each subgroup adds exactly one analysed variable, regardless of categories.' },
  { value: 'catsMinus1', label: '+(categories − 1) per variable', hint: 'One extra analysis per extra category (2 categories → +1). Default.' },
  { value: 'cats', label: '+categories per variable', hint: 'Each subgroup adds one analysed variable per category (2 categories → +2).' },
];

/** The wizard's step map. Phase 1 ends at Review Quotation; Data Management and
 *  Data Analysis & SAP are their own steps (mirroring quotation sections 2 and 3)
 *  so each cost block is edited separately and Review shows the assembled whole. */
const BUILDER_STEPS = [
  { phase: 1, id: 'basics', label: 'Basics', hint: 'Project code, title, sponsor' },
  { phase: 1, id: 'design', label: 'Study Design', hint: 'Arms, blinding, sample size' },
  { phase: 1, id: 'visits', label: 'Visit Schedule', hint: 'Screening → follow-up' },
  { phase: 1, id: 'labs', label: 'Labs & Procedures', hint: 'What is collected at each visit' },
  { phase: 1, id: 'variables', label: 'Variables', hint: 'Lab parameters + non-lab vars' },
  { phase: 1, id: 'outcomes', label: 'Outcomes & Analysis', hint: 'Simple vs complex units' },
  { phase: 1, id: 'flow', label: 'Study Flow Diagram', hint: 'Visualise & share with PI' },
  { phase: 1, id: 'dm', label: 'Data Management', hint: 'DMP & CRF / eCRF cost items' },
  { phase: 1, id: 'analysis', label: 'Data Analysis & SAP', hint: 'Analysis pricing, extra analyses' },
  { phase: 1, id: 'review', label: 'Review Quotation', hint: 'All sections — auto-computed totals' },
  { phase: 2, id: 'accept', label: 'Acceptance & Agreement', hint: 'Sponsor / PI sign-off' },
  { phase: 2, id: 'timeline', label: 'Timeline & Task allocation', hint: 'After acceptance' },
];
const PHASE1_LAST = BUILDER_STEPS.findIndex((s) => s.id === 'review');
const ACCEPT_STEP = BUILDER_STEPS.findIndex((s) => s.id === 'accept');

export function ProjectBuilder({ onClose, onCreate, resumeProjectId }: { onClose: () => void; onCreate: (input: { form: any; quotation: any }) => void; resumeProjectId?: string | null }) {
  const D = useData();
  const toast = useToast();

  const [form, setForm] = useState<any>(() => {
    if (resumeProjectId) {
      // Edit an existing project: reopen ALL builder steps from the saved snapshot.
      const proj: any = D.projects.find((p: any) => p.id === resumeProjectId);
      if (proj?.builderJson) {
        try {
          const f = reviveForm(JSON.parse(proj.builderJson));
          // The snapshot's timeline is frozen at save time (and pre-restamp
          // snapshots carry local 'tbN' ids). The project's LIVE rows are
          // authoritative — adopting them keeps real ids (so a re-save diffs
          // in place instead of recreating rows, preserving compensation
          // links) and keeps statuses/owners changed since activation.
          // Live MEMBERSHIP is authoritative too: people added via the Team
          // tab (or auto-linked from timeline work) after the snapshot was
          // frozen must survive a builder re-save's members replace-set.
          const live = liveTimelineItems(proj.id, D);
          const merged = Array.isArray(proj.members) && proj.members.length
            ? { ...f, members: [...new Set([...(proj.members as string[]), ...((f.members as string[]) || [])])] }
            : f;
          return live.length ? { ...merged, timelineItems: live } : merged;
        }
        catch (e) { console.warn('builderJson snapshot could not be revived — falling back to field overlay', e); }
      }
      return formFromProject(proj, D); // legacy projects with no snapshot: best-effort
    }
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) { const parsed = JSON.parse(raw); if (parsed?.form) return reviveForm(parsed.form); }
    } catch { /* ignore corrupt/unavailable storage */ }
    return initialForm(D);
  });
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const [step, setStep] = useState<number>(() => {
    if (resumeProjectId) {
      // Continue a still-provisional project at Acceptance; a full re-edit
      // of an active project starts at Basics (step 0) so every step is reachable.
      const proj: any = D.projects.find((p: any) => p.id === resumeProjectId);
      return proj && proj.provisional ? ACCEPT_STEP : 0;
    }
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Prefer the step ID (stable across step-list changes).
        if (typeof parsed?.stepId === 'string') {
          const i = BUILDER_STEPS.findIndex((s) => s.id === parsed.stepId);
          if (i >= 0) return i;
        }
        if (typeof parsed?.step === 'number') {
          // Legacy numeric index from the 10-step era: indices 0-6 are the
          // same; old 7/8/9 (review/accept/timeline) moved +2 when the dm +
          // analysis steps were inserted. Clamp to stay in range either way.
          const migrated = parsed.step >= 7 ? parsed.step + 2 : parsed.step;
          return Math.max(0, Math.min(migrated, BUILDER_STEPS.length - 1));
        }
      }
    } catch { /* ignore */ }
    return 0;
  });

  // Furthest step the user has reached — so navigating BACK to edit an earlier
  // step does NOT strip the green ✓ from steps already completed further along.
  const [maxStep, setMaxStep] = useState<number>(step);
  useEffect(() => { setMaxStep((m) => Math.max(m, step)); }, [step]);

  const [draftSavedAt, setDraftSavedAt] = useState<string>('');

  // Autosave the new-project draft on every change so it survives leaving the page.
  // Skipped when resuming an existing provisional project (that flow loads from the API).
  useEffect(() => {
    if (resumeProjectId) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ form: serializeForm(form), step, stepId: steps[step].id, savedAt: new Date().toISOString() }));
      setDraftSavedAt(new Date().toLocaleTimeString());
    } catch { /* quota or serialise error — draft just won't persist this tick */ }
  }, [form, step, resumeProjectId]);

  const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ } };
  const discardDraft = () => { clearDraft(); setForm(initialForm(D)); setStep(0); setDraftSavedAt(''); toast('Draft discarded — started a fresh project'); };

  const steps = BUILDER_STEPS;

  const phase1Last = PHASE1_LAST;
  const phase1Complete = !!form.provisionalSaved;
  const acceptedAgreement = !!form.agreement?.accepted;
  const canSaveProvisional = form.code && form.title && form.design && form.pm && !!form.activeQuotationVersionId;
  const canFinish = canSaveProvisional && acceptedAgreement;
  // Why Activate is disabled — surfaced as the button tooltip so the gate is
  // never a silent dead end (it used to grey out with no explanation).
  const activateBlockReason = !form.code || !form.title || !form.design || !form.pm
    ? 'Fill the required Basics fields first (code, title, design, project manager)'
    : !form.activeQuotationVersionId
      ? 'Save a quotation version in “Review Quotation” and select it (radio) first'
      : !acceptedAgreement
        ? 'Accept the agreement in “Acceptance & Agreement” first'
        : '';

  const quotation = useMemo(() => computeQuotation(form, D), [form, D]);

  useEffect(() => {
    if (steps[step].id === 'timeline' && form.timelineItems.length === 0) {
      set('timelineItems', generateDefaultTimeline(form, D));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const saveProvisional = () => {
    set('provisionalSaved', true);
    set('provisionalSavedAt', new Date().toISOString().slice(0, 16).replace('T', ' '));
    toast(`Provisional project ${form.code} saved · awaiting sponsor acceptance`);
    setStep(ACCEPT_STEP);
  };

  useEffect(() => {
    if (form.__gotoPhase2 && canSaveProvisional) {
      saveProvisional();
      set('__gotoPhase2', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.__gotoPhase2]);

  const next = () => setStep((s) => {
    if (steps[s].id === 'review' && !phase1Complete) return s;
    if (steps[s].id === 'accept' && !acceptedAgreement) return s;
    return Math.min(s + 1, steps.length - 1);
  });
  const back = () => setStep((s) => Math.max(s - 1, 0));

  // Concurrent-edit guardrail: while editing an existing project, heartbeat my
  // presence every 30s and show who ELSE has it open — both sides see the
  // warning, since simultaneous saves silently overwrite each other (last wins).
  const [coEditors, setCoEditors] = useState<Array<{ id: string; name: string; lastActiveAt: string }>>([]);
  useEffect(() => {
    if (!resumeProjectId) return;
    let stopped = false;
    // Heartbeats are CHAINED so the unmount release runs strictly after any
    // in-flight beat — otherwise a late POST could resurrect the presence we
    // just dropped and show a phantom "is editing" warning for 90s.
    let inflight: Promise<unknown> = Promise.resolve();
    const beat = () => {
      inflight = inflight
        .then(() => apiClient.projects.editing(resumeProjectId))
        .then((r) => { if (!stopped) setCoEditors(r.others || []); })
        .catch(() => undefined); // advisory — never block editing on a failed heartbeat
      return inflight;
    };
    void beat();
    const timer = setInterval(() => { void beat(); }, 30_000);
    return () => {
      stopped = true;
      clearInterval(timer);
      void inflight
        .then(() => apiClient.projects.editingRelease(resumeProjectId))
        .catch(() => undefined);
    };
  }, [resumeProjectId]);

  const [activateError, setActivateError] = useState<string>('');
  const [activating, setActivating] = useState(false);
  const canCreateProj = useCan('proj.create') === 'allow';
  const [tplSaveOpen, setTplSaveOpen] = useState(false);
  const [tplStartOpen, setTplStartOpen] = useState(false);
  const finish = async () => {
    // Concurrent-edit guardrail: re-check presence AT SAVE TIME (the 30s
    // heartbeat can be stale) and make overwriting an explicit decision.
    let conflict = coEditors;
    if (resumeProjectId) {
      try {
        conflict = (await apiClient.projects.editing(resumeProjectId)).others || [];
        setCoEditors(conflict);
      } catch { /* advisory — proceed on a failed check */ }
    }
    if (conflict.length > 0) {
      const who = conflict.map((c) => c.name).join(', ');
      if (!window.confirm(`${who} ${conflict.length > 1 ? 'are' : 'is'} editing this project right now. If you save, their unsaved changes may be overwritten. Save anyway?`)) return;
    }
    setActivateError('');
    setActivating(true);
    try {
      await onCreate({ form, quotation });
      clearDraft();
      toast(`Project ${form.code} activated · ${form.timelineItems.length} timeline phases`);
    } catch (e: any) {
      const raw = e?.message ? String(e.message) : String(e);
      const friendly = /does not exist/i.test(raw)
        ? `${raw}\nThe Project Manager chosen in Basics must be a user that exists in this system (the PI field is documentation-only and is linked to the PM). Go back to Basics → Project Manager, pick a registered user, then activate again.`
        : raw;
      setActivateError(friendly);
      toast(`Could not activate ${form.code} — see the error banner.`);
    } finally {
      setActivating(false);
    }
  };

  return (
    <div className="page-pad fadein" style={{ paddingBottom: 60 }}>
      <div style={{ marginBottom: 4 }}>
        <button className="btn ghost sm" onClick={onClose}><Ico.chevLeft /> Back to projects</button>
      </div>
      <div className="page-head">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>New project</div>
            {phase1Complete && (
              <span className="chip" data-tone={acceptedAgreement ? 'green' : 'amber'}>
                <span className="dot" />
                {acceptedAgreement ? 'Phase 2 · Activation' : 'Phase 1 saved · awaiting acceptance'}
              </span>
            )}
            {!phase1Complete && (
              <span className="chip" data-tone="slate"><span className="dot" />Phase 1 · Scoping &amp; Quotation</span>
            )}
          </div>
          <h2 style={{ marginTop: 4 }}>Project builder</h2>
          <div className="sub">
            <strong>Phase 1 — Scoping &amp; Quotation:</strong> design the study, generate a quotation, save the project as provisional.
            &nbsp;<strong>Phase 2 — Activation:</strong> capture sponsor acceptance, then plan the timeline &amp; allocate tasks.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {!resumeProjectId && draftSavedAt && (
            <span style={{ fontSize: 10.5, color: 'var(--text-3)', whiteSpace: 'nowrap' }} title="Your progress is saved in this browser and restored automatically if you leave and come back">
              ✓ Draft autosaved · {draftSavedAt}
            </span>
          )}
          {!resumeProjectId && (
            <button className="btn ghost" onClick={discardDraft} title="Clear the saved draft and start a fresh project"><Ico.trash /> Discard draft</button>
          )}
          {!resumeProjectId && (
            <button className="btn" onClick={() => setTplStartOpen(true)}><Ico.plus /> Start from template</button>
          )}
          {canCreateProj && (
            <button className="btn" onClick={() => setTplSaveOpen(true)}><Ico.dl /> Save as template</button>
          )}
          {!phase1Complete ? (
            <button className="btn primary" disabled={!canSaveProvisional} onClick={saveProvisional}>
              <Ico.check /> Save provisional · {fmt.thb(quotation.grandTotal)}
            </button>
          ) : (
            <button className="btn primary" disabled={!canFinish || activating} onClick={finish} title={activateBlockReason || undefined}>
              <Ico.check /> {activating ? 'Activating…' : 'Activate project'}
            </button>
          )}
        </div>
      </div>

      {tplSaveOpen && (
        <SaveTemplateModal form={form} onClose={() => setTplSaveOpen(false)} onSaved={() => { setTplSaveOpen(false); toast('Template saved'); }} />
      )}
      {tplStartOpen && (
        <StartTemplateModal
          onClose={() => setTplStartOpen(false)}
          onChoose={(tpl) => {
            // Existing autosave persists the draft; reviveForm restores Set fields.
            setForm(reviveForm({ ...serializeForm(initialForm(D)), ...tpl.builder }));
            setStep(0);
            setTplStartOpen(false);
            toast(`Started from template “${tpl.name}”`);
          }}
        />
      )}

      {coEditors.length > 0 && (
        <div role="alert" style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 8, background: 'rgba(196,138,38,0.10)', border: '1px solid #b8860b', color: '#8a6508', fontSize: 12, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span style={{ fontSize: 16, lineHeight: 1 }}>⚠</span>
          <div style={{ flex: 1 }}>
            <strong>{coEditors.map((c) => c.name).join(', ')} {coEditors.length > 1 ? 'are' : 'is'} editing this project right now</strong>
            {' '}(last active {new Date(coEditors[0].lastActiveAt).toLocaleTimeString()}).
            {' '}If you both save, the last save wins and the other person’s changes are overwritten — coordinate before saving.
          </div>
        </div>
      )}

      {activateError && (
        <div role="alert" style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 8, background: 'rgba(158,43,37,0.08)', border: '1px solid var(--accent)', color: 'var(--accent)', fontSize: 12, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span style={{ fontSize: 16, lineHeight: 1 }}>⚠</span>
          <div style={{ flex: 1, whiteSpace: 'pre-line' }}><strong>Activation failed.</strong> {activateError}</div>
          <button className="btn ghost icon sm" onClick={() => setActivateError('')} title="Dismiss"><Ico.close /></button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 16, alignItems: 'flex-start' }}>
        <div className="card" style={{ position: 'sticky', top: 8 }}>
          <div className="card-b" style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ padding: '6px 10px', fontSize: 10, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 14, height: 14, borderRadius: '50%', background: phase1Complete ? '#4a8a55' : 'var(--navy)', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 9, fontWeight: 700 }}>1</span>
              Scoping &amp; Quotation
            </div>
            {steps.map((s, i) => {
              const active = i === step;
              // "Reached" persists the furthest progress; the active step stays
              // highlighted (navy) rather than showing as done.
              const done = !active && (i < maxStep || (i <= phase1Last && phase1Complete));
              const blocked = (s.phase === 2 && !phase1Complete);
              const showPhase2Header = i === phase1Last + 1;
              return (
                <React.Fragment key={s.id}>
                  {showPhase2Header && (
                    <div style={{ padding: '10px 10px 6px', marginTop: 4, borderTop: '1px solid var(--border)', fontSize: 10, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 14, height: 14, borderRadius: '50%', background: phase1Complete ? (acceptedAgreement ? '#4a8a55' : 'var(--navy)') : 'var(--text-3)', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 9, fontWeight: 700 }}>2</span>
                      Activation
                      {blocked && <span style={{ marginLeft: 'auto', fontSize: 9, color: 'var(--text-3)', fontWeight: 500 }}>locked</span>}
                    </div>
                  )}
                  <button
                    onClick={() => !blocked && setStep(i)}
                    className="hover-row"
                    disabled={blocked}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px', borderRadius: 6, border: 'none', background: active ? 'var(--surface-alt)' : 'transparent', cursor: blocked ? 'not-allowed' : 'pointer', textAlign: 'left', width: '100%', opacity: blocked ? 0.45 : 1 }}>
                    <div style={{ width: 22, height: 22, flex: '0 0 22px', borderRadius: '50%', background: done ? '#4a8a55' : active ? 'var(--navy)' : 'var(--surface)', border: '1px solid ' + (done || active ? 'transparent' : 'var(--border-strong)'), color: done || active ? '#fff' : 'var(--text-2)', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 600 }}>
                      {done ? '✓' : i + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: active ? 600 : 500, color: active ? 'var(--text)' : 'var(--text-2)' }}>{s.label}</div>
                      <div style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{s.hint}</div>
                    </div>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
          <div style={{ padding: 10, borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--text-2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Draft total</span>
              <span className="mono tabular" style={{ fontWeight: 700, color: 'var(--text)' }}>{fmt.thb(quotation.grandTotal)}</span>
            </div>
            <div style={{ marginTop: 4, fontSize: 10.5, color: 'var(--text-3)' }}>Updates live as you fill the wizard.</div>
          </div>
        </div>

        <div className="card">
          <div className="card-h">
            <h3>{step + 1}. {steps[step].label}</h3>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn sm" disabled={step === 0} onClick={back}><Ico.chevLeft /> Back</button>
              {steps[step].id === 'review' && !phase1Complete ? (
                <button className="btn primary sm" disabled={!canSaveProvisional} onClick={saveProvisional}><Ico.check /> Save provisional &amp; continue</button>
              ) : step < steps.length - 1 ? (
                <button className="btn primary sm" disabled={steps[step].id === 'review' && !phase1Complete} onClick={next}>Next <Ico.chev /></button>
              ) : (
                <button className="btn primary sm" disabled={!canFinish || activating} onClick={finish} title={activateBlockReason || undefined}><Ico.check /> {activating ? 'Activating…' : 'Activate project'}</button>
              )}
            </div>
          </div>
          <div className="card-b">
            {steps[step].id === 'basics' && <StepBasics form={form} set={set} />}
            {steps[step].id === 'design' && <StepDesign form={form} set={set} />}
            {steps[step].id === 'visits' && <StepVisits form={form} set={set} />}
            {steps[step].id === 'labs' && <StepLabs form={form} set={set} />}
            {steps[step].id === 'variables' && <StepVariables form={form} set={set} />}
            {steps[step].id === 'outcomes' && <StepOutcomes form={form} set={set} />}
            {steps[step].id === 'flow' && <StepStudyFlow form={form} set={set} />}
            {steps[step].id === 'dm' && <StepDataMgmt form={form} set={set} />}
            {steps[step].id === 'analysis' && <StepAnalysis form={form} set={set} quotation={quotation} />}
            {steps[step].id === 'review' && <StepReview form={form} set={set} quotation={quotation} currentUserId="u1" onContinueToPhase2={saveProvisional} canContinue={canSaveProvisional} />}
            {steps[step].id === 'accept' && <StepAccept form={form} set={set} quotation={quotation} />}
            {steps[step].id === 'timeline' && <StepTimeline form={form} set={set} currentProjectId={resumeProjectId ?? null} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function initialForm(_D: HeroData): any {
  return {
    code: 'HERO-006', title: '', shortTitle: '', design: 'Randomized Controlled Trial', area: '', sponsor: '',
    pm: 'u1', piName: '', coIsName: '', startDate: '2026-06-01', endDate: '2027-12-31',
    arms: 2, intervention: '', comparator: '', randomise: true, blinding: 'Double-blind',
    population: '',
    armGroups: [
      { id: 'arm1', label: '', role: 'Experimental / Intervention', description: '', ratio: 1 },
      { id: 'arm2', label: '', role: 'Active comparator', description: '', ratio: 1 },
    ],
    sampleSize: 200, recruitMonths: 12, treatmentMonths: 6, followupMonths: 3,
    // Study-flow diagram (senior request): % of screened participants expected to
    // FAIL screening (drives screened = round(N / (1 − pct/100))). Undefined on
    // legacy projects → falls back to the old 1.5× heuristic. Editable box text
    // overrides keyed by a stable box id.
    screenFailPct: 33, flowBoxOverrides: {},
    protocolLevel: 'Complex',
    visits: [
      { id: 'v0', name: 'Screening', type: 'Screening', week: -1, required: true, compensation: 500 },
      { id: 'v1', name: 'Baseline', type: 'Baseline', week: 0, required: true, compensation: 1000 },
      { id: 'v2', name: 'Randomisation', type: 'Randomisation', week: 0, required: true, compensation: 0 },
      { id: 'v3', name: 'Week 4 follow-up', type: 'Treatment follow-up', week: 4, required: true, compensation: 1000 },
      { id: 'v4', name: 'Week 12 follow-up', type: 'Treatment follow-up', week: 12, required: true, compensation: 1000 },
      { id: 'v5', name: 'Primary endpoint', type: 'Primary outcome', week: 24, required: true, compensation: 1500 },
      { id: 'v6', name: 'Safety follow-up', type: 'Safety follow-up', week: 36, required: false, compensation: 800 },
    ],
    labs: defaultLabs(),
    labSelections: defaultLabSelections(),
    nonLabVarGroups: {
      'Baseline characteristics': ['Age', 'Sex', 'BMI', 'Smoking history'],
      'Vital signs': ['SBP', 'DBP', 'Pulse rate', 'Temperature'],
      'Medical history': ['Hypertension', 'Diabetes mellitus', 'Dyslipidaemia'],
    },
    nonLabSelected: new Set(['Age', 'Sex', 'BMI', 'SBP', 'DBP', 'Hypertension', 'Diabetes mellitus']),
    nonLabAnalyzed: new Set(['Age', 'Sex', 'BMI', 'SBP', 'DBP', 'Hypertension', 'Diabetes mellitus']),
    outcomes: [
      { id: 'o1', name: 'Change in serum uric acid at wk 24', kind: 'Primary', simpleUnits: 4, complexUnits: 2 },
      { id: 'o2', name: '% achieving target uric acid (<6 mg/dL)', kind: 'Secondary', simpleUnits: 3, complexUnits: 0 },
      { id: 'o3', name: 'Adverse events', kind: 'Safety', simpleUnits: 5, complexUnits: 0 },
    ],
    extraAnalyses: [],
    // Seed the analysis-cost drivers from the admin-tuned formula (falls back to
    // the historical defaults). Cast: HeroData may not yet type `analysisFormula`.
    minutesPerSimple: (_D as any)?.analysisFormula?.minutesPerSimple ?? 10,
    minutesPerComplex: (_D as any)?.analysisFormula?.minutesPerComplex ?? 20,
    hourlyRate: (_D as any)?.analysisFormula?.hourlyRate ?? 2000,
    complexMultiplier: (_D as any)?.analysisFormula?.complexMultiplier ?? 1.0,
    sampleSizeTopupPct: 0, summaryReportMode: 'copy', summaryReportPct: 75, summaryReportAmount: undefined, summaryReportBasis: 'pct',
    subgroupCountMode: 'catsMinus1',
    dmpCost: 11000, crfPerForm: 1611,
    dmItems: defaultDmSelections(),
    fundingSource: '', ethics: 'Not yet submitted', ethicsRef: '', registry: '', redcap: '',
    members: ['u1'],
    provisionalSaved: false, provisionalSavedAt: '',
    quotationVersions: [], activeQuotationVersionId: null,
    agreement: { accepted: false, acceptedAt: '', sponsorConfirm: false, piConfirm: false, pmConfirm: false, revisionNote: '' },
    timelineItems: [],
  };
}

/** localStorage key for the in-progress new-project builder draft. */
const DRAFT_KEY = 'heros:builder:draft:v1';

/** Form carries two Set-bearing fields (labSelections values + nonLabSelected) that
 *  don't survive JSON. These mirror the StepReview quotation-snapshot (de)serialisation. */
function serializeForm(f: any): any {
  return {
    ...f,
    labSelections: Object.fromEntries(Object.entries(f.labSelections || {}).map(([k, v]: any) => [k, [...(v || [])]])),
    nonLabSelected: [...(f.nonLabSelected || new Set())],
    nonLabAnalyzed: [...(f.nonLabAnalyzed || f.nonLabSelected || new Set())],
  };
}
function reviveForm(o: any): any {
  const m = migrateDurationsToWeeks(o);
  return {
    ...m,
    labSelections: Object.fromEntries(Object.entries(m.labSelections || {}).map(([k, v]: any) => [k, new Set(v as any[])])),
    nonLabSelected: new Set(m.nonLabSelected || []),
    nonLabAnalyzed: new Set(m.nonLabAnalyzed || m.nonLabSelected || []),
  };
}

/** Serialise a form into a REUSABLE template skeleton: strip all project-identity and
 *  lifecycle fields (code, sponsor, PI, dates, ethics, members, timeline, quotation
 *  versions, acceptance) back to their initial defaults, while KEEPING every
 *  design-skeleton field (design, arms, visits, labs, labSelections, nonLab fields,
 *  outcomes, extraAnalyses, minutes/hourlyRate/complexMultiplier, sampleSize, dm and
 *  summaryReport fields). */
function sanitizeTemplateForm(form: any): any {
  const base = initialForm(null as any); // initialForm ignores its arg — used only for identity defaults
  const out: any = {
    ...serializeForm(form),
    code: '', title: '', shortTitle: '', sponsor: '', area: '', pm: '', piName: '', coIsName: '',
    startDate: base.startDate, endDate: base.endDate,
    fundingSource: '', ethics: 'Not yet submitted', ethicsRef: '', registry: '', redcap: '',
    members: [], timelineItems: [], quotationVersions: [], activeQuotationVersionId: null,
    provisionalSaved: false, provisionalSavedAt: '',
    agreement: { accepted: false, acceptedAt: '', sponsorConfirm: false, piConfirm: false, pmConfirm: false, revisionNote: '' },
    flowBoxOverrides: {},
  };
  delete out.__gotoPhase2;
  return out;
}

/** Save the current builder design as a reusable, identity-stripped template. */
function SaveTemplateModal({ form, onClose, onSaved }: { form: any; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast('Template name is required'); return; }
    setBusy(true);
    try {
      await apiClient.templates.create({ name: name.trim(), description: description.trim() || undefined, builder: sanitizeTemplateForm(form) });
      onSaved();
    } catch (e: any) {
      toast(String(e?.message || 'Could not save template'));
    } finally { setBusy(false); }
  };
  return (
    <Modal title="Save as template" onClose={onClose}
      footer={(
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={busy || !name.trim()} onClick={() => void save()}>{busy ? 'Saving…' : 'Save template'}</button>
        </>
      )}>
      <div className="sub" style={{ marginBottom: 10 }}>Saves the current design skeleton (study design, visits, labs, variables, outcomes, analysis pricing) with no project identity — reuse it to start new projects.</div>
      <div className="field"><label>Template name <span style={{ color: 'var(--accent)' }}>*</span></label><input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Standard RCT — complex analysis" /></div>
      <div className="field" style={{ marginTop: 8 }}><label>Description</label><textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional — when to use this template" /></div>
    </Modal>
  );
}

/** Pick a saved template to start a fresh project from. Owner/admin may delete. */
function StartTemplateModal({ onClose, onChoose }: { onClose: () => void; onChoose: (tpl: any) => void }) {
  const toast = useToast();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.roleKey === 'super' || currentUser?.roleKey === 'admin';
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const load = async () => {
    setLoading(true);
    try { setTemplates(await apiClient.templates.list()); }
    catch (e: any) { toast(String(e?.message || 'Could not load templates')); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);
  const remove = async (id: string) => {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    setBusyId(id);
    try { await apiClient.templates.remove(id); await load(); toast('Template deleted'); }
    catch (e: any) { toast(String(e?.message || 'Could not delete template')); }
    finally { setBusyId(null); }
  };
  return (
    <Modal title="Start from template" onClose={onClose}
      footer={<button className="btn" onClick={onClose}>Close</button>}>
      {loading ? (
        <div className="sub" style={{ padding: 12 }}>Loading templates…</div>
      ) : templates.length === 0 ? (
        <div className="sub" style={{ padding: 12 }}>No templates saved yet. Use “Save as template” from any project design to create one.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {templates.map((t) => {
            const mine = t.createdBy === currentUser?.id;
            return (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, border: '1px solid var(--border)', borderRadius: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{t.name}</div>
                  {t.description && <div className="sub" style={{ fontSize: 11.5 }}>{t.description}</div>}
                  <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
                    {t.createdByName || '—'} · {String(t.createdAt || '').slice(0, 10)}
                  </div>
                </div>
                {(mine || isAdmin) && (
                  <button className="btn ghost icon sm" title="Delete template" disabled={busyId === t.id} onClick={() => void remove(t.id)}><Ico.trash /></button>
                )}
                <button className="btn primary sm" onClick={() => onChoose(t)}>Use</button>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

/** Best-effort builder form for a LEGACY project that has no builderJson snapshot.
 *  Restores the fields we persist (profile + outcomes), PLUS the project's REAL
 *  timeline rows and quotation versions from the already-loaded API data — so
 *  steps 9/10 show that project's data, not wizard defaults, and the Activate
 *  gate (activeQuotationVersionId) is satisfied. Visits/labs aren't recoverable
 *  (never persisted pre-snapshot), so the user re-adds those. Projects created
 *  with the snapshot always restore fully. */
/** The project's real timeline rows, mapped to builder-form shape (real DB row
 *  ids kept, so a re-save can diff rows in place instead of recreating them). */
function liveTimelineItems(projectId: string, D: HeroData): any[] {
  return (D.timeline || [])
    .filter((t: any) => t.project === projectId)
    .slice()
    .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
    .map((t: any) => ({
      id: t.id, name: t.name, category: t.category, rowType: t.rowType || 'task',
      order: t.order ?? 0,
      durationWeeks: t.durationWeeks ?? weeksBetween(t.startDate, t.endDate),
      splits: Array.isArray(t.splits) && t.splits.length ? t.splits : (t.user ? [{ user: t.user, pct: 100 }] : []),
      user: t.user || '',
      startDate: t.startDate || '', endDate: t.endDate || '',
      start: t.start ?? 0, end: t.end ?? 0, status: t.status || 'planned',
    }));
}

function formFromProject(proj: any, D: HeroData): any {
  const base = initialForm(D);
  if (!proj) return base;
  const active = !proj.provisional;

  // Without this, step 12 starts empty and the step-change effect fabricates
  // the generic default timeline instead of showing the project's real rows.
  const timelineItems = liveTimelineItems(proj.id, D);

  // The project's real quotation rows -> version history + the active selection.
  const quots = (D.quotations || []).filter((q: any) => q.projectId === proj.id);
  const quotationVersions = quots.map((q: any) => ({
    id: q.id,
    label: q.version || 'v1.0',
    status: q.status === 'Final' ? 'Accepted' : (q.status || 'Draft'),
    createdAt: q.submitted || '',
    createdBy: proj.pi || '',
    changeNote: 'Restored from the project’s saved quotation',
    grandTotal: q.grandTotal ?? (q.sections || []).reduce((s: number, x: any) => s + (x.total || 0), 0),
    meta: {},
    snapshot: {
      protocolLevel: base.protocolLevel, sampleSize: proj.sampleSize ?? 0, arms: base.arms,
      visits: 0, outcomes: (proj.outcomes || []).length, labCount: 0, totalVars: 0,
    },
    sections: q.sections || [],
    // No designSnapshot — this version predates the builder snapshot; the
    // "Restore" action guards on its absence.
  }));
  const finalQ = quots.find((q: any) => q.status === 'Final') || quots[0];

  return {
    ...base,
    code: proj.code || base.code,
    title: proj.title || '',
    shortTitle: proj.shortTitle || '',
    design: proj.design || base.design,
    area: proj.area || '',
    sponsor: proj.sponsor || '',
    pm: proj.pi || base.pm,
    piName: proj.piName || '',
    members: Array.isArray(proj.members) ? proj.members : base.members,
    sampleSize: proj.sampleSize || base.sampleSize,
    startDate: proj.startDate || base.startDate,
    endDate: proj.endDate || base.endDate,
    ethics: proj.ethics || base.ethics,
    registry: proj.registry || base.registry,
    outcomes: Array.isArray(proj.outcomes) && proj.outcomes.length ? proj.outcomes.map((o: any) => ({
      id: o.id, name: o.name, kind: o.kind, simpleUnits: o.simpleUnits ?? 2, complexUnits: o.complexUnits ?? 0,
      tpValue: o.timepointWeeks ?? '', tpUnit: 'week', linkedVisitId: o.linkedVisitId || '',
      // Subgroups persist as plain modifier strings (legacy) OR the richer
      // {modifier,categories,prespecified} objects — accept both, default 2 cats.
      subgroups: (o.subgroups || []).map((m: any, i: number) => (typeof m === 'string'
        ? { id: 'sg' + i, modifier: m, categories: 2, prespecified: true, testInteraction: true }
        : { id: 'sg' + i, modifier: m.modifier || '', categories: m.categories ?? 2, prespecified: m.prespecified !== false, testInteraction: true })),
      sensitivity: o.sensitivity || [],
    })) : base.outcomes,
    dmItems: Array.isArray(proj.dmItems) && proj.dmItems.length ? proj.dmItems : base.dmItems,
    provisionalSaved: true,
    quotationVersions,
    activeQuotationVersionId: finalQ ? finalQ.id : null,
    timelineItems,
    agreement: active
      ? {
          ...(base.agreement || {}),
          accepted: true, acceptedAt: '',
          sponsorConfirm: true, piConfirm: true, pmConfirm: true,
          sponsorOverrideReason: 'Previously activated — acceptance on record',
          revisionNote: 'Restored from the activated project record (created before full builder snapshots).',
        }
      : { ...(base.agreement || {}), accepted: false },
  };
}

/** ISO date helpers for week-based timeline sequencing (brief Feature 8). */
const addDaysISO = (iso: string, n: number) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso; // cleared/partial date input — don't throw
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const monthIdxOf = (iso: string) => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? 0 : Math.max(0, Math.min(11, d.getMonth())); };
const validStartISO = (s: any) => (/^\d{4}-\d{2}-\d{2}/.test(String(s || '')) ? String(s).slice(0, 10) : new Date().toISOString().slice(0, 10));
const weeksBetween = (a: string, b: string) => { const da = new Date(a), db = new Date(b); if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return 1; return Math.max(1, Math.round((db.getTime() - da.getTime()) / (7 * 86400000))); };

/** The canonical clinical-research workflow order (brief Feature 8). Each task is
 *  CHAINED: start = previous task's end; end = start + durationWeeks × 7 days. The
 *  month-index start/end are DERIVED from the ISO dates for the dashboard Gantt. */
function generateDefaultTimeline(f: any, D: HeroData): any[] {
  const pm = f.pm;
  // Leave a role UNASSIGNED ('') when nobody on the team holds it, instead of
  // collapsing every task onto the PM/creator. On a thin roster (e.g. just the
  // admin) this stops "Sample size", "CRF build", etc. from auto-assigning to
  // HEROs Admin — the leader assigns the real person later (which stamps the
  // retroactive "added after the fact" badge when done past the task's date).
  const dm = D.team.find((u) => u.role.includes('Data Manager'))?.id || '';
  const ra = D.team.find((u) => u.role.includes('Research Assistant'))?.id || '';
  const stat = D.team.find((u) => u.role.includes('Statistician') && u.id !== pm)?.id || '';
  const recW = Math.max(1, monthsToWeeks(f.recruitMonths) || 52);
  const folW = Math.max(1, monthsToWeeks(f.treatmentMonths) + monthsToWeeks(f.followupMonths) || 13);

  // `phase` rows are visual divider headers that group the tasks below them
  // (senior request: 5 default phases). They carry no owner/dates/compensation.
  type StepDef =
    | { phase: string }
    | { name: string; category: string; weeks: number; splits: any[] };
  const STEPS: StepDef[] = [
    { phase: 'Phase 1' },
    { name: 'Project first meeting with PI', category: 'Initiation meeting', weeks: 1, splits: [{ user: pm, pct: 100 }] },
    { name: 'Sample size calculation', category: 'Sample size', weeks: 2, splits: [{ user: stat, pct: 100 }] },
    { name: 'Create quotation', category: 'Admin', weeks: 1, splits: [{ user: pm, pct: 100 }] },
    { phase: 'Phase 2' },
    { name: 'Protocol drafting', category: 'Protocol', weeks: 8, splits: [{ user: pm, pct: 100 }] },
    { name: 'SAP drafting', category: 'Protocol SAP', weeks: 4, splits: [{ user: stat, pct: 100 }] },
    { name: 'Ethics submission and revision', category: 'Ethics', weeks: 8, splits: [{ user: pm, pct: 100 }] },
    { phase: 'Phase pre-screening' },
    { name: 'CRF / REDCap build', category: 'CRF/eCRF', weeks: 6, splits: [{ user: dm, pct: 80 }, { user: stat, pct: 20 }] },
    { name: 'Site initiation visit and first meeting', category: 'Initiation meeting', weeks: 1, splits: [{ user: pm, pct: 100 }] },
    { phase: 'Phase recruitment' },
    { name: 'Recruitment', category: 'Recruitment', weeks: recW, splits: [{ user: ra, pct: 80 }, { user: pm, pct: 20 }] },
    { name: 'Follow-up', category: 'Follow-up', weeks: folW, splits: [{ user: ra, pct: 100 }] },
    { name: 'Data cleaning & lock', category: 'Data cleaning', weeks: 4, splits: [{ user: dm, pct: 100 }] },
    { phase: 'Phase 3' },
    { name: 'Primary analysis', category: 'Analysis', weeks: 6, splits: [{ user: stat, pct: 70 }, { user: pm, pct: 30 }] },
    // New task (senior request): right after Primary analysis, before the sponsor report.
    { name: 'Data quality / check / cleaning / QA', category: 'Data cleaning', weeks: 2, splits: [{ user: dm, pct: 70 }, { user: stat, pct: 30 }] },
    { name: 'Summary report to sponsor', category: 'Summary report', weeks: 3, splits: [{ user: stat, pct: 60 }, { user: pm, pct: 40 }] },
  ];

  let cursor = validStartISO(f.startDate);
  let taskNo = 0;
  return STEPS.map((s, i) => {
    if ('phase' in s) {
      // Divider row: no dates of its own — sits at the current boundary so the
      // Gantt axis math (which ignores phase rows) never sees a NaN.
      return {
        id: 'tbph' + i, name: s.phase, category: 'Phase', rowType: 'phase', order: i,
        durationWeeks: 0, splits: [], user: '',
        startDate: cursor, endDate: cursor, start: monthIdxOf(cursor), end: monthIdxOf(cursor), status: 'planned',
      };
    }
    const weeks = Math.max(1, Math.round(s.weeks));
    const sDate = cursor;
    const eDate = addDaysISO(sDate, weeks * 7);
    cursor = eDate; // next task starts where this one ends
    // Drop role-holders nobody fills — the row stays a Task but is left
    // unassigned (empty splits) for the leader to assign the right person.
    const splits = (s.splits || []).filter((sp: any) => sp.user);
    taskNo += 1;
    return {
      id: 'tb' + taskNo, name: s.name, category: s.category, rowType: 'task', order: i,
      durationWeeks: weeks, splits, user: splits[0]?.user || '',
      startDate: sDate, endDate: eDate, start: monthIdxOf(sDate), end: monthIdxOf(eDate), status: 'planned',
    };
  });
}

/** A lab/procedure is a PANEL of one or more sub-parameters. Each sub-parameter can be
 *  ANALYSED (drives descriptive-analysis cost) or merely COLLECTED (recorded in the CRF
 *  but not analysed). e.g. a CBC yields Hb/WBC/Platelets… but you may only analyse Hb. */
const P = (name: string, analyzed = true) => ({ name, analyzed });
/** Time-unit model: visits have a canonical `week` (used by labs/sorting/flow); a visit may
 *  also carry timeValue + timeUnit so it can be expressed in days/weeks/months/years.
 *  Conversions use 365.25 days/year (so 1 year = 52.18 wk, 1 month = 4.35 wk). */
// WEEK-ONLY duration model (brief Feature 1): Week is the ONLY user-visible duration unit.
// `toWeeks` + WK_PER_MONTH are retained solely to MIGRATE any legacy day/month/year values
// (autosaved drafts, quotation design snapshots, resumed projects) to weeks on load.
const DAYS_PER: Record<string, number> = { day: 1, week: 7, month: 365.25 / 12, year: 365.25 };
const toWeeks = (value: number, unit: string) => (value * (DAYS_PER[unit] || 7)) / 7;
/** Spec conversion constant (brief §1): months × 4.345 ≈ weeks. */
const WK_PER_MONTH = 4.345;
const monthsToWeeks = (m: number) => Math.round((m || 0) * WK_PER_MONTH);
const weeksToMonths = (w: number) => +(((w || 0) / WK_PER_MONTH).toFixed(2));
const round1 = (n: number) => (Number.isInteger(n) ? n : +Number(n).toFixed(1));
/** Human label for a visit's timepoint — ALWAYS weeks. */
function visitTimeLabel(v: any): string {
  const w = v?.week ?? v?.timeValue ?? 0;
  return `Wk ${round1(Number(w))}`;
}
/** One-time, IDEMPOTENT migration of any legacy day/month/year durations → weeks.
 *  Visits keep their canonical numeric `week`; outcome timepoints (tpValue/tpUnit) are
 *  converted; recruit/treatment/follow-up stay months INTERNALLY (displayed as weeks). */
function migrateDurationsToWeeks(f: any): any {
  if (!f || typeof f !== 'object') return f;
  const visits = Array.isArray(f.visits) ? f.visits.map((v: any) => {
    if (v && v.timeUnit && v.timeUnit !== 'week') {
      const wk = round1(toWeeks(Number(v.timeValue ?? v.week ?? 0), v.timeUnit));
      return { ...v, week: wk, timeValue: wk, timeUnit: 'week' };
    }
    if (v && v.timeValue == null) return { ...v, timeValue: v.week ?? 0, timeUnit: 'week' };
    return v;
  }) : f.visits;
  const outcomes = Array.isArray(f.outcomes) ? f.outcomes.map((o: any) => {
    if (o && o.tpUnit && o.tpUnit !== 'week' && o.tpValue != null && o.tpValue !== '') {
      return { ...o, tpValue: round1(toWeeks(Number(o.tpValue), o.tpUnit)), tpUnit: 'week' };
    }
    return o;
  }) : f.outcomes;
  return { ...f, visits, outcomes };
}
function inferVisitType(weeks: number): string {
  if (weeks < 0) return 'Screening';
  if (weeks === 0) return 'Baseline';
  return 'Treatment follow-up';
}

/** The selectable visit types. A single visit can carry SEVERAL (e.g. Screening +
 *  Baseline + Randomisation are routinely done together on day 1 / week 0). */
const VISIT_TYPES = ['Screening', 'Baseline', 'Randomisation', 'Treatment follow-up', 'End of treatment', 'Primary outcome', 'Secondary outcome', 'Safety follow-up', 'Post-treatment follow-up', 'Unscheduled visit', 'Early termination visit'];
/** A visit's type list — uses the explicit `types` array, else wraps the legacy
 *  single `type` string (back-compat for older drafts / seeds). */
function visitTypeList(v: any): string[] {
  if (Array.isArray(v?.types)) return v.types;
  return v?.type ? [v.type] : [];
}

/** Compact multi-select for visit types: a chip-summary button that opens a
 *  checkbox dropdown. Keeps the legacy `type` field synced to the first choice. */
function VisitTypeSelect({ value, onChange }: { value: string[]; onChange: (next: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const toggle = (t: string) => {
    const has = value.includes(t);
    onChange(has ? value.filter((x) => x !== t) : [...value, t]);
  };
  const openMenu = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setRect({ top: r.bottom + 2, left: r.left, width: r.width });
    setOpen(true);
  };
  return (
    <div style={{ position: 'relative' }}>
      <button ref={btnRef} type="button" className="btn sm" onClick={() => (open ? setOpen(false) : openMenu())} style={{ width: '100%', justifyContent: 'space-between', display: 'flex', gap: 6, textAlign: 'left', fontWeight: 500 }} title={value.join(', ')}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value.length ? value.join(' + ') : <span style={{ color: 'var(--text-3)' }}>Select type(s)…</span>}</span>
        <Ico.chev />
      </button>
      {open && rect && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 59 }} />
          {/* Fixed-positioned so it is never clipped by a horizontally-scrolling table wrapper. */}
          <div style={{ position: 'fixed', zIndex: 60, top: rect.top, left: rect.left, minWidth: Math.max(rect.width, 230), background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 280, overflowY: 'auto', padding: 4 }}>
            {VISIT_TYPES.map((t) => (
              <label key={t} className="hover-row" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={value.includes(t)} onChange={() => toggle(t)} style={{ accentColor: 'var(--accent)' }} />{t}
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** The default visit-schedule template (also the reset target). */
function defaultVisits(): any[] {
  return [
    { id: 'v0', name: 'Screening', type: 'Screening', week: -1, required: true, compensation: 500 },
    { id: 'v1', name: 'Baseline', type: 'Baseline', week: 0, required: true, compensation: 1000 },
    { id: 'v2', name: 'Randomisation', type: 'Randomisation', week: 0, required: true, compensation: 0 },
    { id: 'v3', name: 'Week 4 follow-up', type: 'Treatment follow-up', week: 4, required: true, compensation: 1000 },
    { id: 'v4', name: 'Week 12 follow-up', type: 'Treatment follow-up', week: 12, required: true, compensation: 1000 },
    { id: 'v5', name: 'Primary endpoint', type: 'Primary outcome', week: 24, required: true, compensation: 1500 },
    { id: 'v6', name: 'Safety follow-up', type: 'Safety follow-up', week: 36, required: false, compensation: 800 },
  ];
}

/** Typo-resistant week field: accepts only digits with an optional leading "-" (so Screening
 *  can be −1), commits a number when valid, and never gets stuck mid-typing on "-". */
function WeekInput({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const [txt, setTxt] = useState<string>(String(value ?? 0));
  useEffect(() => { setTxt(String(value ?? 0)); }, [value]);
  return (
    <input
      className="tabular mono" inputMode="numeric" value={txt}
      onChange={(e) => { const t = e.target.value; if (/^-?\d*$/.test(t)) { setTxt(t); if (t !== '' && t !== '-') onChange(parseInt(t, 10)); } }}
      onBlur={() => { if (txt === '' || txt === '-') { setTxt('0'); onChange(0); } }}
      style={{ textAlign: 'right', width: 64 }}
    />
  );
}

function defaultLabs(): any[] {
  return [
    { id: 'cbc', name: 'CBC (Complete blood count)', price: 132, parameters: [P('RBC'), P('Hemoglobin (Hb)'), P('Hematocrit (Hct)'), P('WBC'), P('Platelet count (Plt)'), P('Neutrophils'), P('Lymphocytes')] },
    { id: 'cr', name: 'Creatinine', price: 72, parameters: [P('Creatinine')] },
    { id: 'ast', name: 'AST', price: 72, parameters: [P('AST')] },
    { id: 'alt', name: 'ALT', price: 72, parameters: [P('ALT')] },
    { id: 'ua', name: 'Serum uric acid', price: 96, parameters: [P('Uric acid')] },
    { id: 'ualy', name: 'Urinalysis', price: 110, parameters: [P('pH'), P('Specific gravity'), P('Protein'), P('Glucose'), P('Ketones'), P('RBC (urine)'), P('WBC (urine)')] },
    { id: 'ecg', name: 'ECG 12-lead', price: 250, parameters: [P('ECG interpretation')] },
  ];
}

/** Sub-parameters of a lab — uses the explicit `parameters` list, else synthesises from
 *  the legacy flat `params` count (all analysed) for backward compatibility. */
function labParams(lab: any): any[] {
  if (Array.isArray(lab.parameters)) return lab.parameters;
  const n = lab.params || 0;
  return Array.from({ length: n }, (_, i) => ({ name: `${lab.name} param ${i + 1}`, analyzed: true }));
}
/** Count of sub-parameters that are ANALYSED (undefined flag = analysed, for back-compat). */
function analyzedParamCount(lab: any): number {
  return labParams(lab).filter((p: any) => p.analyzed !== false).length;
}

/** Arm/group roles (aligned to ClinicalTrials.gov arm types for future registry export). */
const ARM_ROLES = ['Experimental / Intervention', 'Active comparator', 'Placebo', 'Standard-of-care / Control'];
const isControlRole = (role: string) => /comparator|placebo|control/i.test(role || '');
/** Structured arm list — uses form.armGroups, else synthesises from the legacy
 *  arms-count + intervention/comparator fields for backward compatibility. */
function synthArmGroups(form: any): any[] {
  if (Array.isArray(form.armGroups) && form.armGroups.length) return form.armGroups;
  const n = Math.max(1, form.arms || 2);
  return Array.from({ length: n }, (_, i) => ({
    id: 'arm' + (i + 1),
    label: i === 0 ? (form.intervention || '') : i === n - 1 ? (form.comparator || '') : '',
    role: i === 0 ? 'Experimental / Intervention' : (i === n - 1 ? 'Active comparator' : 'Experimental / Intervention'),
    description: '', ratio: 1,
  }));
}
function armDesignLabel(n: number): string {
  if (n <= 1) return 'single-arm';
  if (n === 2) return 'two-arm (parallel-group)';
  if (n === 3) return 'three-arm';
  return `${n}-arm (multi-arm)`;
}

/** Single source of truth for the CONSORT participant-flow counts (senior request).
 *  `screenFailPct` = % of screened participants expected to fail screening, so
 *  screened = round(N / (1 − pct/100)). Legacy projects (no screenFailPct) keep
 *  the original 1.5× heuristic so their diagram is unchanged. Used by the on-page
 *  diagram, the HTML/Word exports, and the server-PDF payload so all four agree. */
function consortCounts(form: any): { N: number; screened: number; excluded: number } {
  const N = Number(form.sampleSize) || 0;
  const raw = form.screenFailPct;
  const hasPct = raw !== undefined && raw !== null && raw !== '';
  const pct = Math.min(95, Math.max(0, Number(raw) || 0));
  const screened = hasPct ? Math.round(N / (1 - pct / 100)) : Math.round(N * 1.5);
  return { N, screened, excluded: Math.max(0, screened - N) };
}

/** Resolve a CONSORT box's text: a user override (form.flowBoxOverrides[id]) wins,
 *  else the computed default. Keeps the editable-text feature in one place. */
function flowBoxText(form: any, id: string, fallback: string): string {
  const ov = form?.flowBoxOverrides?.[id];
  return typeof ov === 'string' && ov.trim() ? ov : fallback;
}

/* ----- Reusable mouse-resizable table columns ----------------------------------
 * Shared by the Arms editor (CSS grid) and the Visit Schedule (HTML <table>).
 * Persists per-column widths to localStorage; double-click a handle to reset it. */
function useColWidths<T extends Record<string, number>>(
  storageKey: string, defaults: T, mins: { [K in keyof T]: number },
) {
  const [cols, setCols] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return { ...defaults, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return defaults;
  });
  const drag = React.useRef<{ key: keyof T; startX: number; startW: number } | null>(null);
  // active key drives the handle highlight; a ref alone wouldn't re-render.
  const [activeCol, setActiveCol] = useState<keyof T | null>(null);

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(cols)); } catch { /* ignore */ }
  }, [cols, storageKey]);

  const startResize = (key: keyof T) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    drag.current = { key, startX: e.clientX, startW: cols[key] };
    setActiveCol(key);
    const onMove = (ev: MouseEvent) => {
      const d = drag.current;
      if (!d) return;
      const next = Math.max(mins[d.key], d.startW + (ev.clientX - d.startX));
      setCols((c) => ({ ...c, [d.key]: next }));
    };
    const onUp = () => {
      drag.current = null;
      setActiveCol(null);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };
  const resetCol = (key: keyof T) => () => setCols((c) => ({ ...c, [key]: defaults[key] }));
  return { cols, activeCol, startResize, resetCol };
}

/** A boundary resize grip for a (relatively-positioned) table header cell. Wide hit area,
 *  unclipped, centred on the column edge, with an active highlight. */
function ColResizeHandle({ active, onMouseDown, onDoubleClick }: {
  active: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onDoubleClick: () => void;
}) {
  return (
    <div
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
      title="Drag to resize · double-click to reset"
      style={{ position: 'absolute', top: 0, right: -7, width: 14, height: '100%', cursor: 'col-resize', zIndex: 5, display: 'flex', justifyContent: 'center', touchAction: 'none' }}
    >
      <div style={{ width: active ? 3 : 2, height: '100%', background: active ? 'var(--brand, #9E2B25)' : 'var(--border-strong)', borderRadius: 2, transition: 'background .12s, width .12s' }} />
    </div>
  );
}

/** A resizable header cell for an HTML <table> using table-layout:fixed (+ a <colgroup>).
 *  The label is ellipsised so it never collides with the boundary grip. */
function ResizableTh({ label, active, onMouseDown, onDoubleClick, style, className }: {
  label: React.ReactNode;
  active: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onDoubleClick: () => void;
  style?: React.CSSProperties;
  className?: string;
}) {
  return (
    <th className={className} style={{ position: 'relative', overflow: 'visible', ...style }}>
      <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>{label}</span>
      <ColResizeHandle active={active} onMouseDown={onMouseDown} onDoubleClick={onDoubleClick} />
    </th>
  );
}

/* ----- Arms / groups editor — CSS-grid table with mouse-resizable columns ----- */
const ARM_COLS_KEY = 'heros.armColWidths';
const DEFAULT_ARM_COLS = { label: 220, role: 210, ratio: 100 };
const ARM_COL_MIN = { label: 110, role: 150, ratio: 72 } as const;

function ArmsTable({ armGroups, updArm, rmArm }: {
  armGroups: any[];
  updArm: (i: number, k: string, v: any) => void;
  rmArm: (i: number) => void;
}) {
  const { cols, activeCol, startResize, resetCol } = useColWidths(ARM_COLS_KEY, DEFAULT_ARM_COLS, ARM_COL_MIN);

  const tmpl = `34px ${cols.label}px ${cols.role}px minmax(160px,1fr) ${cols.ratio}px 36px`;
  const minW = 34 + cols.label + cols.role + 160 + cols.ratio + 36;
  const cell: React.CSSProperties = { padding: '6px 8px', minWidth: 0 };

  const handle = (key: keyof typeof cols) => (
    <ColResizeHandle active={activeCol === key} onMouseDown={startResize(key)} onDoubleClick={resetCol(key)} />
  );
  const headCell = (label: React.ReactNode, key: keyof typeof cols | null, extra: React.CSSProperties = {}) => (
    // overflow:visible on the cell so the boundary handle isn't clipped; the label itself
    // is ellipsised by an inner span, and reserves right padding so text never sits under the handle.
    <div style={{ position: 'relative', padding: '8px 10px', overflow: 'visible', ...extra }}>
      <span style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: key ? 8 : 0 }}>{label}</span>
      {key && handle(key)}
    </div>
  );

  return (
    <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
      <div style={{ minWidth: minW }}>
        <div style={{ display: 'grid', gridTemplateColumns: tmpl, background: 'var(--surface-alt)', borderBottom: '1px solid var(--border)', fontSize: 11.5, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
          {headCell('#', null, { textAlign: 'center' })}
          {headCell('Group label', 'label')}
          {headCell('Role', 'role')}
          {headCell('Regimen / description', null)}
          {headCell('Ratio', 'ratio', { textAlign: 'right' })}
          {headCell('', null)}
        </div>
        {armGroups.map((a, i) => (
          <div key={a.id} style={{ display: 'grid', gridTemplateColumns: tmpl, alignItems: 'center', borderBottom: i < armGroups.length - 1 ? '1px solid var(--border)' : 'none' }}>
            <div className="mono" style={{ textAlign: 'center', color: 'var(--text-3)', fontSize: 12 }}>{i + 1}</div>
            <div style={cell}>
              <input value={a.label} onChange={(e) => updArm(i, 'label', e.target.value)} placeholder={isControlRole(a.role) ? 'e.g. Placebo' : 'e.g. Generic febuxostat 80 mg/d'} />
            </div>
            <div style={cell}>
              <select value={a.role} onChange={(e) => updArm(i, 'role', e.target.value)}>{ARM_ROLES.map((r) => <option key={r}>{r}</option>)}</select>
            </div>
            <div style={cell}>
              <input value={a.description} onChange={(e) => updArm(i, 'description', e.target.value)} placeholder="dose · route · frequency" />
            </div>
            <div style={cell}>
              <input className="tabular mono" type="number" min="1" value={a.ratio || 1} onChange={(e) => updArm(i, 'ratio', +e.target.value || 1)} style={{ textAlign: 'right' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button className="btn ghost icon sm" onClick={() => rmArm(i)} disabled={armGroups.length <= 1} title="Remove arm"><Ico.trash /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Build a paper-ready CONSORT 2025 participant-flow diagram as a self-contained, printable
 *  HTML document, flexible for ANY number of arms (driven by armGroups + allocation ratio;
 *  also handles single-arm / observational cohort). Wording follows the CONSORT 2025 flow
 *  template ("Enrolment", "Lost to follow-up for primary outcome", "Analysed for primary
 *  outcome", "(give reasons)"). Used by "Share with PI" (open/print → PDF, download .html/.doc). */
function consortFlowHtml(form: any): string {
  const esc = (s: any) => String(s ?? '').replace(/[&<>"]/g, (c: string) => (({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' } as any)[c]));
  const isCohort = String(form.design || '').toLowerCase().includes('cohort');
  const { N, screened, excluded } = consortCounts(form);
  const groups = isCohort ? [{ label: 'Cohort', ratio: 1 }] : synthArmGroups(form);
  const labels = groups.map((g: any, i: number) => g.label || (isCohort ? 'Cohort' : `Group ${i + 1}`));
  const ratios = groups.map((g: any) => Number(g.ratio) || 1);
  const rsum = ratios.reduce((a: number, b: number) => a + b, 0) || 1;
  const perArmN = ratios.map((r: number) => Math.round((N * r) / rsum));
  let diff = N - perArmN.reduce((a: number, b: number) => a + b, 0);
  for (let i = 0; diff !== 0 && i < perArmN.length; i++) { const d = diff > 0 ? 1 : -1; perArmN[i] += d; diff -= d; }
  const cols = labels.length;
  const primaryName = (form.outcomes || []).find((o: any) => o.kind === 'Primary')?.name;
  const primaryTxt = primaryName ? ` <span class="po">[${esc(primaryName)}]</span>` : '';
  const box = (inner: string) => `<div class="box">${inner}</div>`;
  const armRow = (fn: (i: number) => string) => `<div class="arms" style="grid-template-columns:repeat(${cols},minmax(0,1fr))">`
    + labels.map((lab: string, i: number) => `<div class="acol"><div class="armhdr">${esc(lab)}</div>${box(fn(i))}</div>`).join('') + `</div>`;
  const title = `${esc(form.code || '')}${form.code ? ' — ' : ''}${esc(form.shortTitle || form.title || 'Study')}`;
  const ratioStr = ratios.join(':');
  const enrolLabel = isCohort ? 'Enrolled' : 'Randomised';
  const css = `
    *{box-sizing:border-box}
    body{font-family:'Segoe UI',Arial,Helvetica,sans-serif;color:#1f2328;margin:0;background:#fff}
    .wrap{max-width:1120px;margin:0 auto;padding:30px 36px}
    .head{border-bottom:3px solid #9E2B25;padding-bottom:9px}
    h1{font-size:20px;margin:0;color:#7a1d1a;letter-spacing:.2px}
    .sub{font-size:11.5px;color:#555;margin:4px 0 0;font-style:italic}
    .meta{font-size:11px;color:#555;margin:10px 0 22px;display:flex;flex-wrap:wrap;gap:3px 18px}
    .meta b{color:#1f2328}
    .phase{background:#9E2B25;color:#fff;font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;padding:5px 14px;border-radius:4px;display:inline-block;margin:22px 0 12px}
    .box{border:1.5px solid #3a3a3a;border-radius:6px;padding:9px 14px;font-size:12px;line-height:1.45;background:#fff;box-shadow:0 1px 2px rgba(0,0,0,.06);max-width:560px;margin:0 auto;text-align:center}
    .box b{font-size:12.5px}
    .box .n{font-weight:700;color:#7a1d1a;white-space:nowrap}
    .box ul{text-align:left;margin:6px 0 0;padding-left:18px;font-size:11px;color:#444}
    .box ul li{margin:1.5px 0}
    .po{font-weight:600;color:#7a1d1a;font-size:11px}
    .center{display:flex;flex-direction:column;align-items:center}
    .split{display:grid;grid-template-columns:1fr 340px;gap:0 30px;align-items:center;max-width:940px;width:100%;margin:0 auto}
    .split .line{height:1.5px;background:#3a3a3a;position:relative}
    .split .line:after{content:'';position:absolute;right:-1px;top:-4px;border-top:5px solid transparent;border-bottom:5px solid transparent;border-left:7px solid #3a3a3a}
    .arrow{width:2px;height:24px;background:#3a3a3a;margin:7px auto;position:relative}
    .arrow:after{content:'';position:absolute;left:-4px;bottom:-1px;border-left:5px solid transparent;border-right:5px solid transparent;border-top:7px solid #3a3a3a}
    .arms{display:grid;gap:18px}
    .acol .box{max-width:none}
    .armhdr{font-weight:700;font-size:11px;color:#7a1d1a;text-align:center;margin-bottom:5px;padding-bottom:3px;border-bottom:1px dashed #d8b9b7}
    .foot{font-size:10px;color:#888;margin-top:26px;border-top:1px solid #e6e6e6;padding-top:10px;line-height:1.5}
    @page{size:A4 landscape;margin:11mm}
    @media print{.wrap{padding:0;max-width:none}.box{box-shadow:none}}`;
  const enroll = `
    <div class="phase">Enrolment</div>
    <div class="center">
      ${box(`<b>${esc(flowBoxText(form, 'screened', 'Assessed for eligibility'))}</b> <span class="n">(n = ${screened})</span>`)}
      <div class="arrow"></div>
      <div class="split">
        <div class="line"></div>
        ${box(`<b>${esc(flowBoxText(form, 'excluded', 'Excluded'))}</b> <span class="n">(n = ${excluded})</span><ul><li>Not meeting inclusion criteria (n =&nbsp;&nbsp;)</li><li>Declined to participate (n =&nbsp;&nbsp;)</li><li>Other reasons (give reasons) (n =&nbsp;&nbsp;)</li></ul>`)}
      </div>
      <div class="arrow"></div>
      ${box(`<b>${esc(flowBoxText(form, 'enrolled', enrolLabel))}</b> <span class="n">(n = ${N})</span>`)}
      <div class="arrow"></div>
    </div>`;
  const allocation = `
    <div class="phase">Allocation</div>
    ${armRow((i) => `<b>Allocated to ${esc(labels[i])}</b> <span class="n">(n = ${perArmN[i]})</span><ul><li>Received allocated intervention (n = ${perArmN[i]})</li><li>Did not receive allocated intervention (give reasons) (n = 0)</li></ul>`)}
    <div class="arrow"></div>`;
  const followup = `
    <div class="phase">Follow-Up</div>
    ${armRow(() => `<ul><li>Lost to follow-up for primary outcome${primaryTxt} (give reasons) (n = 0)</li><li>Discontinued intervention (give reasons) (n = 0)</li></ul>`)}
    <div class="arrow"></div>`;
  const analysis = `
    <div class="phase">Analysis</div>
    ${armRow((i) => `<b>${esc(flowBoxText(form, 'analysed', 'Analysed for primary outcome'))}</b>${primaryTxt} <span class="n">(n = ${perArmN[i]})</span><ul><li>Excluded from analysis (give reasons) (n = 0)</li></ul>`)}`;
  const sub = `Flow diagram of the progress through the phases of ${isCohort ? 'an observational cohort' : `a ${esc(form.design || 'randomised trial')}`} of ${cols} group${cols === 1 ? '' : 's'} — enrolment, ${isCohort ? '' : 'intervention allocation, '}follow-up, and data analysis.`;
  return `<!doctype html><html><head><meta charset="utf-8"><title>CONSORT 2025 flow — ${title}</title><style>${css}</style></head>`
    + `<body><div class="wrap"><div class="head"><h1>CONSORT 2025 Flow Diagram</h1><div class="sub">${sub}</div></div>`
    + `<div class="meta"><span><b>Study:</b> ${title}</span><span><b>Design:</b> ${esc(form.design || '—')} (${esc(armDesignLabel(cols))})</span><span><b>Target N:</b> ${N}</span><span><b>Allocation:</b> ${ratioStr}</span>${primaryName ? `<span><b>Primary outcome:</b> ${esc(primaryName)}</span>` : ''}</div>`
    + enroll + allocation + followup + analysis
    + `<div class="foot">Generated by HEROs CTMS from the study design. Counts shown are planned/target figures — replace with actual numbers as the trial progresses. Wording and structure follow the <b>CONSORT 2025</b> flow diagram (Hopewell et al., 2025; consort-statement.org).${isCohort ? ' Adapted for an observational cohort (Enrolled instead of Randomised).' : ''}</div>`
    + `</div></body></html>`;
}

/** Download the generated CONSORT flow as a standalone .html or Word-openable .doc file. */
function downloadConsort(form: any, kind: 'html' | 'doc') {
  const html = consortFlowHtml(form);
  const code = String(form.code || 'study').replace(/[^a-z0-9-]+/gi, '_');
  const mime = kind === 'doc' ? 'application/msword' : 'text/html;charset=utf-8';
  const blob = new Blob([html], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `CONSORT_${code}.${kind}`; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

/* ---- Minimal in-browser .docx (OOXML) writer — no dependencies ----
 * Produces a genuine Word document (zip of [Content_Types].xml + _rels/.rels +
 * word/document.xml) with a TABLE-based CONSORT 2025 flow that opens & edits
 * natively in Word — "more valid" than HTML-as-.doc. */
function crc32(buf: Uint8Array): number {
  let c = ~0;
  for (let i = 0; i < buf.length; i++) { c ^= buf[i]; for (let k = 0; k < 8; k++) c = (c >>> 1) ^ (0xEDB88320 & -(c & 1)); }
  return (~c) >>> 0;
}
function zipStore(files: { name: string; data: Uint8Array }[]): Uint8Array {
  const enc = new TextEncoder();
  const u16 = (n: number) => new Uint8Array([n & 255, (n >> 8) & 255]);
  const u32 = (n: number) => new Uint8Array([n & 255, (n >> 8) & 255, (n >> 16) & 255, (n >>> 24) & 255]);
  const cat = (arrs: Uint8Array[]) => { let len = 0; arrs.forEach((a) => (len += a.length)); const o = new Uint8Array(len); let p = 0; arrs.forEach((a) => { o.set(a, p); p += a.length; }); return o; };
  const locals: Uint8Array[] = []; const central: Uint8Array[] = []; let offset = 0;
  for (const f of files) {
    const name = enc.encode(f.name); const crc = crc32(f.data); const sz = f.data.length;
    const local = cat([u32(0x04034b50), u16(20), u16(0), u16(0), u16(0), u16(0), u32(crc), u32(sz), u32(sz), u16(name.length), u16(0), name, f.data]);
    locals.push(local);
    central.push(cat([u32(0x02014b50), u16(20), u16(20), u16(0), u16(0), u16(0), u16(0), u32(crc), u32(sz), u32(sz), u16(name.length), u16(0), u16(0), u16(0), u16(0), u32(0), u32(offset), name]));
    offset += local.length;
  }
  const cd = cat(central);
  const end = cat([u32(0x06054b50), u16(0), u16(0), u16(files.length), u16(files.length), u32(cd.length), u32(offset), u16(0)]);
  return cat([...locals, cd, end]);
}
/** CONSORT 2025 flow as floating DrawingML text boxes + connector arrows, replicating the
 *  official template's layout (reverse-engineered from CONSORT_2025_flow_diagram.docx):
 *  centre column (Assessed → Randomised), an Excluded box to the right, per-arm Allocation /
 *  Follow-Up / Analysis columns, and blue vertical phase labels. Parametrised for N arms. */
function consortDocXml(form: any): string {
  const x = (s: any) => String(s ?? '').replace(/[&<>"']/g, (c) => (({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' } as any)[c]));
  const isCohort = String(form.design || '').toLowerCase().includes('cohort');
  const { N, screened, excluded } = consortCounts(form);
  const groups = isCohort ? [{ label: 'Cohort', ratio: 1 }] : synthArmGroups(form);
  const labels = groups.map((g: any, i: number) => g.label || (isCohort ? 'Cohort' : `Group ${i + 1}`));
  const ratios = groups.map((g: any) => Number(g.ratio) || 1);
  const rsum = ratios.reduce((a: number, b: number) => a + b, 0) || 1;
  const perArmN = ratios.map((r: number) => Math.round((N * r) / rsum));
  let diff = N - perArmN.reduce((a: number, b: number) => a + b, 0);
  for (let i = 0; diff !== 0 && i < perArmN.length; i++) { const d = diff > 0 ? 1 : -1; perArmN[i] += d; diff -= d; }
  const cols = labels.length;
  const primaryName = (form.outcomes || []).find((o: any) => o.kind === 'Primary')?.name;
  const po = primaryName ? ` ${primaryName}` : '';

  const EMU = (cm: number) => Math.round(cm * 360000);
  let did = 1;
  const tline = (t: string, o: any = {}) => `<w:p><w:pPr><w:spacing w:after="0" w:line="240" w:lineRule="auto"/></w:pPr><w:r><w:rPr><w:rFonts w:ascii="${o.font || 'Arial'}" w:hAnsi="${o.font || 'Arial'}" w:cs="${o.font || 'Arial'}"/>${o.b ? '<w:b/>' : ''}${o.color ? `<w:color w:val="${o.color}"/>` : ''}<w:sz w:val="${o.sz || 20}"/></w:rPr><w:t xml:space="preserve">${x(t)}</w:t></w:r></w:p>`;
  const box = (xc: number, yc: number, wc: number, hc: number, lines: any[], o: any = {}) => {
    const id = did++;
    const paras = lines.map((l) => tline(typeof l === 'string' ? l : l.t, { b: l.b, font: o.font, color: o.color, sz: o.sz })).join('') || '<w:p/>';
    const fill = o.noFill ? '<a:noFill/>' : `<a:solidFill><a:srgbClr val="${o.fill || 'FFFFFF'}"/></a:solidFill>`;
    const ln = o.noBorder ? '<a:ln><a:noFill/></a:ln>' : '<a:ln w="9525"><a:solidFill><a:srgbClr val="000000"/></a:solidFill></a:ln>';
    const vert = o.vert ? ' vert="vert270"' : '';
    return `<w:r><w:drawing><wp:anchor distT="0" distB="0" distL="0" distR="0" simplePos="0" relativeHeight="${251650000 + id}" behindDoc="0" locked="0" layoutInCell="1" allowOverlap="1"><wp:simplePos x="0" y="0"/><wp:positionH relativeFrom="page"><wp:posOffset>${EMU(xc)}</wp:posOffset></wp:positionH><wp:positionV relativeFrom="page"><wp:posOffset>${EMU(yc)}</wp:posOffset></wp:positionV><wp:extent cx="${EMU(wc)}" cy="${EMU(hc)}"/><wp:effectExtent l="0" t="0" r="0" b="0"/><wp:wrapNone/><wp:docPr id="${id}" name="b${id}"/><wp:cNvGraphicFramePr/><a:graphic><a:graphicData uri="http://schemas.microsoft.com/office/word/2010/wordprocessingShape"><wps:wsp><wps:cNvSpPr txBox="1"/><wps:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="${EMU(wc)}" cy="${EMU(hc)}"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom>${fill}${ln}</wps:spPr><wps:txbx><w:txbxContent>${paras}</w:txbxContent></wps:txbx><wps:bodyPr rot="0"${vert} wrap="square" lIns="73152" tIns="36576" rIns="73152" bIns="36576" anchor="t"><a:noAutofit/></wps:bodyPr></wps:wsp></a:graphicData></a:graphic></wp:anchor></w:drawing></w:r>`;
  };
  const conn = (x1: number, y1: number, x2: number, y2: number, arrow = true) => {
    const id = did++; const xc = Math.min(x1, x2); const yc = Math.min(y1, y2); const wc = Math.abs(x2 - x1); const hc = Math.abs(y2 - y1);
    return `<w:r><w:drawing><wp:anchor distT="0" distB="0" distL="0" distR="0" simplePos="0" relativeHeight="${251640000 + id}" behindDoc="0" locked="0" layoutInCell="1" allowOverlap="1"><wp:simplePos x="0" y="0"/><wp:positionH relativeFrom="page"><wp:posOffset>${EMU(xc)}</wp:posOffset></wp:positionH><wp:positionV relativeFrom="page"><wp:posOffset>${EMU(yc)}</wp:posOffset></wp:positionV><wp:extent cx="${EMU(wc) || 1}" cy="${EMU(hc) || 1}"/><wp:effectExtent l="0" t="0" r="0" b="0"/><wp:wrapNone/><wp:docPr id="${id}" name="c${id}"/><wp:cNvGraphicFramePr/><a:graphic><a:graphicData uri="http://schemas.microsoft.com/office/word/2010/wordprocessingShape"><wps:wsp><wps:cNvCnPr/><wps:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="${EMU(wc) || 1}" cy="${EMU(hc) || 1}"/></a:xfrm><a:prstGeom prst="straightConnector1"><a:avLst/></a:prstGeom><a:ln w="12700"><a:solidFill><a:srgbClr val="3A3A3A"/></a:solidFill>${arrow ? '<a:tailEnd type="triangle"/>' : ''}</a:ln></wps:spPr><wps:bodyPr/></wps:wsp></a:graphicData></a:graphic></wp:anchor></w:drawing></w:r>`;
  };

  // --- layout (cm, A4 portrait 21×29.7) ---
  const Wc = 6.4; const cX = (21 - Wc) / 2; const cMid = cX + Wc / 2;
  const exW = 6.4; const exX = cX + Wc + 0.6; const exY = 3.0; const exH = 2.4;
  const Wa = Math.max(3.2, Math.min(7.2, (20 - (cols - 1) * 0.5) / cols));
  const totalA = cols * Wa + (cols - 1) * 0.5; const startA = (21 - totalA) / 2;
  const armX = (i: number) => startA + i * (Wa + 0.5); const armMid = (i: number) => armX(i) + Wa / 2;
  const assessedY = 1.9, assessedH = 1.0, randY = 5.8, randH = 1.0, busY = 7.7, allocY = 8.6, allocH = 2.6, fuY = 12.6, fuH = 2.0, anaY = 15.8, anaH = 2.0;
  const lbl = { font: 'Calibri', color: '2F5496', sz: 22, noBorder: true, noFill: true, vert: true };

  const parts: string[] = [];
  // title (floating, top)
  parts.push(box(0.6, 0.4, 19.8, 1.3, [{ t: 'CONSORT 2025 Flow Diagram', b: true }, { t: `${form.code ? form.code + ' — ' : ''}${form.shortTitle || form.title || 'Study'}  ·  ${form.design || ''}  ·  N = ${N}  ·  allocation ${ratios.join(':')}` }], { noBorder: true, noFill: true, font: 'Calibri', sz: 18 }));
  // phase labels (vertical, left)
  parts.push(box(0.2, assessedY, 1.0, 3.0, ['Enrolment'], lbl));
  parts.push(box(0.2, allocY, 1.0, allocH, ['Allocation'], lbl));
  parts.push(box(0.2, fuY, 1.0, fuH, ['Follow-Up'], lbl));
  parts.push(box(0.2, anaY, 1.0, anaH, ['Analysis'], lbl));
  // centre column
  parts.push(box(cX, assessedY, Wc, assessedH, [`${flowBoxText(form, 'screened', 'Assessed for eligibility')} (n = ${screened})`]));
  parts.push(box(exX, exY, exW, exH, [`${flowBoxText(form, 'excluded', 'Excluded')} (n = ${excluded})`, '   Not meeting inclusion criteria (n =   )', '   Declined to participate (n =   )', '   Other reasons (n =   )']));
  parts.push(box(cX, randY, Wc, randH, [`${flowBoxText(form, 'enrolled', isCohort ? 'Enrolled' : 'Randomised')} (n = ${N})`]));
  // connectors: assessed→randomised, assessed→excluded, randomised→bus, bus→each arm
  parts.push(conn(cMid, assessedY + assessedH, cMid, randY));
  parts.push(conn(cMid, exY + exH / 2, exX, exY + exH / 2));
  parts.push(conn(cMid, randY + randH, cMid, busY, false));
  if (cols > 1) parts.push(conn(armMid(0), busY, armMid(cols - 1), busY, false));
  for (let i = 0; i < cols; i++) {
    parts.push(conn(armMid(i), busY, armMid(i), allocY));
    parts.push(box(armX(i), allocY, Wa, allocH, [{ t: `Allocated to ${labels[i]} (n = ${perArmN[i]})` }, `Received allocated intervention (n = ${perArmN[i]})`, 'Did not receive allocated intervention (give reasons) (n =   )']));
    parts.push(conn(armMid(i), allocY + allocH, armMid(i), fuY));
    parts.push(box(armX(i), fuY, Wa, fuH, [`Lost to follow-up for primary outcome${po} (give reasons) (n =   )`, 'Discontinued intervention (give reasons) (n =   )']));
    parts.push(conn(armMid(i), fuY + fuH, armMid(i), anaY));
    parts.push(box(armX(i), anaY, Wa, anaH, [{ t: `${flowBoxText(form, 'analysed', 'Analysed for primary outcome')}${po} (n = ${perArmN[i]})` }, 'Excluded from analysis (give reasons) (n =   )']));
  }
  const sect = '<w:sectPr><w:pgSz w:w="11906" w:h="16838"/><w:pgMar w:top="567" w:right="567" w:bottom="567" w:left="567"/></w:sectPr>';
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
    + `<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:wps="http://schemas.microsoft.com/office/word/2010/wordprocessingShape">`
    + `<w:body><w:p>${parts.join('')}</w:p>${sect}</w:body></w:document>`;
}
/** Build the structured payload for the server-side LaTeX CONSORT PDF endpoint. */
function consortPayload(form: any) {
  const isCohort = String(form.design || '').toLowerCase().includes('cohort');
  const { N, screened, excluded } = consortCounts(form);
  const groups = isCohort ? [{ label: 'Cohort', ratio: 1 }] : synthArmGroups(form);
  const labels = groups.map((g: any, i: number) => g.label || (isCohort ? 'Cohort' : `Group ${i + 1}`));
  const ratios = groups.map((g: any) => Number(g.ratio) || 1);
  const rsum = ratios.reduce((a: number, b: number) => a + b, 0) || 1;
  const perArmN = ratios.map((r: number) => Math.round((N * r) / rsum));
  let diff = N - perArmN.reduce((a: number, b: number) => a + b, 0);
  for (let i = 0; diff !== 0 && i < perArmN.length; i++) { const d = diff > 0 ? 1 : -1; perArmN[i] += d; diff -= d; }
  const primary = (form.outcomes || []).find((o: any) => o.kind === 'Primary')?.name;
  const subtitle = `${form.code ? form.code + ' - ' : ''}${form.shortTitle || form.title || 'Study'} - ${form.design || ''} - N = ${N} - allocation ${ratios.join(':')}`;
  // Editable box-text overrides (only send ones the user actually changed).
  const labelDefaults: Record<string, string> = { screened: 'Assessed for eligibility', excluded: 'Excluded', enrolled: isCohort ? 'Enrolled' : 'Randomised', analysed: 'Analysed for primary outcome' };
  const boxLabels: Record<string, string> = {};
  for (const k of Object.keys(labelDefaults)) {
    const t = flowBoxText(form, k, labelDefaults[k]);
    if (t !== labelDefaults[k]) boxLabels[k] = t;
  }
  return { title: 'CONSORT 2025 Flow Diagram', subtitle, isCohort, screened, excluded, enrolled: N, enrolLabel: flowBoxText(form, 'enrolled', isCohort ? 'Enrolled' : 'Randomised'), primary, arms: labels.map((label: string, i: number) => ({ label, n: perArmN[i] })), boxLabels };
}
async function downloadConsortPdf(form: any): Promise<void> {
  const blob = await apiClient.consort.pdf(consortPayload(form));
  const code = String(form.code || 'study').replace(/[^a-z0-9-]+/gi, '_');
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `CONSORT_${code}.pdf`; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
async function downloadQuotationPdf(form: any, quotation: any, version: string): Promise<void> {
  const adj = quotation.adjustments || {};
  const adjustments = [
    { label: 'Discount', amount: -(adj.discount || 0) },
    { label: 'Additional charge', amount: adj.additional || 0 },
    { label: 'VAT 7%', amount: adj.vat || 0 },
    { label: 'Rounding', amount: adj.rounding || 0 },
  ].filter((a) => a.amount !== 0);
  const blob = await apiClient.quotations.pdf({
    studyCode: form.code || '',
    studyTitle: form.title || form.shortTitle || '',
    quotationNumber: version,
    version,
    dateText: new Date().toISOString().slice(0, 10),
    currency: 'THB',
    grandTotal: Math.round(quotation.grandTotal || 0),
    subtotal: Math.round(quotation.sections.reduce((s: number, sec: any) => s + (sec.total || 0), 0)),
    sections: quotation.sections.map((s: any) => ({
      title: s.title,
      total: Math.round(s.total || 0),
      items: (s.items || []).filter((it: any) => it.included !== false).map((it: any) => ({
        code: it.code, name: it.name, qty: it.qty, unit: it.unit,
        price: Math.round(it.price || 0), total: Math.round(it.total || 0),
      })),
    })),
    adjustments,
  });
  const code = String(form.code || 'study').replace(/[^a-z0-9-]+/gi, '_');
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `Quotation_${code}.pdf`; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
function downloadConsortDocx(form: any) {
  const enc = new TextEncoder();
  const files = [
    { name: '[Content_Types].xml', data: enc.encode('<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/></Types>') },
    { name: '_rels/.rels', data: enc.encode('<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/></Relationships>') },
    { name: 'word/document.xml', data: enc.encode(consortDocXml(form)) },
  ];
  const zip = zipStore(files);
  const code = String(form.code || 'study').replace(/[^a-z0-9-]+/gi, '_');
  const blob = new Blob([zip], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `CONSORT_${code}.docx`; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
function defaultLabSelections(): Record<string, Set<string>> {
  const all = ['v1', 'v2', 'v3', 'v4', 'v5'];
  return {
    cbc: new Set(['v1', 'v5']),
    cr: new Set(all),
    ast: new Set(all),
    alt: new Set(all),
    ua: new Set(['v0', 'v1', 'v3', 'v4', 'v5']),
    ualy: new Set(['v0', 'v5']),
    ecg: new Set(['v1', 'v5']),
  };
}

/** Live quotation engine — now a thin wrapper over the CANONICAL engine in
 *  `@heros/shared`. The builder's preview, the sections it persists, the server
 *  `/compute` endpoint and the recompute backfill all run the SAME code, so they
 *  can no longer drift. Subgroup analyses fold into the analysed-variable count
 *  (per `form.subgroupCountMode`) instead of billing a flat per-subgroup line,
 *  and the summary report supports a `custom` %/฿ mode (senior request). */
export function computeQuotation(f: any, D: HeroData) {
  return sharedComputeQuotation(f, { pricingProtocol: D.pricingProtocol, dmCatalog: D.dmCatalog });
}

function StepBasics({ form, set }: any) {
  const D = useData();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      <div className="field" style={{ gridColumn: 'span 2' }}>
        <label>Full project title *</label>
        <input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Effect of … on … in adults with …" autoFocus />
      </div>
      <div className="field"><label>Project code *</label><input className="mono" value={form.code} onChange={(e) => set('code', e.target.value)} /></div>
      <div className="field"><label>Short title</label><input value={form.shortTitle} onChange={(e) => set('shortTitle', e.target.value)} placeholder="e.g. GOUT-FEBU RCT" /></div>
      <div className="field"><label>Study design *</label>
        <select value={form.design} onChange={(e) => set('design', e.target.value)}>
          {['Randomized Controlled Trial', 'Adaptive multi-arm RCT', 'Cluster RCT', 'Pragmatic RCT', 'Crossover RCT', 'Factorial trial', 'Prospective cohort', 'Retrospective cohort', 'Case-control'].map((d) => <option key={d}>{d}</option>)}
        </select>
      </div>
      <div className="field"><label>Clinical area</label><input value={form.area} onChange={(e) => set('area', e.target.value)} placeholder="e.g. Cardiology" /></div>
      <div className="field"><label>Sponsor / client</label><input value={form.sponsor} onChange={(e) => set('sponsor', e.target.value)} placeholder="e.g. Thai Rheumatism Association" /></div>
      <div className="field"><label>Project Manager (in system)</label>
        <select value={form.pm} onChange={(e) => set('pm', e.target.value)}>{D.team.map((u) => <option key={u.id} value={u.id}>{u.name} — {u.role}</option>)}</select>
      </div>
      <div className="field"><label>Principal Investigator (documentation)</label><input value={form.piName} onChange={(e) => set('piName', e.target.value)} placeholder="Prof. ____, MD" /></div>
      <div className="field" style={{ gridColumn: 'span 2' }}><label>Co-Investigators (comma-separated, documentation)</label><input value={form.coIsName} onChange={(e) => set('coIsName', e.target.value)} placeholder="Dr. _____, Asst. Prof. _____" /></div>
      <div className="field"><label>First encounter date</label><input type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} /><span style={{ fontSize: 10.5, color: 'var(--text-3)', marginTop: 2 }}>Date of first contact / intake — the project has not started yet. Anchors the provisional timeline.</span></div>
      <div className="field"><label>Expected end date</label><input type="date" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} /><span style={{ fontSize: 10.5, color: 'var(--text-3)', marginTop: 2 }}>Provisional — firms up once the agreement is accepted.</span></div>

      <div style={{ gridColumn: 'span 2', marginTop: 8, paddingTop: 12, borderTop: '1px solid var(--border)', fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Compliance &amp; Registration
      </div>
      <div className="field"><label>Funding source</label><input value={form.fundingSource || ''} onChange={(e) => set('fundingSource', e.target.value)} placeholder="e.g. Faculty of Medicine, CMU · grant 2026/045" /></div>
      <div className="field"><label>Clinical area</label><input value={form.area} onChange={(e) => set('area', e.target.value)} placeholder="e.g. Cardiology" /></div>
      <div className="field"><label>Ethics approval status</label>
        <select value={form.ethics || 'Not yet submitted'} onChange={(e) => set('ethics', e.target.value)}>
          {['Not yet submitted', 'In preparation', 'In submission', 'Approved', 'Approved with conditions', 'Rejected'].map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <div className="field"><label>Ethics committee reference</label><input className="mono" value={form.ethicsRef || ''} onChange={(e) => set('ethicsRef', e.target.value)} placeholder="e.g. FAC-MED 2026/045" /></div>
      <div className="field"><label>Trial registry #</label><input className="mono" value={form.registry || ''} onChange={(e) => set('registry', e.target.value)} placeholder="e.g. TCTR202604_____" /></div>
      <div className="field"><label>REDCap project link</label><input className="mono" value={form.redcap || ''} onChange={(e) => set('redcap', e.target.value)} placeholder="https://redcap.cmu.ac.th/…" /></div>

      <div style={{ gridColumn: 'span 2', marginTop: 8, paddingTop: 12, borderTop: '1px solid var(--border)', fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Project members (in system)
      </div>
      <div className="field" style={{ gridColumn: 'span 2' }}>
        <label>Members</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {D.team.map((u) => {
            const on = (form.members || [form.pm]).includes(u.id);
            return (
              <label key={u.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, padding: '4px 10px', borderRadius: 14, background: on ? 'var(--accent-soft)' : 'var(--surface-alt)', border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)'), color: on ? 'var(--accent)' : 'var(--text)', fontWeight: on ? 600 : 500, cursor: 'pointer' }}>
                <input type="checkbox" checked={on} onChange={(e) => {
                  const cur = new Set(form.members || [form.pm]);
                  if (e.target.checked) cur.add(u.id); else cur.delete(u.id);
                  cur.add(form.pm);
                  set('members', [...cur]);
                }} style={{ accentColor: 'var(--accent)' }} />
                {u.name} <span style={{ color: 'var(--text-3)', fontSize: 10 }}>{u.role}</span>
              </label>
            );
          })}
        </div>
        <div style={{ fontSize: 10.5, color: 'var(--text-3)', marginTop: 4 }}>Project Manager is always a member. Anyone given work in the Timeline &amp; Task-allocation step is added automatically — membership stays in sync with the timeline. Others can tick "received" on their compensation lines for this project.</div>
      </div>
    </div>
  );
}

function StepDesign({ form, set }: any) {
  const D = useData();
  const armGroups = synthArmGroups(form);
  // Keep the legacy arms-count + intervention/comparator fields in sync (used by the
  // Study Flow diagram, the quotation snapshot, and serialisation).
  const commitArms = (next: any[]) => {
    set('armGroups', next);
    set('arms', next.length);
    const exp = next.find((a) => !isControlRole(a.role));
    const ctrl = [...next].reverse().find((a) => isControlRole(a.role));
    set('intervention', exp?.label || '');
    set('comparator', ctrl?.label || '');
  };
  const updArm = (i: number, k: string, v: any) => commitArms(armGroups.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)));
  const addArm = () => commitArms([...armGroups, { id: 'arm' + Date.now(), label: '', role: 'Experimental / Intervention', description: '', ratio: 1 }]);
  const rmArm = (i: number) => { if (armGroups.length > 1) commitArms(armGroups.filter((_, idx) => idx !== i)); };
  const setArmCount = (n: number) => {
    const target = Math.max(1, Math.min(8, n || 1));
    if (target === armGroups.length) return;
    if (target > armGroups.length) {
      const add = Array.from({ length: target - armGroups.length }, (_, k) => ({ id: 'arm' + (Date.now() + k), label: '', role: 'Experimental / Intervention', description: '', ratio: 1 }));
      commitArms([...armGroups, ...add]);
    } else commitArms(armGroups.slice(0, target));
  };
  const ratioStr = armGroups.map((a) => a.ratio || 1).join(':');
  const expArms = armGroups.filter((a) => !isControlRole(a.role));
  const ctrlArms = armGroups.filter((a) => isControlRole(a.role));
  const picoI = expArms.map((a) => a.label).filter(Boolean).join(' + ') || '—';
  const picoC = ctrlArms.map((a) => a.label).filter(Boolean).join(' + ') || '—';
  const multiExp = expArms.length > 1;
  const noControl = ctrlArms.length === 0;
  const manyControl = ctrlArms.length > 1;
  const nP = form.outcomes.filter((o: any) => o.kind === 'Primary').length;
  const nS = form.outcomes.filter((o: any) => o.kind === 'Secondary').length;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="field"><label>Protocol complexity *</label>
          <select value={form.protocolLevel} onChange={(e) => set('protocolLevel', e.target.value)}>
            {D.pricingProtocol.map((p) => <option key={p.id}>{p.level}</option>)}
          </select>
          <div style={{ fontSize: 10.5, color: 'var(--text-2)', marginTop: 2 }}>Drives protocol cost; sets the PICO depth expected for this study.</div>
        </div>
        <div className="field"><label>Design</label>
          <input value={form.design} readOnly style={{ background: 'var(--surface-alt)' }} title="Set in Basics" />
          <div style={{ fontSize: 10.5, color: 'var(--text-2)', marginTop: 2 }}>{armDesignLabel(armGroups.length)} · allocation {ratioStr}</div>
        </div>
      </div>

      <div className="field"><label>Population — PICO “P”</label>
        <textarea value={form.population || ''} onChange={(e) => set('population', e.target.value)} rows={2} placeholder="e.g. Adults ≥18 with gout and serum urate ≥6 mg/dL on stable urate-lowering therapy" />
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Arms / groups — PICO “I” vs “C”</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 11, color: 'var(--text-2)' }}>Arms
              <input type="number" min="1" max="8" value={armGroups.length} onChange={(e) => setArmCount(+e.target.value)} style={{ width: 56, marginLeft: 6 }} />
            </label>
            <button className="btn primary sm" onClick={addArm}><Ico.plus /> Add arm</button>
          </div>
        </div>
        <ArmsTable armGroups={armGroups} updArm={updArm} rmArm={rmArm} />
        <div style={{ marginTop: 4, fontSize: 10, color: 'var(--text-3)' }}>Tip: drag the dividers in the header to resize the Group label, Role, and Ratio columns — widths are remembered.</div>
        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-2)' }}>
          PICO summary — <strong>I:</strong> {picoI} &nbsp;<strong>vs</strong>&nbsp; <strong>C:</strong> {picoC} &nbsp;·&nbsp; allocation {ratioStr} &nbsp;·&nbsp; {armDesignLabel(armGroups.length)} &nbsp;·&nbsp; <strong>O:</strong> {nP} primary, {nS} secondary (defined below).
        </div>
        {(multiExp || noControl || manyControl) && (
          <div style={{ marginTop: 6, padding: 8, borderRadius: 6, background: 'rgba(194,138,30,0.10)', border: '1px solid #caa24a', fontSize: 10.5, color: '#8a5d12', display: 'flex', flexDirection: 'column', gap: 3 }}>
            {noControl && <div>⚠ No comparator/control arm — set at least one arm's role to Active comparator, Placebo, or Standard-of-care.</div>}
            {manyControl && <div>⚠ More than one control arm — multi-arm trials usually share a single control; confirm this is intended.</div>}
            {multiExp && <div>⚠ {expArms.length} experimental arms ⇒ multiple primary comparisons. Pre-specify the primary contrast and family-wise error control (e.g. Dunnett / Bonferroni); the total sample size must power each comparison, and weighting the shared control can improve efficiency.</div>}
          </div>
        )}
      </div>

      <div>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Outcomes — PICO “O”</div>
        {/* Outcomes are editable here AND in Outcomes &amp; Analysis (same list). The
            subgroup/sensitivity PLAN lives only in Outcomes &amp; Analysis (showPlan). */}
        <OutcomesEditor form={form} set={set} showPlan={false} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="field"><label>Randomisation</label>
          <select value={form.randomise ? 'yes' : 'no'} onChange={(e) => set('randomise', e.target.value === 'yes')}>
            <option value="yes">Yes — {ratioStr} allocation</option>
            <option value="no">No</option>
          </select>
          {form.randomise && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 11.5 }}>
              <input type="checkbox" checked={!!form.stratified} onChange={(e) => set('stratified', e.target.checked)} />
              Stratified randomisation
            </label>
          )}
          {form.randomise && form.stratified && (
            <div style={{ marginTop: 6 }}>
              <div style={{ fontSize: 10.5, color: 'var(--text-2)', marginBottom: 4 }}>Stratification groups ({(form.strata || []).length}) — e.g. study site, sex, disease severity</div>
              {(form.strata || []).map((s: string, i: number) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                  <input value={s} placeholder={`Group ${i + 1}`} onChange={(e) => { const next = [...(form.strata || [])]; next[i] = e.target.value; set('strata', next); }} />
                  <button type="button" className="btn ghost icon sm" title="Remove this group" onClick={() => set('strata', (form.strata || []).filter((_: string, idx: number) => idx !== i))}><Ico.close /></button>
                </div>
              ))}
              <button type="button" className="btn ghost sm" onClick={() => set('strata', [...(form.strata || []), ''])}><Ico.plus /> Add stratification group</button>
            </div>
          )}
        </div>
        <div className="field"><label>Blinding</label>
          <select value={form.blinding} onChange={(e) => set('blinding', e.target.value)}>
            {['Open-label', 'Single-blind', 'Double-blind', 'Triple-blind', 'Not applicable'].map((b) => <option key={b}>{b}</option>)}
          </select>
        </div>
        <div className="field"><label>Sample size (total)</label>
          <input type="number" min="1" value={form.sampleSize} onChange={(e) => set('sampleSize', +e.target.value)} />
          <div style={{ fontSize: 10.5, color: 'var(--text-2)', marginTop: 2 }}>≈ {Math.floor((form.sampleSize || 0) / Math.max(1, armGroups.length))} per arm (equal split){multiExp ? ' · must power each comparison' : ''}</div>
        </div>
        <div className="field"><label>Recruitment duration (Weeks)</label>
          <input type="number" min="1" value={monthsToWeeks(form.recruitMonths)} onChange={(e) => set('recruitMonths', weeksToMonths(+e.target.value))} />
        </div>
        <div className="field"><label>Treatment duration (Weeks)</label>
          <input type="number" min="0" value={monthsToWeeks(form.treatmentMonths)} onChange={(e) => set('treatmentMonths', weeksToMonths(+e.target.value))} />
        </div>
        <div className="field"><label>Follow-up duration (Weeks)</label>
          <input type="number" min="0" value={monthsToWeeks(form.followupMonths)} onChange={(e) => set('followupMonths', weeksToMonths(+e.target.value))} />
        </div>
      </div>
    </div>
  );
}

const VISIT_COLS_KEY = 'heros.visitColWidths';
const DEFAULT_VISIT_COLS = { visit: 200, types: 220, timepoint: 160, required: 130 };
const VISIT_COL_MIN = { visit: 110, types: 140, timepoint: 120, required: 90 } as const;

function StepVisits({ form, set }: any) {
  const { cols, activeCol, startResize, resetCol } = useColWidths(VISIT_COLS_KEY, DEFAULT_VISIT_COLS, VISIT_COL_MIN);
  const addVisit = () => set('visits', [...form.visits, { id: 'v' + Date.now(), name: 'New visit', types: ['Treatment follow-up'], type: 'Treatment follow-up', week: 0, timeValue: 0, timeUnit: 'week', required: true }]);
  const updVisit = (id: string, k: string, v: any) => set('visits', form.visits.map((x: any) => (x.id === id ? { ...x, [k]: v } : x)));
  const patchVisit = (id: string, patch: any) => set('visits', form.visits.map((x: any) => (x.id === id ? { ...x, ...patch } : x)));
  const delVisit = (id: string) => set('visits', form.visits.filter((x: any) => x.id !== id));
  const resetVisits = () => {
    if (!window.confirm('Reset the visit schedule to the default template? Your visit edits will be lost (lab ticks for the default visits are kept).')) return;
    set('visits', defaultVisits());
  };
  const sortedVisits = [...form.visits].sort((a: any, b: any) => (a.week ?? 0) - (b.week ?? 0));

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div className="sub">Visits drive the lab matrix and CRF count. Rows are ordered by time. A visit can have <strong>several types</strong> (Screening + Baseline + Randomisation are often one day-1 visit). Timepoint is entered in <strong>weeks</strong> and accepts negatives (Screening = −1).</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn sm" onClick={resetVisits} title="Restore the default visit schedule">↻ Reset to default</button>
          <button className="btn primary sm" onClick={addVisit}><Ico.plus /> Add visit</button>
        </div>
      </div>
      <div className="scroll-x">
      <table style={{ minWidth: cols.visit + cols.types + cols.timepoint + cols.required + 44, tableLayout: 'fixed', width: '100%' }}>
        <colgroup>
          <col style={{ width: cols.visit }} />
          <col style={{ width: cols.types }} />
          <col style={{ width: cols.timepoint }} />
          <col style={{ width: cols.required }} />
          <col style={{ width: 44 }} />
        </colgroup>
        <thead><tr>
          <ResizableTh label="Visit" active={activeCol === 'visit'} onMouseDown={startResize('visit')} onDoubleClick={resetCol('visit')} />
          <ResizableTh label="Type(s)" active={activeCol === 'types'} onMouseDown={startResize('types')} onDoubleClick={resetCol('types')} />
          <ResizableTh label="Timepoint" active={activeCol === 'timepoint'} onMouseDown={startResize('timepoint')} onDoubleClick={resetCol('timepoint')} />
          <ResizableTh label="Required" active={activeCol === 'required'} onMouseDown={startResize('required')} onDoubleClick={resetCol('required')} />
          <th></th>
        </tr></thead>
        <tbody>
          {sortedVisits.map((v: any) => (
            <tr key={v.id}>
              <td><input value={v.name} onChange={(e) => updVisit(v.id, 'name', e.target.value)} /></td>
              <td>
                <VisitTypeSelect value={visitTypeList(v)} onChange={(types) => patchVisit(v.id, { types, type: types[0] || '' })} />
              </td>
              <td>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <WeekInput value={v.week ?? 0} onChange={(n) => patchVisit(v.id, { week: n, timeValue: n, timeUnit: 'week' })} />
                  <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Weeks</span>
                </div>
              </td>
              <td>
                <select value={v.required ? 'yes' : 'no'} onChange={(e) => updVisit(v.id, 'required', e.target.value === 'yes')}>
                  <option value="yes">Required</option><option value="no">Optional</option>
                </select>
              </td>
              <td><button className="btn ghost icon sm" onClick={() => delVisit(v.id)}><Ico.trash /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </>
  );
}

/** Canonical lab group/category list (brief Feature 2) — used for the group-first picker
 *  and as suggestions when creating a custom group. */
const LAB_GROUPS = [
  'Hematology', 'Liver function', 'Renal function', 'Electrolytes', 'Coagulation',
  'Inflammatory markers', 'Cardiac markers', 'Endocrine / Hormonal tests', 'Tumor markers',
  'Urinalysis', 'Microbiology', 'Serology / Immunology', 'Other / Custom',
];

function StepLabs({ form, set }: any) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggle = (labId: string, visitId: string) => {
    const set0 = new Set(form.labSelections[labId] || []);
    if (set0.has(visitId)) set0.delete(visitId); else set0.add(visitId);
    set('labSelections', { ...form.labSelections, [labId]: set0 });
  };
  // --- Bulk select-all / clear-all (senior request): whole table / column / row ---
  // All operate on a fresh clone with NEW Set instances (React identity) and skip
  // compensation rows (they show '—' and have no checkbox).
  const nonCompLabRows = () => form.labs.filter((l: any) => !l.isCompensation);
  const allVisitIds = (): string[] => visitsByTime.map((v: any) => v.id);
  const setRowAll = (labId: string, on: boolean) =>
    set('labSelections', { ...form.labSelections, [labId]: new Set(on ? allVisitIds() : []) });
  const setColumnAll = (visitId: string, on: boolean) => {
    const next: Record<string, Set<string>> = { ...form.labSelections };
    for (const l of nonCompLabRows()) {
      const s0 = new Set(next[l.id] || []);
      if (on) s0.add(visitId); else s0.delete(visitId);
      next[l.id] = s0;
    }
    set('labSelections', next);
  };
  const setTableAll = (on: boolean) => {
    const next: Record<string, Set<string>> = { ...form.labSelections };
    for (const l of nonCompLabRows()) next[l.id] = new Set(on ? allVisitIds() : []);
    set('labSelections', next);
  };
  const setParamAnalyzed = (labId: string, idx: number, analyzed: boolean) =>
    set('labs', form.labs.map((l: any) => (l.id !== labId ? l : { ...l, parameters: labParams(l).map((p: any, i: number) => (i === idx ? { ...p, analyzed } : p)) })));
  const removeLab = (labId: string) => {
    set('labs', form.labs.filter((l: any) => l.id !== labId));
    const next = { ...form.labSelections }; delete next[labId]; set('labSelections', next);
  };
  const visitsByTime = [...form.visits].sort((a: any, b: any) => (a.week ?? 0) - (b.week ?? 0));
  const colSpan = 3 + visitsByTime.length; // name + analysed + visits + actions (price column removed)

  // --- Catalog: search the shared library + inline-create (Topic A) ---
  const D = useData();
  const actions = useDataActions();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<any>({ name: '', kind: 'lab', price: 0, groupName: '', subParams: '', propose: true });
  const existing = new Set(form.labs.map((l: any) => (l.name || '').toLowerCase()));

  // --- Lab-set registry + custom catalog: group-first selection + alias/typo search (Lab list spec) ---
  const [selGroup, setSelGroup] = useState('');
  const [groupQ, setGroupQ] = useState('');
  const activeLabItems = D.catalog.filter((c) => (c.kind === 'lab' || c.kind === 'procedure') && c.status === 'active');
  const catalogGroups = [...new Set(activeLabItems.map((c) => c.groupName || 'Other / Custom'))];
  // Built-in lab-set categories (registry) unioned with any custom catalog groups.
  const groupOptions = [...new Set([...labSetCategories(), ...catalogGroups, 'Other / Custom'])];
  const norm = (s: string) => (s || '').toLowerCase();
  const labAdded = (name: string, id?: string) =>
    form.labs.some((l: any) => (id && l.id === id) || norm(l.name) === norm(name));
  // All built-in names (sets + single labs) — used to hide DB catalog rows that
  // duplicate a registry entry, so e.g. "CBC" never appears twice.
  const regNames = new Set<string>([...LAB_SETS.map((s) => norm(s.name)), ...allSingleLabs().map((it) => norm(it.name))]);
  // Registry sets in the selected category (filtered by the in-group search box).
  const groupSets = selGroup
    ? LAB_SETS.filter((s) => s.category === selGroup
        && (!groupQ.trim() || norm(s.name).includes(norm(groupQ)) || s.aliases.some((a) => norm(a).includes(norm(groupQ)))
            || expandLabSet(s.id).some((it) => norm(it.name).includes(norm(groupQ)))))
    : [];
  // Custom / approved catalog items in the selected category (not a built-in set).
  const groupCatalog = selGroup
    ? activeLabItems.filter((c) => (c.groupName || 'Other / Custom') === selGroup
        && !regNames.has(norm(c.name))
        && (!groupQ.trim() || norm(c.name).includes(norm(groupQ)) || (c.parameters || []).some((p: any) => norm(p.name).includes(norm(groupQ)))))
    : [];
  // Search across the registry (panels + single labs, alias/typo-aware) + custom catalog.
  const regResults = q.trim() ? searchLabRegistry(q.trim(), 12) : [];
  const catMatches = q.trim()
    ? activeLabItems.filter((c) => norm(c.name).includes(norm(q)) && !existing.has(norm(c.name))
        && !regNames.has(norm(c.name))).slice(0, 6)
    : [];
  const addCatalogLab = (c: any) => {
    const id = 'lab_' + (c.code || c.id);
    if (form.labs.some((l: any) => l.id === id)) return;
    const parameters = c.parameters && c.parameters.length ? c.parameters.map((p: any) => ({ name: p.name, analyzed: p.analyzed !== false })) : [{ name: c.name, analyzed: true }];
    set('labs', [...form.labs, { id, name: c.name, price: c.price ?? 0, parameters, catalogCode: c.code }]);
    set('labSelections', { ...form.labSelections, [id]: new Set() });
    setQ('');
  };
  // Add a built-in Lab Set as ONE lab row with all (recursively expanded) children as sub-parameters.
  const addLabSet = (setId: string) => {
    const s = LAB_SETS.find((x) => x.id === setId);
    if (!s) return;
    const id = 'lab_' + s.id;
    if (labAdded(s.name, id)) return;
    const parameters = expandLabSet(s.id).map((it) => ({ name: it.name, analyzed: true }));
    set('labs', [...form.labs, { id, name: s.name, price: s.price ?? 0, parameters, labSetId: s.id }]);
    set('labSelections', { ...form.labSelections, [id]: new Set() });
    setQ('');
  };
  // Add a single child lab (one sub-parameter = itself).
  const addSingleLab = (code: string, name: string) => {
    const id = 'lab_single_' + code.toLowerCase().replace(/[^a-z0-9]+/g, '_');
    if (labAdded(name, id)) return;
    set('labs', [...form.labs, { id, name, price: 0, parameters: [{ name, analyzed: true }], singleLab: true }]);
    set('labSelections', { ...form.labSelections, [id]: new Set() });
    setQ('');
  };
  const createLab = async () => {
    const name = draft.name.trim();
    if (!name) return;
    const id = 'lab_custom_' + Date.now();
    // Split sub-parameters on comma / semicolon / newline so a panel like
    // "Thyroid function test → FT3, FT4, TSH" is added in ONE step (Feature 3).
    const subNames = String(draft.subParams || '').split(/[,;\n]+/).map((s: string) => s.trim()).filter(Boolean);
    const parameters = subNames.length ? subNames.map((n: string) => ({ name: n, analyzed: true })) : [{ name, analyzed: true }];
    set('labs', [...form.labs, { id, name, price: 0, parameters }]);
    set('labSelections', { ...form.labSelections, [id]: new Set() });
    const pCount = `${parameters.length} sub-parameter${parameters.length === 1 ? '' : 's'}`;
    // Capture EVERY inline lab/procedure into the catalog databank so it links to
    // the Pricing Library and is reusable across projects (senior request). Labs
    // carry no price now. The checkbox routes it to the SHARED library (admin
    // review) vs a project-scoped catalog row that's immediately searchable.
    // Skip the catalog capture when an active/pending item of the same kind+name
    // already exists, so re-adding never spams the promotion queue / library.
    const dupe = D.catalog.some((c: any) => c.kind === draft.kind && norm(c.name) === norm(name) && (c.status === 'active' || c.status === 'pending'));
    if (dupe) {
      toast(`Added · ${pCount} · already in the catalog`);
    } else {
      try {
        await actions.proposeCatalogItem({ kind: draft.kind, name, groupName: draft.groupName || undefined, parameters, proposeToLibrary: !!draft.propose, projectId: form.code });
        toast(draft.propose ? `Added · ${pCount} · proposed to shared library (pending review)` : `Added · ${pCount} · saved to the catalog databank`);
      } catch { toast(`Added to this project · ${pCount} (catalog save failed)`); }
    }
    setDraft({ name: '', kind: 'lab', price: 0, groupName: '', subParams: '', propose: true }); setCreating(false);
  };

  // Mouse drag-to-reorder for the labs table (Lab list spec: parent/child rows reorderable).
  const { rowProps: labRowProps, gripProps: labGripProps } = useRowDrag(form.labs, (next: any[]) => set('labs', next));

  return (
    <>
      <div className="sub" style={{ marginBottom: 10 }}>Pick a <strong>group</strong> then add a whole <strong>Lab Set</strong> (e.g. CBC, Renal function test, Electrolytes, Thyroid function test) — it expands into all its child tests in one click — or <strong>search</strong> a panel name, abbreviation or single test (typo-tolerant: “elyte”, “tft”, “nutrophil”). Tick the visits at which each lab is collected; expand a panel (▸) to mark which sub-parameters are <strong>analysed</strong> (drive descriptive cost) vs merely <strong>collected</strong>. Drag the <strong>⠿</strong> grip to reorder rows.</div>

      {/* Group-first picker (brief Feature 2): choose a group/category, then add its parameters. */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 8, flexWrap: 'wrap' }}>
        <div className="field" style={{ width: 220 }}>
          <label>Lab group / category</label>
          <select value={selGroup} onChange={(e) => { setSelGroup(e.target.value); setGroupQ(''); }}>
            <option value="">— pick a group to browse —</option>
            {groupOptions.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        {selGroup && (
          <div className="field" style={{ flex: 1, minWidth: 200 }}>
            <label>Search within “{selGroup}”</label>
            <input value={groupQ} onChange={(e) => setGroupQ(e.target.value)} placeholder="Filter parameters in this group…" />
          </div>
        )}
      </div>
      {selGroup && (() => {
        const total = groupSets.length + groupCatalog.length;
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10, padding: total ? 8 : 0, border: total ? '1px solid var(--border)' : 'none', borderRadius: 6 }}>
            {total === 0
              ? <span style={{ fontSize: 11, color: 'var(--text-3)' }}>No lab sets in this group{groupQ ? ' match your search' : ''} — use the search/Create below, or pick another group.</span>
              : <>
                  {groupSets.map((s) => {
                    const added = labAdded(s.name, 'lab_' + s.id);
                    const n = expandLabSet(s.id).length;
                    return (
                      <button key={s.id} type="button" disabled={added} onClick={() => addLabSet(s.id)}
                        title={added ? 'Already added' : `Add ${s.name} — ${n} child items`}
                        style={{ fontSize: 11.5, padding: '4px 10px', borderRadius: 14, cursor: added ? 'default' : 'pointer', background: added ? 'var(--surface-alt)' : 'var(--surface)', border: '1px solid ' + (added ? 'var(--border)' : 'var(--accent)'), color: added ? 'var(--text-3)' : 'var(--accent)' }}>
                        {added ? '✓ ' : '+ '}{s.name} <span style={{ opacity: 0.7 }}>· {n}p</span>
                      </button>
                    );
                  })}
                  {groupCatalog.map((c) => {
                    const added = labAdded(c.name, 'lab_' + (c.code || c.id));
                    return (
                      <button key={c.id} type="button" disabled={added} onClick={() => addCatalogLab(c)}
                        title={added ? 'Already added' : `Add ${c.name}${c.parameters?.length ? ` (${c.parameters.length} params)` : ''} · custom`}
                        style={{ fontSize: 11.5, padding: '4px 10px', borderRadius: 14, cursor: added ? 'default' : 'pointer', background: added ? 'var(--surface-alt)' : 'var(--surface)', border: '1px dashed ' + (added ? 'var(--border)' : 'var(--accent)'), color: added ? 'var(--text-3)' : 'var(--accent)' }}>
                        {added ? '✓ ' : '+ '}{c.name}{c.parameters?.length ? ` · ${c.parameters.length}p` : ''}
                      </button>
                    );
                  })}
                </>}
          </div>
        );
      })()}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10, flexWrap: 'wrap', position: 'relative' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 240 }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search labs — a panel (CBC, RFT, Elyte, LFT, TFT…) or a single test (Hb, TSH, Cr)…" style={{ width: '100%' }} />
          {(regResults.length > 0 || catMatches.length > 0) && (
            <div style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, right: 0, marginTop: 2, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 280, overflowY: 'auto' }}>
              {regResults.map((r, i) => r.type === 'set' ? (
                <button key={'s_' + r.id} type="button" disabled={labAdded(r.name, 'lab_' + r.id)} onClick={() => addLabSet(r.id)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, width: '100%', border: 'none', background: 'none', padding: '6px 10px', cursor: labAdded(r.name, 'lab_' + r.id) ? 'default' : 'pointer', fontSize: 12, textAlign: 'left', opacity: labAdded(r.name, 'lab_' + r.id) ? 0.5 : 1 }}>
                  <span><strong>{r.name}</strong> <span style={{ fontSize: 9, fontWeight: 700, color: '#fff', background: 'var(--navy)', borderRadius: 8, padding: '1px 6px' }}>LAB SET</span></span>
                  <span style={{ color: 'var(--text-3)', fontSize: 10 }}>{r.category} · {r.items.length} items</span>
                </button>
              ) : (
                <button key={'g_' + r.code + '_' + i} type="button" disabled={labAdded(r.name)} onClick={() => addSingleLab(r.code, r.name)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, width: '100%', border: 'none', background: 'none', padding: '6px 10px', cursor: labAdded(r.name) ? 'default' : 'pointer', fontSize: 12, textAlign: 'left', opacity: labAdded(r.name) ? 0.5 : 1 }}>
                  <span>{r.name} <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-2)', background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: 8, padding: '1px 6px' }}>Single lab</span></span>
                </button>
              ))}
              {catMatches.map((c) => (
                <button key={'c_' + c.id} type="button" onClick={() => addCatalogLab(c)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, width: '100%', border: 'none', background: 'none', padding: '6px 10px', cursor: 'pointer', fontSize: 12, textAlign: 'left' }}>
                  <span>{c.name} <span style={{ fontSize: 9, fontWeight: 700, color: '#8a5d12', background: 'rgba(194,138,30,0.14)', border: '1px solid #caa24a', borderRadius: 8, padding: '1px 6px' }}>Custom</span> <span style={{ color: 'var(--text-3)', fontSize: 10 }}>{c.groupName}</span></span>
                </button>
              ))}
            </div>
          )}
          {q.trim() && regResults.length === 0 && catMatches.length === 0 && (
            <div style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, right: 0, marginTop: 2, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px', fontSize: 11.5, color: 'var(--text-2)' }}>
              No lab-set or single-lab match. <button type="button" className="btn ghost xs" onClick={() => { setDraft((d: any) => ({ ...d, name: q.trim() })); setCreating(true); setQ(''); }}>Create “{q.trim()}”…</button>
            </div>
          )}
        </div>
        <button className="btn sm" onClick={() => setCreating((v) => !v)}><Ico.plus /> New lab / procedure</button>
      </div>
      {creating && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 10, padding: 10, background: 'var(--surface-alt)', borderRadius: 8, border: '1px solid var(--border)' }}>
          <div className="field" style={{ minWidth: 160 }}><label>Name</label><input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Procalcitonin" /></div>
          <div className="field" style={{ width: 130 }}><label>Type</label><select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })}><option value="lab">Lab</option><option value="procedure">Procedure</option></select></div>
          <div className="field" style={{ width: 170 }}><label>Group</label><input list="lab-groups" value={draft.groupName} onChange={(e) => setDraft({ ...draft, groupName: e.target.value })} placeholder="pick or type a custom group" /><datalist id="lab-groups">{LAB_GROUPS.map((g) => <option key={g} value={g} />)}</datalist></div>
          <div className="field" style={{ flexBasis: '100%', minWidth: 240 }}>
            <label>Sub-parameters <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>— comma-separated, e.g. <strong>FT3, FT4, TSH</strong> (leave blank for a single-parameter test)</span></label>
            <input value={draft.subParams} onChange={(e) => setDraft({ ...draft, subParams: e.target.value })} placeholder="FT3, FT4, TSH" style={{ width: '100%' }} />
          </div>
          <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 5, paddingBottom: 8 }}><input type="checkbox" checked={draft.propose} onChange={(e) => setDraft({ ...draft, propose: e.target.checked })} /> Propose to shared library</label>
          <button className="btn primary sm" style={{ marginBottom: 2 }} onClick={createLab} disabled={!draft.name.trim()}>Add</button>
          <button className="btn ghost sm" style={{ marginBottom: 2 }} onClick={() => setCreating(false)}>Cancel</button>
        </div>
      )}
      {form.labs.some((l: any) => !l.isCompensation) && visitsByTime.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 11, color: 'var(--text-2)' }}>
          <span>Tick visits in bulk —</span>
          <button className="btn ghost xs" onClick={() => setTableAll(true)} title="Tick every lab at every visit">✓ Select all</button>
          <button className="btn ghost xs" onClick={() => setTableAll(false)} title="Clear every tick in the whole table">✗ Clear all</button>
          <span style={{ color: 'var(--text-3)' }}>(or use ✓/✗ per column header and per row)</span>
        </div>
      )}
      <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
        <table style={{ minWidth: 800 }}>
          <thead>
            <tr>
              <th style={{ width: 220, position: 'sticky', left: 0, background: 'var(--surface)', zIndex: 2, boxShadow: '1px 0 0 var(--border)' }}>Lab / procedure</th>
              <th className="num" title="analysed / total sub-parameters">Analysed</th>
              {visitsByTime.map((v: any) => (
                <th key={v.id} style={{ textAlign: 'center', minWidth: 60, fontSize: 10 }}>
                  {v.name}<br /><span style={{ color: 'var(--text-3)', fontWeight: 400 }}>{visitTimeLabel(v)}</span>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 2, marginTop: 2 }}>
                    <button className="btn ghost xs" style={{ padding: '0 4px', minWidth: 0, fontSize: 9 }} onClick={() => setColumnAll(v.id, true)} title={`Tick every lab at ${v.name}`}>✓</button>
                    <button className="btn ghost xs" style={{ padding: '0 4px', minWidth: 0, fontSize: 9 }} onClick={() => setColumnAll(v.id, false)} title={`Clear every lab at ${v.name}`}>✗</button>
                  </div>
                </th>
              ))}
              <th style={{ width: 56 }}></th>
            </tr>
          </thead>
          <tbody>
            {form.labs.map((lab: any, labIdx: number) => {
              const params = labParams(lab);
              const hasParams = !lab.isCompensation && params.length > 0;
              const isOpen = !!expanded[lab.id];
              return (
                <React.Fragment key={lab.id}>
                  <tr {...labRowProps(labIdx)}>
                    <td style={{ fontWeight: 500, position: 'sticky', left: 0, background: 'var(--surface)', zIndex: 1, boxShadow: '1px 0 0 var(--border)', whiteSpace: 'nowrap' }}>
                      <span {...labGripProps(labIdx)}>⠿</span>
                      {hasParams && (
                        <button className="btn ghost xs" onClick={() => setExpanded((e) => ({ ...e, [lab.id]: !isOpen }))} style={{ marginRight: 6, width: 18 }} title={isOpen ? 'Collapse' : 'Expand sub-parameters'}>{isOpen ? '▾' : '▸'}</button>
                      )}
                      {lab.name}{lab.isCompensation && <div style={{ fontSize: 10, color: 'var(--text-2)' }}>(uses per-visit compensation)</div>}
                    </td>
                    <td className="num tabular mono" title="analysed / total sub-parameters">{lab.isCompensation ? '—' : `${analyzedParamCount(lab)}/${params.length}`}</td>
                    {visitsByTime.map((v: any) => (
                      <td key={v.id} style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={(form.labSelections[lab.id] || new Set()).has(v.id)} onChange={() => toggle(lab.id, v.id)} />
                      </td>
                    ))}
                    <td>{!lab.isCompensation && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                        {visitsByTime.length > 0 && <>
                          <button className="btn ghost xs" style={{ padding: '0 4px', minWidth: 0, fontSize: 9 }} onClick={() => setRowAll(lab.id, true)} title={`Tick ${lab.name} at every visit`}>✓</button>
                          <button className="btn ghost xs" style={{ padding: '0 4px', minWidth: 0, fontSize: 9 }} onClick={() => setRowAll(lab.id, false)} title={`Clear ${lab.name} at every visit`}>✗</button>
                        </>}
                        <button className="btn ghost icon sm" onClick={() => removeLab(lab.id)} title="Remove from project"><Ico.trash /></button>
                      </div>
                    )}</td>
                  </tr>
                  {isOpen && hasParams && (
                    <tr>
                      <td colSpan={colSpan} style={{ background: 'var(--surface-alt)', padding: '8px 12px' }}>
                        <div style={{ fontSize: 10.5, color: 'var(--text-2)', marginBottom: 5 }}>Tick each sub-parameter that will be <strong>analysed</strong> (counts toward descriptive cost). Unticked = collected only (recorded in the CRF, not analysed).</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {params.map((p: any, i: number) => {
                            const on = p.analyzed !== false;
                            return (
                              <label key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, padding: '3px 9px', borderRadius: 12, background: on ? 'var(--accent-soft)' : 'var(--surface)', border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)'), color: on ? 'var(--accent)' : 'var(--text-2)', cursor: 'pointer' }}>
                                <input type="checkbox" checked={on} onChange={(e) => setParamAnalyzed(lab.id, i, e.target.checked)} style={{ accentColor: 'var(--accent)' }} />
                                {p.name}<span style={{ fontSize: 9, opacity: 0.7 }}>{on ? 'analysed' : 'collected'}</span>
                              </label>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function StepVariables({ form, set }: any) {
  const analyzedSet: Set<string> = form.nonLabAnalyzed instanceof Set ? form.nonLabAnalyzed : new Set(form.nonLabSelected);
  const toggleVar = (v: string) => {
    const sel = new Set(form.nonLabSelected);
    const an = new Set(analyzedSet);
    if (sel.has(v)) { sel.delete(v); an.delete(v); } else { sel.add(v); an.add(v); }
    set('nonLabSelected', sel); set('nonLabAnalyzed', an);
  };
  const toggleAnalyzed = (v: string) => {
    const an = new Set(analyzedSet);
    if (an.has(v)) an.delete(v); else an.add(v);
    set('nonLabAnalyzed', an);
  };
  let labVarCount = 0; let labCollected = 0;
  form.labs.forEach((lab: any) => {
    if (lab.isCompensation) return;
    const sel = form.labSelections[lab.id] || new Set();
    labVarCount += analyzedParamCount(lab) * sel.size;
    labCollected += labParams(lab).length * sel.size;
  });
  const nonLabAnalyzedCount = [...form.nonLabSelected].filter((v: string) => analyzedSet.has(v)).length;
  const totalAnalyzed = labVarCount + nonLabAnalyzedCount;
  const totalCollected = labCollected + form.nonLabSelected.size;

  // --- Catalog: search the shared variable library + inline-create (Topic A) ---
  const D = useData();
  const actions = useDataActions();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<any>({ name: '', groupName: '', propose: true });
  const addVarToGroup = (name: string, group: string) => {
    const groups = { ...form.nonLabVarGroups };
    const g = group || 'Other';
    const list = groups[g] ? [...groups[g]] : [];
    if (!list.includes(name)) list.push(name);
    groups[g] = list;
    set('nonLabVarGroups', groups);
    const sel = new Set(form.nonLabSelected); sel.add(name);
    const an = new Set(analyzedSet); an.add(name);
    set('nonLabSelected', sel); set('nonLabAnalyzed', an);
  };
  const allVarNames = new Set(Object.values(form.nonLabVarGroups).flat().map((s: any) => String(s).toLowerCase()));
  // "Group of variable" dropdown options (senior request): canonical VAR_GROUPS +
  // any group already used in this project + any group seen on library variables.
  const varGroupOptions = [...new Set([
    ...VAR_GROUPS,
    ...Object.keys(form.nonLabVarGroups || {}),
    ...D.catalog.filter((c) => c.kind === 'variable' && c.groupName).map((c) => c.groupName as string),
  ])].filter(Boolean);
  const matches = q.trim()
    ? D.catalog.filter((c) => c.kind === 'variable' && c.status === 'active'
        && c.name.toLowerCase().includes(q.trim().toLowerCase()) && !allVarNames.has(c.name.toLowerCase())).slice(0, 8)
    : [];
  const createVar = async () => {
    const name = draft.name.trim();
    if (!name) return;
    addVarToGroup(name, draft.groupName || 'Other');
    const dupe = D.catalog.some((c) => c.kind === 'variable' && c.name.toLowerCase() === name.toLowerCase() && (c.status === 'active' || c.status === 'pending'));
    if (draft.propose && !dupe) {
      try { await actions.proposeCatalogItem({ kind: 'variable', name, groupName: draft.groupName || undefined, proposeToLibrary: true, projectId: form.code }); toast('Added · proposed to library (pending admin review)'); }
      catch { toast('Added to project (library proposal failed)'); }
    } else toast(dupe ? 'Added · already in the catalog' : 'Added to this project only');
    setDraft({ name: '', groupName: '', propose: true }); setCreating(false);
  };

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
        <div className="stat"><div className="label">Lab variables (analysed)</div><div className="v tabular">{labVarCount}</div><div className="d">{labCollected} collected · analysed × visits</div></div>
        <div className="stat"><div className="label">Non-lab variables (analysed)</div><div className="v tabular">{nonLabAnalyzedCount}</div><div className="d">{form.nonLabSelected.size} collected</div></div>
        <div className="stat" style={{ borderColor: 'var(--accent)' }}><div className="label">Total analysed variables</div><div className="v tabular" style={{ color: 'var(--accent)' }}>{totalAnalyzed}</div><div className="d">{totalCollected} collected · analysed drives descriptive cost</div></div>
      </div>
      <div className="sub" style={{ marginBottom: 8 }}>Select non-laboratory variables collected from each participant. A selected variable is <strong>collected</strong>; the trailing tag toggles whether it is also <strong>analysed</strong> (drives descriptive cost).</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 240 }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the variable library to add…" style={{ width: '100%' }} />
          {matches.length > 0 && (
            <div style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, right: 0, marginTop: 2, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 240, overflowY: 'auto' }}>
              {matches.map((c) => (
                <button key={c.id} type="button" onClick={() => { addVarToGroup(c.name, c.groupName || 'Other'); setQ(''); }} style={{ display: 'flex', justifyContent: 'space-between', width: '100%', border: 'none', background: 'none', padding: '6px 10px', cursor: 'pointer', fontSize: 12, textAlign: 'left' }}>
                  <span>{c.name}</span><span style={{ color: 'var(--text-3)', fontSize: 10 }}>{c.groupName} · {c.code}</span>
                </button>
              ))}
            </div>
          )}
          {q.trim() && matches.length === 0 && (
            <div style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, right: 0, marginTop: 2, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px', fontSize: 11.5, color: 'var(--text-2)' }}>
              No library match. <button type="button" className="btn ghost xs" onClick={() => { setDraft((d: any) => ({ ...d, name: q.trim() })); setCreating(true); setQ(''); }}>Create “{q.trim()}”…</button>
            </div>
          )}
        </div>
        <button className="btn sm" onClick={() => setCreating((v) => !v)}><Ico.plus /> New variable</button>
      </div>
      {creating && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12, padding: 10, background: 'var(--surface-alt)', borderRadius: 8, border: '1px solid var(--border)' }}>
          <div className="field" style={{ minWidth: 160 }}><label>Variable name</label><input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Frailty index" /></div>
          <div className="field" style={{ width: 200 }}><label>Group of variable</label><input list="var-groups-builder" value={draft.groupName} onChange={(e) => setDraft({ ...draft, groupName: e.target.value })} placeholder="pick or type a group" /><datalist id="var-groups-builder">{varGroupOptions.map((g) => <option key={g} value={g} />)}</datalist></div>
          <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 5, paddingBottom: 8 }}><input type="checkbox" checked={draft.propose} onChange={(e) => setDraft({ ...draft, propose: e.target.checked })} /> Propose to shared library</label>
          <button className="btn primary sm" style={{ marginBottom: 2 }} onClick={createVar} disabled={!draft.name.trim()}>Add</button>
          <button className="btn ghost sm" style={{ marginBottom: 2 }} onClick={() => setCreating(false)}>Cancel</button>
        </div>
      )}
      {Object.entries(form.nonLabVarGroups).map(([group, vars]: any) => (
        <div key={group} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{group}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {vars.map((v: string) => {
              const on = form.nonLabSelected.has(v);
              const an = analyzedSet.has(v);
              return (
                <span key={v} style={{ display: 'inline-flex', alignItems: 'stretch', borderRadius: 14, overflow: 'hidden', border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)') }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, padding: '4px 10px', background: on ? 'var(--accent-soft)' : 'var(--surface-alt)', color: on ? 'var(--accent)' : 'var(--text)', fontWeight: on ? 600 : 500, cursor: 'pointer' }}>
                    <input type="checkbox" checked={on} onChange={() => toggleVar(v)} style={{ accentColor: 'var(--accent)' }} />
                    {v}
                  </label>
                  {on && (
                    <button type="button" onClick={() => toggleAnalyzed(v)} title={an ? 'Analysed — counts toward descriptive cost. Click to mark collected-only.' : 'Collected only. Click to mark analysed.'} style={{ border: 'none', borderLeft: '1px solid var(--accent)', padding: '0 8px', fontSize: 9.5, fontWeight: 700, cursor: 'pointer', background: an ? 'var(--accent)' : 'var(--surface)', color: an ? '#fff' : 'var(--text-3)' }}>
                      {an ? 'analysed' : 'collected'}
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
}

/** Sensitivity-analysis preset menu (curated by clinical-epi-statistician, CECS). */
const SENSITIVITY_PRESETS: [string, string][] = [
  ['cc-mi', 'Complete-case vs multiple imputation'],
  ['itt-pp', 'ITT vs per-protocol'],
  ['on-tx', 'On-treatment vs ITT'],
  ['alt-ps', 'Alternative PS method (matching / IPTW / stratification)'],
  ['e-value', 'Unmeasured confounding (E-value / medsens ρ)'],
  ['gee-1visit', 'Repeated measures: GEE vs single-visit'],
  ['alt-outcome', 'Alternative outcome definition'],
  ['alt-cutpoint', 'Alternative cut-point / threshold'],
  ['outliers', 'Outlier handling: trim / winsorise'],
  ['competing', 'Competing risks: cause-specific vs Fine–Gray'],
  ['adj-set', 'Adjustment-set / functional-form variation'],
  ['kappa-wts', 'Alternative weighted-kappa weights'],
];

/** Study-level ADDITIONAL analyses (priced as their own SAP line items, separate
 *  from per-outcome subgroup/sensitivity). Default prices are editable per project. */
const EXTRA_ANALYSIS_PRESETS: { key: string; label: string; price: number; hint: string }[] = [
  { key: 'cea', label: 'Cost-effectiveness / cost-utility analysis (CEA/CUA)', price: 25000, hint: 'ICER, cost per QALY, CEAC — health-economic evaluation alongside the trial.' },
  { key: 'cace', label: 'CACE — complier-average causal effect', price: 15000, hint: 'Instrumental-variable estimate of the effect among compliers (vs ITT).' },
  { key: 'missing', label: 'Missing-data management (multiple imputation)', price: 12000, hint: 'MICE / FCS imputation + pooling under MAR, with sensitivity to MNAR.' },
];

/** Shared outcomes editor (PICO "O"): numbered primary/secondary outcomes, each linked to
 *  a timepoint VISIT (create one inline if needed). Used in Study Design + Outcomes steps. */
const OUT_COLS_KEY = 'heros.outcomeColWidths';
const DEFAULT_OUT_COLS = { num: 40, name: 240, role: 116, tp: 230, simple: 66, complex: 66 };
const OUT_COL_MIN = { num: 32, name: 150, role: 90, tp: 150, simple: 52, complex: 52 } as const;
/** Header definitions for the outcomes table — order matters (drives the colgroup). */
const OUT_COL_DEFS: { key: keyof typeof DEFAULT_OUT_COLS; label: string; num?: boolean }[] = [
  { key: 'num', label: '#' },
  { key: 'name', label: 'Outcome / measure' },
  { key: 'role', label: 'Role' },
  { key: 'tp', label: 'Timepoint (visit)' },
  { key: 'simple', label: 'Simple', num: true },
  { key: 'complex', label: 'Complex', num: true },
];

/** Read-only outcome reflection for Study Design. Outcomes are ENTERED ONCE, in the
 *  Outcomes & Analysis step (brief Feature 4 — single source of outcome truth). */
function OutcomesEditor({ form, set, showPlan = true }: any) {
  const D = useData();
  const { cols, activeCol, startResize, resetCol } = useColWidths(OUT_COLS_KEY, DEFAULT_OUT_COLS, OUT_COL_MIN);
  const [activeName, setActiveName] = useState<string | null>(null);
  // Suggestion pool = catalog item names + their sub-parameter names (e.g. Haemoglobin, LDL-C).
  const pool = useMemo(() => {
    const s = new Set<string>();
    (D.catalog || []).forEach((c: any) => { if (c.name) s.add(c.name); (c.parameters || []).forEach((p: any) => p?.name && s.add(p.name)); });
    return [...s];
  }, [D.catalog]);
  const fuzzy = (q: string, name: string) => {
    const nm = name.toLowerCase();
    if (nm.includes(q)) return 2;
    let i = 0; for (const ch of nm) { if (ch === q[i]) i++; if (i === q.length) return 1; }
    return 0;
  };
  const suggestFor = (raw: string) => {
    const q = (raw || '').trim().toLowerCase();
    if (q.length < 2) return [] as string[];
    return pool.map((n) => [n, fuzzy(q, n)] as [string, number])
      .filter(([n, sc]) => sc > 0 && n.toLowerCase() !== q)
      .sort((a, b) => b[1] - a[1]).slice(0, 7).map(([n]) => n);
  };
  const updOut = (id: string, k: string, v: any) => set('outcomes', form.outcomes.map((o: any) => (o.id === id ? { ...o, [k]: v } : o)));
  const patchOut = (id: string, patch: any) => set('outcomes', form.outcomes.map((o: any) => (o.id === id ? { ...o, ...patch } : o)));
  const delOut = (id: string) => set('outcomes', form.outcomes.filter((o: any) => o.id !== id));
  const addOut = (kind: string) => set('outcomes', [...form.outcomes, { id: 'o' + Date.now(), name: '', kind, simpleUnits: kind === 'Primary' ? 4 : 2, complexUnits: kind === 'Primary' ? 2 : 0, linkedVisitId: '', timepointText: '' }]);
  const dupOut = (o: any) => set('outcomes', [...form.outcomes, { ...o, id: 'o' + Date.now(), kind: o.kind === 'Primary' ? 'Secondary' : o.kind }]);
  const moveOut = (idx: number, dir: number) => {
    const arr = [...form.outcomes]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]]; set('outcomes', arr);
  };
  const visitsByTime = [...form.visits].sort((a: any, b: any) => (a.week ?? 0) - (b.week ?? 0));
  const ROLES = ['Primary', 'Secondary', 'Safety', 'Compliance', 'Exploratory'];
  const ABBR: any = { Primary: 'P', Secondary: 'S', Safety: 'SA', Compliance: 'C', Exploratory: 'E' };
  const counters: any = {};

  // --- Typeable, fuzzy timepoint (parses day/week/month/year → links/creates a visit) ---
  // Timepoint = two blocks: a number + a unit (day/week/month/year). Derives canonical weeks,
  // auto-links a matching visit, or offers to create one.
  // Timepoint is entered in WEEKS only (brief Feature 1). The value IS the week number.
  const tpValOf = (o: any) => {
    if (o.tpValue != null && o.tpValue !== '') return o.tpValue;
    const v = form.visits.find((x: any) => x.id === o.linkedVisitId);
    return v ? (v.week ?? v.timeValue ?? '') : '';
  };
  const matchVisit = (weeks: number) => visitsByTime.find((v: any) => Math.abs((v.week ?? 0) - weeks) < 0.6);
  const onTp = (o: any, value: any) => {
    const v = value === '' || value == null ? '' : Number(value);
    if (v === '' || Number.isNaN(v as number)) { patchOut(o.id, { tpValue: '', tpUnit: 'week', linkedVisitId: '' }); return; }
    const m = matchVisit(v as number);
    patchOut(o.id, { tpValue: v, tpUnit: 'week', linkedVisitId: m ? m.id : '' });
  };
  const createTpVisitFromBlocks = (o: any) => {
    const v = Number(tpValOf(o)) || 0;
    const vid = 'v' + Date.now();
    set('visits', [...form.visits, { id: vid, name: `Week ${v}`, type: inferVisitType(v), week: v, timeValue: v, timeUnit: 'week', required: true, compensation: 0 }]);
    patchOut(o.id, { tpValue: v, tpUnit: 'week', linkedVisitId: vid });
  };

  // --- Mouse-resizable columns: shared useColWidths hook (persisted, double-click to reset) ---

  // --- Per-outcome analysis plan: Subgroup (effect modification) + Sensitivity (robustness) ---
  const [planOpen, setPlanOpen] = useState<string | null>(null);
  const varSuggest = useMemo(() => {
    const s = new Set<string>([...(form.nonLabSelected || [])]);
    (D.catalog || []).forEach((c: any) => { if (c.kind === 'variable' && c.name) s.add(c.name); });
    return [...s];
  }, [form.nonLabSelected, D.catalog]);
  const addSubgroup = (o: any) => patchOut(o.id, { subgroups: [...(o.subgroups || []), { id: 'sg' + Date.now(), modifier: '', categories: 2, prespecified: true, testInteraction: true }] });
  const updSubgroup = (o: any, sid: string, patch: any) => patchOut(o.id, { subgroups: (o.subgroups || []).map((s: any) => (s.id === sid ? { ...s, ...patch } : s)) });
  const rmSubgroup = (o: any, sid: string) => patchOut(o.id, { subgroups: (o.subgroups || []).filter((s: any) => s.id !== sid) });
  const toggleSens = (o: any, key: string) => { const cur = o.sensitivity || []; patchOut(o.id, { sensitivity: cur.includes(key) ? cur.filter((k: string) => k !== key) : [...cur, key] }); };
  const planCount = (o: any) => (o.subgroups || []).length + (o.sensitivity || []).length;

  // Mouse drag-to-reorder for outcome rows (in addition to the ▲▼ buttons).
  const { rowProps: outRowProps, gripProps: outGripProps } = useRowDrag(form.outcomes, (next: any[]) => set('outcomes', next));

  return (
    <>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <button className="btn primary sm" onClick={() => addOut('Primary')}><Ico.plus /> Primary outcome</button>
        <button className="btn sm" onClick={() => addOut('Secondary')}><Ico.plus /> Secondary outcome</button>
        <button className="btn ghost sm" onClick={() => addOut('Safety')}><Ico.plus /> Safety</button>
      </div>
      <table style={{ tableLayout: 'fixed', width: '100%' }}>
        <colgroup>
          {OUT_COL_DEFS.map((c) => <col key={c.key} style={{ width: cols[c.key] }} />)}
          <col style={{ width: 104 }} />
        </colgroup>
        <thead><tr>
          {OUT_COL_DEFS.map((c) => (
            <ResizableTh key={c.key} label={c.label} className={c.num ? 'num' : undefined}
              active={activeCol === c.key} onMouseDown={startResize(c.key)} onDoubleClick={resetCol(c.key)} />
          ))}
          <th></th>
        </tr></thead>
        <tbody>
          {form.outcomes.map((o: any, idx: number) => {
            counters[o.kind] = (counters[o.kind] || 0) + 1;
            const num = (ABBR[o.kind] || 'O') + counters[o.kind];
            return (
              <React.Fragment key={o.id}>
              <tr {...outRowProps(idx)}>
                <td className="mono" style={{ fontWeight: 700, color: o.kind === 'Primary' ? 'var(--accent)' : 'var(--text-2)' }}><span {...outGripProps(idx)}>⠿</span>{num}</td>
                <td style={{ position: 'relative', overflow: 'visible' }}>
                  <textarea value={o.name} onChange={(e) => updOut(o.id, 'name', e.target.value)} onFocus={() => setActiveName(o.id)} onBlur={() => setTimeout(() => setActiveName((a) => (a === o.id ? null : a)), 150)} placeholder="e.g. Change from baseline in serum uric acid at week 24, measured by …" rows={2} style={{ width: '100%', minHeight: 46, resize: 'vertical', lineHeight: 1.4, fontFamily: 'inherit' }} />
                  {activeName === o.id && suggestFor(o.name).length > 0 && (
                    <div style={{ position: 'absolute', zIndex: 40, top: '100%', left: 0, right: 0, marginTop: 2, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto' }}>
                      <div style={{ fontSize: 9.5, color: 'var(--text-3)', padding: '4px 10px', borderBottom: '1px solid var(--border)' }}>From library</div>
                      {suggestFor(o.name).map((n) => (
                        <button key={n} type="button" onMouseDown={(e) => { e.preventDefault(); updOut(o.id, 'name', n); setActiveName(null); }} style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'none', padding: '5px 10px', fontSize: 11.5, cursor: 'pointer' }}>{n}</button>
                      ))}
                    </div>
                  )}
                </td>
                <td><select value={o.kind} onChange={(e) => updOut(o.id, 'kind', e.target.value)} style={{ width: '100%' }}>{ROLES.map((k) => <option key={k}>{k}</option>)}</select></td>
                <td>
                  {(() => {
                    const val = tpValOf(o);
                    const linked = form.visits.find((v: any) => v.id === o.linkedVisitId);
                    const hasVal = val !== '' && val != null;
                    return (
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input type="number" className="tabular mono" value={val} placeholder="#" onChange={(e) => onTp(o, e.target.value)} style={{ width: 48, textAlign: 'right' }} />
                        <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>wk</span>
                        {linked ? <span title={`Linked to ${linked.name}`} style={{ fontSize: 9.5, color: '#2E6A3C', whiteSpace: 'nowrap' }}>✓ {linked.name}</span>
                          : hasVal ? <button type="button" className="btn ghost xs" title="Create a visit at this timepoint" onClick={() => createTpVisitFromBlocks(o)}>＋ visit</button>
                            : null}
                      </div>
                    );
                  })()}
                </td>
                <td><input type="number" className="tabular mono" min="0" value={o.simpleUnits} onChange={(e) => updOut(o.id, 'simpleUnits', +e.target.value)} style={{ textAlign: 'right', width: '100%' }} /></td>
                <td><input type="number" className="tabular mono" min="0" value={o.complexUnits} onChange={(e) => updOut(o.id, 'complexUnits', +e.target.value)} style={{ textAlign: 'right', width: '100%' }} /></td>
                <td><div style={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <button className="btn ghost icon sm" title="Move up" disabled={idx === 0} onClick={() => moveOut(idx, -1)}>▲</button>
                  <button className="btn ghost icon sm" title="Move down" disabled={idx === form.outcomes.length - 1} onClick={() => moveOut(idx, 1)}>▼</button>
                  {showPlan && <button className="btn ghost xs" title="Subgroup & sensitivity analysis plan" onClick={() => setPlanOpen((p) => (p === o.id ? null : o.id))}>Plan{planCount(o) ? ` ·${planCount(o)}` : ''}</button>}
                  <button className="btn ghost xs" title="Duplicate at another timepoint" onClick={() => dupOut(o)}>Dup</button>
                  <button className="btn ghost icon sm" onClick={() => delOut(o.id)}><Ico.trash /></button>
                </div></td>
              </tr>
              {showPlan && planOpen === o.id && (
                <tr><td colSpan={7} style={{ background: 'var(--surface-alt)', padding: '10px 12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', marginBottom: 4 }}>Subgroup analyses — effect modification</div>
                      <div style={{ fontSize: 10, color: 'var(--text-3)', marginBottom: 6 }}>Each modifier = an interaction term on the full model (margins per subgroup). Don't split the data; pre-specify and judge by the interaction test. <strong>Categories</strong> = how many strata the modifier has — it adds to the analysed-variable count (formula chosen in Data Analysis &amp; SAP).</div>
                      {(o.subgroups || []).map((s: any) => (
                        <div key={s.id} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                          <input list={`varsg-${o.id}`} value={s.modifier} onChange={(e) => updSubgroup(o, s.id, { modifier: e.target.value })} placeholder="modifier e.g. Sex, Smoking, Age" style={{ flex: 1 }} />
                          <label style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 3 }} title="Number of categories / strata of this modifier (e.g. Sex = 2). Drives how many analysed variables the subgroup adds."><input type="number" min="1" className="tabular mono" value={s.categories ?? 2} onChange={(e) => updSubgroup(o, s.id, { categories: Math.max(1, Math.round(+e.target.value) || 1) })} style={{ width: 42, textAlign: 'right' }} /> cat</label>
                          <label style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 3 }} title="Pre-specified = confirmatory; else exploratory"><input type="checkbox" checked={s.prespecified !== false} onChange={(e) => updSubgroup(o, s.id, { prespecified: e.target.checked })} /> pre-spec</label>
                          <button className="btn ghost icon sm" onClick={() => rmSubgroup(o, s.id)}><Ico.close /></button>
                        </div>
                      ))}
                      <datalist id={`varsg-${o.id}`}>{varSuggest.map((v) => <option key={v} value={v} />)}</datalist>
                      <button className="btn ghost xs" onClick={() => addSubgroup(o)}><Ico.plus /> Add subgroup</button>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', marginBottom: 4 }}>Sensitivity analyses — robustness</div>
                      <div style={{ fontSize: 10, color: 'var(--text-3)', marginBottom: 6 }}>Robustness checks (NOT diagnostic sensitivity). Reported alongside the primary result, never replacing it. Pick a focused, pre-specified set.</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                        {SENSITIVITY_PRESETS.map(([key, label]) => {
                          const on = (o.sensitivity || []).includes(key);
                          return (
                            <label key={key} title={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, padding: '3px 8px', borderRadius: 11, cursor: 'pointer', background: on ? 'var(--accent-soft)' : 'var(--surface)', border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)'), color: on ? 'var(--accent)' : 'var(--text-2)' }}>
                              <input type="checkbox" checked={on} onChange={() => toggleSens(o, key)} style={{ accentColor: 'var(--accent)' }} />{label}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </td></tr>
              )}
              </React.Fragment>
            );
          })}
          {form.outcomes.length === 0 && <tr><td colSpan={7} style={{ padding: 16, textAlign: 'center', color: 'var(--text-2)' }}>No outcomes yet — add a primary outcome to start.</td></tr>}
        </tbody>
      </table>
      <div style={{ fontSize: 10.5, color: 'var(--text-2)', marginTop: 6 }}>
        <strong>Timepoint</strong>: a number in <strong>weeks</strong> — auto-links a matching visit or <strong>＋ visit</strong> creates one. <strong>▲▼</strong> reorder rows; drag column borders to resize. {showPlan && <><strong>Plan</strong> sets per-outcome subgroup &amp; sensitivity analyses (each priced into the SAP). </>}Same measure at two timepoints? <strong>Dup</strong> and change the role + timepoint.
      </div>
    </>
  );
}

function StepOutcomes({ form, set }: any) {
  const totalSimple = form.outcomes.reduce((s: number, o: any) => s + (+o.simpleUnits || 0), 0);
  const totalComplex = form.outcomes.reduce((s: number, o: any) => s + (+o.complexUnits || 0), 0);

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
        <div className="stat"><div className="label">Outcomes</div><div className="v tabular">{form.outcomes.length}</div></div>
        <div className="stat"><div className="label">Total simple units</div><div className="v tabular">{totalSimple}</div></div>
        <div className="stat"><div className="label">Total complex units</div><div className="v tabular">{totalComplex}</div></div>
      </div>

      <div className="sub" style={{ marginBottom: 8 }}>Outcomes are defined here and in <strong>Study Design</strong> (same list). Each gets simple/complex analysis units and a timepoint visit. Pricing for these units is set in the <strong>Data Analysis &amp; SAP</strong> step.</div>
      <OutcomesEditor form={form} set={set} />
    </>
  );
}

/** Data Management — its own wizard step (was a card inside Review Quotation).
 *  Edits form.dmItems, which both quotation engines roll up into section 2. */
function StepDataMgmt({ form, set }: any) {
  return (
    <>
      <div className="sub" style={{ marginBottom: 10 }}>
        Choose the Data-Management work this study needs (DMP development, CRF / eCRF build). The selection prices the quotation’s <strong>“2. Data Management”</strong> section — the Review step shows it as its 2.1 / 2.2 sub-totals.
      </div>
      <DataManagementEditor form={form} set={set} />
    </>
  );
}

/** Data Analysis & SAP — its own wizard step (cost rules moved out of Outcomes).
 *  Owns the analysis pricing knobs + optional study-level analyses that price
 *  the quotation's "3. Data Analysis & SAP" section. */
function StepAnalysis({ form, set, quotation }: any) {
  const totalSimple = form.outcomes.reduce((s: number, o: any) => s + (+o.simpleUnits || 0), 0);
  const totalComplex = form.outcomes.reduce((s: number, o: any) => s + (+o.complexUnits || 0), 0);
  const analysisSection = (quotation?.sections || []).find((s: any) => /data analysis/i.test(s.title));

  // Study-level additional analyses: built-in presets + admin-curated Add-on
  // service LIBRARY items (catalog kind='addon'), so the databank syncs into
  // the project picker — mirrors how labs merge the registry + DB catalog.
  const D = useData();
  const addonExtras = (D.catalog || [])
    .filter((c: any) => c.kind === 'addon' && c.status === 'active')
    .map((c: any) => ({ key: 'cat_' + c.id, label: c.name, price: c.price ?? 0, hint: c.groupName || 'From the Add-on service library.' }));
  const ALL_EXTRAS = [...EXTRA_ANALYSIS_PRESETS, ...addonExtras];
  const extra: any[] = form.extraAnalyses || [];
  const extraOn = (key: string) => extra.some((a) => a.key === key);
  const extraPrice = (key: string) => {
    const a = extra.find((x) => x.key === key);
    if (a) return a.price;
    return ALL_EXTRAS.find((p) => p.key === key)?.price ?? 0;
  };
  const toggleExtra = (p: { key: string; label: string }) => {
    if (extraOn(p.key)) set('extraAnalyses', extra.filter((a) => a.key !== p.key));
    else set('extraAnalyses', [...extra, { key: p.key, label: p.label, price: extraPrice(p.key) }]);
  };
  const setExtraPrice = (key: string, price: number) => set('extraAnalyses', extra.map((a) => (a.key === key ? { ...a, price } : a)));

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
        <div className="stat"><div className="label">Total simple units</div><div className="v tabular">{totalSimple}</div><div className="d">{form.minutesPerSimple} min each</div></div>
        <div className="stat"><div className="label">Total complex units</div><div className="v tabular">{totalComplex}</div><div className="d">{form.minutesPerComplex} min each</div></div>
        <div className="stat"><div className="label">Hourly rate</div><div className="v">฿{form.hourlyRate.toLocaleString()}</div></div>
        <div className="stat"><div className="label">Section 3 subtotal</div><div className="v tabular">{analysisSection ? fmt.thb(analysisSection.total) : '—'}</div><div className="d">Data Analysis &amp; SAP</div></div>
      </div>

      <div className="card">
        <div className="card-h"><h3>Analysis pricing rules</h3></div>
        <div className="card-b">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            <div className="field"><label>Minutes per simple unit</label><input type="number" min="1" value={form.minutesPerSimple} onChange={(e) => set('minutesPerSimple', +e.target.value)} /></div>
            <div className="field"><label>Minutes per complex unit</label><input type="number" min="1" value={form.minutesPerComplex} onChange={(e) => set('minutesPerComplex', +e.target.value)} /></div>
            <div className="field"><label>Hourly rate (฿)</label><input type="number" min="0" value={form.hourlyRate} onChange={(e) => set('hourlyRate', +e.target.value)} /></div>
            <div className="field"><label>Complex design multiplier</label>
              <select value={form.complexMultiplier} onChange={(e) => set('complexMultiplier', +e.target.value)}>
                <option value={1.0}>×1.00 — standard</option>
                <option value={1.1}>×1.10 — adaptive / cluster</option>
                <option value={1.2}>×1.20 — crossover / factorial</option>
              </select>
            </div>
            <div className="field"><label>Summary report mode</label>
              <select value={form.summaryReportMode || 'copy'} onChange={(e) => set('summaryReportMode', e.target.value)}>
                <option value="copy">Mirror analysis cost</option>
                <option value="separate">Separate (60% of analysis)</option>
                <option value="custom">Custom (% or ฿ amount)</option>
              </select>
            </div>
            <div className="field"><label>Subgroup → variable count</label>
              <select value={form.subgroupCountMode || 'catsMinus1'} onChange={(e) => set('subgroupCountMode', e.target.value)} title="How a subgrouped variable adds to the analysed-variable count (reported as base(+added))">
                {SUBGROUP_COUNT_MODES.map((m) => <option key={m.value} value={m.value} title={m.hint}>{m.label}</option>)}
              </select>
            </div>
          </div>
          {form.summaryReportMode === 'custom' && (() => {
            const analysisBase = (quotation?.meta?.analysisTotal ?? analysisSection?.total ?? 0) as number;
            const basisAmount = form.summaryReportBasis === 'amount' && Number.isFinite(Number(form.summaryReportAmount));
            const pct = basisAmount ? (analysisBase > 0 ? Math.round((Number(form.summaryReportAmount) / analysisBase) * 1000) / 10 : 0) : (Number.isFinite(Number(form.summaryReportPct)) ? Number(form.summaryReportPct) : 100);
            const amount = basisAmount ? Math.max(0, Math.round(Number(form.summaryReportAmount))) : Math.max(0, Math.round(analysisBase * pct / 100));
            return (
              <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: '1px solid var(--accent)', background: 'var(--accent-soft)' }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-2)', marginBottom: 8 }}>Custom summary-report price</div>
                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div className="field" style={{ margin: 0 }}><label>Percentage of analysis</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input type="number" min="0" step="0.1" className="tabular mono" style={{ width: 100, textAlign: 'right' }} value={pct} onChange={(e) => { set('summaryReportBasis', 'pct'); set('summaryReportPct', Math.max(0, +e.target.value || 0)); }} />
                      <span style={{ color: 'var(--text-3)' }}>%</span>
                    </div>
                  </div>
                  <div style={{ color: 'var(--text-3)', paddingBottom: 8 }}>↔</div>
                  <div className="field" style={{ margin: 0 }}><label>Raw amount (฿)</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ color: 'var(--text-3)' }}>฿</span>
                      <input type="number" min="0" className="tabular mono" style={{ width: 130, textAlign: 'right' }} value={amount} onChange={(e) => { set('summaryReportBasis', 'amount'); set('summaryReportAmount', Math.max(0, +e.target.value || 0)); }} />
                    </div>
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-3)', paddingBottom: 8, flex: 1, minWidth: 180 }}>
                    Analysis cost {fmt.thb(analysisBase)} · edit either field — the other recalculates. {basisAmount ? 'Pinned to the ฿ amount.' : 'Tracks the % of analysis.'}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <div className="card-h"><h3>Additional study-level analyses</h3></div>
        <div className="card-b">
          <div className="sub" style={{ marginBottom: 10 }}>Optional analyses beyond the primary SAP. Tick to include — each is priced as its own line in the quotation (section 3). Prices are editable per project.{addonExtras.length > 0 && <> Includes <strong>{addonExtras.length}</strong> add-on{addonExtras.length === 1 ? '' : 's'} from the Pricing Library’s <strong>Add-on service</strong> tab.</>}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ALL_EXTRAS.map((p) => {
              const on = extraOn(p.key);
              return (
                <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)'), background: on ? 'var(--accent-soft)' : 'var(--surface)' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flex: 1, cursor: 'pointer' }}>
                    <input type="checkbox" checked={on} onChange={() => toggleExtra(p)} style={{ accentColor: 'var(--accent)', marginTop: 2 }} />
                    <span>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{p.label}</div>
                      <div style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{p.hint}</div>
                    </span>
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, opacity: on ? 1 : 0.4 }}>
                    <span style={{ fontSize: 11, color: 'var(--text-3)' }}>฿</span>
                    <input type="number" className="tabular mono" min="0" disabled={!on} value={extraPrice(p.key)} onChange={(e) => setExtraPrice(p.key, +e.target.value || 0)} style={{ width: 96, textAlign: 'right' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

/** Data-Management quotation section editor (brief Feature 6). Drives form.dmItems,
 *  which both quotation engines roll up into the "2. Data Management" section. */
function DataManagementEditor({ form, set }: any) {
  const canPrice = useCan('quote.adjust') === 'allow';
  const D = useData();
  // The editable DB databank (admin-curated); falls back to the built-in
  // constant so the picker + pricing never go empty if the table is unseeded.
  const dmCat = D.dmCatalog && D.dmCatalog.length ? D.dmCatalog : DM_CATALOG;
  const sels: DataManagementSelection[] = Array.isArray(form.dmItems) ? form.dmItems : defaultDmSelections();
  const byId = new Map(sels.map((s) => [s.itemId, s]));
  const isSel = (item: any) => (byId.has(item.id) ? !!byId.get(item.id)!.selected : item.defaultSelected);
  const selOf = (id: string) => byId.get(id);

  const writeSel = (itemId: string, patch: Partial<DataManagementSelection>) => {
    const next = byId.has(itemId)
      ? sels.map((s) => (s.itemId === itemId ? { ...s, ...patch } : s))
      : [...sels, { itemId, selected: false, ...patch }];
    set('dmItems', next);
  };
  const setSelected = (item: any, selected: boolean) => {
    let next: DataManagementSelection[] = byId.has(item.id)
      ? sels.map((s) => (s.itemId === item.id ? { ...s, selected } : s))
      : [...sels, { itemId: item.id, selected }];
    if (selected && item.exclusiveGroup) {
      const siblings = dmCat.filter((i) => i.exclusiveGroup === item.exclusiveGroup && i.id !== item.id).map((i) => i.id);
      next = next.map((s) => (siblings.includes(s.itemId) ? { ...s, selected: false } : s));
      siblings.forEach((sid2) => { if (!next.some((s) => s.itemId === sid2)) next.push({ itemId: sid2, selected: false }); });
    }
    set('dmItems', next);
  };

  const section = computeDmSection(sels, dmCat);
  const cats: Array<{ key: 'DMP' | 'CRF'; label: string }> = [
    { key: 'DMP', label: 'A. DMP Development' },
    { key: 'CRF', label: 'B. CRF / eCRF Development' },
  ];
  // Two-level expand: the editor first shows just the two subsets (with their
  // counts + subtotals); expanding a subset reveals its item table.
  const [openCats, setOpenCats] = useState<Record<string, boolean>>({});
  const isCatOpen = (k: string) => openCats[k] ?? false;
  const catTotal = (k: 'DMP' | 'CRF') => section.items.filter((it: any) => it.code === k).reduce((s: number, it: any) => s + (it.total || 0), 0);
  const catSelected = (k: 'DMP' | 'CRF') => section.items.filter((it: any) => it.code === k).length;
  const catCount = (k: 'DMP' | 'CRF') => dmCat.filter((i) => i.category === k && i.active).length;

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-h" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3>Data Management — quotation items</h3>
        <span className="mono tabular" style={{ fontWeight: 700, color: 'var(--navy)' }}>Subtotal {fmt.thb(section.total)}</span>
      </div>
      <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
          Click a subset to expand its items. Tick the items needed for this study. Cost = estimated units × unit price; “minimum cost” items are billed as a lump sum. Mutually-exclusive choices (e.g. No-DSMB vs DSMB) auto-deselect their partner. {canPrice ? 'You may edit units and unit price.' : 'You may edit units; unit price is read-only for your role.'}
        </div>
        {cats.map((cat) => (
          <div key={cat.key} style={{ border: '1px solid var(--border)', borderRadius: 8 }}>
            <button
              type="button"
              onClick={() => setOpenCats((o) => ({ ...o, [cat.key]: !isCatOpen(cat.key) }))}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px', border: 'none', background: 'var(--surface-alt)', borderRadius: 8, cursor: 'pointer', textAlign: 'left' }}>
              <span style={{ width: 14, color: 'var(--text-3)' }}>{isCatOpen(cat.key) ? '▾' : '▸'}</span>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{cat.label}</span>
              <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{catSelected(cat.key)} of {catCount(cat.key)} items selected</span>
              <span className="mono tabular" style={{ marginLeft: 'auto', fontWeight: 700, color: 'var(--navy)' }}>{fmt.thb(catTotal(cat.key))}</span>
            </button>
            {isCatOpen(cat.key) && (
            <div className="scroll-x" style={{ padding: '0 10px 10px' }}>
              <table style={{ width: '100%' }}>
                <thead><tr>
                  <th style={{ width: 30 }}></th><th>Item</th>
                  <th className="num" style={{ width: 70 }}>Units</th>
                  <th className="num" style={{ width: 90 }}>Unit ฿</th>
                  <th className="num" style={{ width: 90 }}>Cost ฿</th>
                </tr></thead>
                <tbody>
                  {dmCat.filter((i) => i.category === cat.key && i.active).map((item) => {
                    const on = isSel(item);
                    const sel = selOf(item.id);
                    const units = sel?.units ?? item.estUnits;
                    const price = sel?.unitPrice ?? item.unitPrice;
                    const cost = dmItemCost(item, sel);
                    return (
                      <tr key={item.id} style={{ opacity: on ? 1 : 0.55 }}>
                        <td><input type="checkbox" checked={on} onChange={(e) => setSelected(item, e.target.checked)} title={item.exclusiveGroup ? `Mutually exclusive within "${item.exclusiveGroup}"` : undefined} /></td>
                        <td style={{ fontSize: 12 }}>
                          {item.name}
                          {item.isFixedMin && <span style={{ marginLeft: 6, fontSize: 9.5, color: 'var(--accent)' }}>min cost</span>}
                          {item.exclusiveGroup && <span style={{ marginLeft: 6, fontSize: 9.5, color: 'var(--text-3)' }}>· either/or</span>}
                          {item.details && <div style={{ fontSize: 10, color: 'var(--text-3)' }}>{item.details}</div>}
                        </td>
                        <td className="num">
                          {item.isFixedMin || !item.editableUnits
                            ? <span style={{ color: 'var(--text-3)' }}>—</span>
                            : <input type="number" min="0" className="tabular mono" disabled={!on} value={units} onChange={(e) => writeSel(item.id, { units: Math.max(0, +e.target.value) })} style={{ width: 56, textAlign: 'right' }} />}
                        </td>
                        <td className="num">
                          {item.isFixedMin
                            ? <span style={{ color: 'var(--text-3)' }}>{fmt.thb(item.fixedCost || 0)}</span>
                            : canPrice
                              ? <input type="number" min="0" className="tabular mono" disabled={!on} value={price} onChange={(e) => writeSel(item.id, { unitPrice: Math.max(0, +e.target.value) })} style={{ width: 76, textAlign: 'right' }} />
                              : <span className="tabular mono" style={{ color: 'var(--text-2)' }}>{fmt.thb(price)}</span>}
                        </td>
                        <td className="num tabular mono" style={{ fontWeight: on ? 600 : 400, color: on ? 'var(--text)' : 'var(--text-3)' }}>{on ? fmt.thb(cost) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn ghost sm" onClick={() => set('dmItems', defaultDmSelections())}>↻ Reset to defaults</button>
          <span style={{ fontSize: 10.5, color: 'var(--text-3)', alignSelf: 'center' }}>{section.items.length} item{section.items.length === 1 ? '' : 's'} selected → flows into the quotation’s “2. Data Management” section.</span>
        </div>
      </div>
    </div>
  );
}

/** The DM section's sub-groups (2.1 DMP / 2.2 CRF), built from item codes —
 *  shared by the Review tree display and the export shaper. */
function dmSubGroupsOf(s: any) {
  if (!/data management/i.test(s.title)) return null;
  // Match on the carried DM category (`group`, set by computeQuotation); the
  // item `code` is the sequential display number (2.1, 2.2, …), NOT the group.
  const groups = [
    { key: 'DMP', code: '2.1', label: 'DMP Development', items: (s.items || []).filter((it: any) => it.group === 'DMP') },
    { key: 'CRF', code: '2.2', label: 'CRF / eCRF Development', items: (s.items || []).filter((it: any) => it.group === 'CRF') },
  ].filter((g) => g.items.length > 0);
  return groups.length ? groups : null;
}

/** Section export modes. 'subgroups' is only meaningful for Data Management. */
type SectionExportMode = 'detail' | 'subgroups' | 'summary';

/** Shape the quotation sections for export according to the per-section modes
 *  (summary = section line only; subgroups = one line per 2.1/2.2 group). */
function shapeSectionsForExport(sections: any[], modes: Record<string, SectionExportMode>): any[] {
  return sections.map((s) => {
    const subs = dmSubGroupsOf(s);
    const mode = modes[s.title] ?? (subs ? 'subgroups' : 'detail');
    let items = s.items || [];
    if (mode === 'summary') items = [];
    else if (mode === 'subgroups' && subs) {
      items = subs.map((g) => {
        const total = g.items.reduce((sum: number, it: any) => sum + (it.total || 0), 0);
        return { code: g.code, name: g.label, qty: g.items.length, unit: 'items', price: total, total };
      });
    }
    return { ...s, items };
  });
}

/** Export options modal: pick Detail / Sub-groups / Summary per section, then
 *  download as PDF (server LaTeX) or Excel-openable CSV. The default matches
 *  the on-screen tree: everything detailed except Data Management = 2.1/2.2. */
function ExportQuotationModal({ form, quotation, version, onClose }: any) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [modes, setModes] = useState<Record<string, SectionExportMode>>(() =>
    Object.fromEntries(quotation.sections.map((s: any) => [s.title, dmSubGroupsOf(s) ? 'subgroups' : 'detail'])));

  const shaped = shapeSectionsForExport(quotation.sections, modes);

  const exportPdf = async () => {
    setBusy(true);
    try { await downloadQuotationPdf(form, { ...quotation, sections: shaped }, version); onClose(); }
    catch (e: any) {
      const m = String(e?.message || '');
      toast(/not installed|Unavailable|503/i.test(m) ? 'Server LaTeX is unavailable — cannot generate the PDF.' : 'Quotation PDF failed — please try again.');
    } finally { setBusy(false); }
  };

  // Templated Excel export. Excel opens a styled HTML table natively, so we
  // emit a formatted .xls (HEROs header + section/item rows + grand total)
  // instead of the old bare CSV ("plain text"). UTF-8 BOM keeps Thai correct.
  const exportExcel = () => {
    const esc = (v: any) => String(v ?? '').replace(/[&<>]/g, (c) => (({ '&': '&amp;', '<': '&lt;', '>': '&gt;' } as any)[c]));
    const baht = (n: any) => '฿' + (Math.round(Number(n) || 0)).toLocaleString();
    const body = shaped.map((s: any) => {
      const head = `<tr><td class="sec" colspan="2">${esc(s.title)}</td><td class="sec num">${baht(s.total)}</td></tr>`;
      const items = (s.items || []).map((it: any) => `<tr><td class="code">${esc(it.code || '')}</td><td>${esc(it.name)}</td><td class="num">${baht(it.total)}</td></tr>`).join('');
      return head + items;
    }).join('');
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"><style>
      table{border-collapse:collapse;font-family:Calibri,Arial,sans-serif;font-size:11pt}
      td,th{border:0.5pt solid #c9c9c9;padding:4px 10px}
      .title{font-size:16pt;font-weight:bold;color:#1A1F2E;border:none}
      .org{font-size:9pt;color:#555;border:none}
      .meta td{border:none;padding:2px 8px}
      .sec{background:#1A1F2E;color:#ffffff;font-weight:bold}
      .code{color:#888888}
      .num{text-align:right}
      th{background:#eef0f4;font-weight:bold}
      .grand td{background:#9E2B25;color:#ffffff;font-weight:bold;font-size:13pt}
    </style></head><body>
      <table><tr><td class="title" colspan="3">HEROs — Analysis Quotation</td></tr>
      <tr><td class="org" colspan="3">Health Evidence and Research Operations (HEROs) · BioCE · Faculty of Medicine, Chiang Mai University</td></tr></table>
      <table>
        <tr class="meta"><td>Project</td><td colspan="2">${esc(form.code || '')} — ${esc(form.title || form.shortTitle || '')}</td></tr>
        <tr class="meta"><td>Version</td><td colspan="2">${esc(version)}</td></tr>
        <tr class="meta"><td>Design</td><td colspan="2">${esc(form.design || '')} · N = ${esc(form.sampleSize || 0)}</td></tr>
      </table>
      <table>
        <tr><th class="code">Code</th><th>Description</th><th class="num">Total (฿)</th></tr>
        ${body}
        <tr class="grand"><td colspan="2">Grand total</td><td class="num">${baht(quotation.grandTotal)}</td></tr>
      </table>
    </body></html>`;
    const code = String(form.code || 'study').replace(/[^a-z0-9-]+/gi, '_');
    const url = URL.createObjectURL(new Blob(['﻿' + html], { type: 'application/vnd.ms-excel;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `Quotation_${code}.xls`; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
    onClose();
  };

  return (
    <Modal title="Export quotation" onClose={onClose}
      footer={(
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn" onClick={exportExcel}><Ico.dl /> Download Excel</button>
          <button className="btn primary" disabled={busy} onClick={() => void exportPdf()}><Ico.dl /> {busy ? 'Generating…' : 'Download PDF'}</button>
        </>
      )}>
      <div className="sub" style={{ marginBottom: 10 }}>Choose how much detail each section carries in the exported file. The default mirrors the review table: full detail, with Data Management as its 2.1 / 2.2 sub-totals.</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {quotation.sections.map((s: any) => {
          const subs = dmSubGroupsOf(s);
          const mode = modes[s.title] ?? (subs ? 'subgroups' : 'detail');
          const opts: Array<{ v: SectionExportMode; label: string }> = [
            { v: 'detail', label: `All items (${(s.items || []).length})` },
            ...(subs ? [{ v: 'subgroups' as SectionExportMode, label: 'Sub-groups (2.1 / 2.2)' }] : []),
            { v: 'summary', label: 'Section total only' },
          ];
          return (
            <div key={s.title} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600 }}>{s.title}</div>
                <div className="mono tabular" style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{fmt.thb(s.total)}</div>
              </div>
              <select value={mode} onChange={(e) => setModes((m) => ({ ...m, [s.title]: e.target.value as SectionExportMode }))} style={{ width: 'auto', fontSize: 12 }}>
                {opts.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

/** Read-only cost-formula breakdown (senior request): shows HOW each section
 *  total is derived so the PM can check/recheck the math. Deliberately NOT part
 *  of the exported PDF/Excel — purely an in-app verification aid. */
function CostFormulaModal({ quotation, form, onClose }: { quotation: any; form: any; onClose: () => void }) {
  const m = quotation?.meta || {};
  const thb = (n: number) => fmt.thb(Math.round(n || 0));
  const modeLabel = (SUBGROUP_COUNT_MODES.find((x) => x.value === (m.subgroupCountMode || 'catsMinus1')) || SUBGROUP_COUNT_MODES[1]).label;
  const Row = ({ label, value, strong }: { label: React.ReactNode; value: React.ReactNode; strong?: boolean }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0', fontWeight: strong ? 700 : 400 }}>
      <span style={{ color: strong ? 'var(--text)' : 'var(--text-2)' }}>{label}</span>
      <span className="tabular mono" style={{ whiteSpace: 'nowrap', color: strong ? 'var(--accent)' : 'var(--text)' }}>{value}</span>
    </div>
  );
  const Head = ({ children }: { children: React.ReactNode }) => (
    <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '14px 0 4px', borderBottom: '1px solid var(--border)', paddingBottom: 3 }}>{children}</div>
  );
  const reportFormula = m.reportMode === 'copy' ? '100% of analysis (mirror)'
    : m.reportMode === 'custom' ? `${m.reportPct}% of analysis (custom)`
    : '60% of analysis (separate)';
  return (
    <Modal title="Cost formula — how the quotation is calculated" onClose={onClose} wide>
      <div style={{ fontSize: 12, lineHeight: 1.55 }}>
        <div style={{ fontSize: 11, color: 'var(--text-3)', background: 'var(--surface-alt)', padding: '6px 10px', borderRadius: 6, marginBottom: 6 }}>
          For checking only — this breakdown is <strong>not</strong> included in the exported PDF. Tell us if any formula is wrong and we’ll adjust it.
        </div>

        <Head>1. Protocol &amp; Sample Size</Head>
        <Row label={`Protocol (${form.protocolLevel})`} value={thb(m.protoCost)} />

        <Head>2. Data Management</Head>
        <Row label={Array.isArray(form.dmItems) && form.dmItems.length ? 'Selected DM catalog items (Σ)' : `DMP + CRF (${m.crfCount} forms)`} value={thb(m.dmTotal)} />

        <Head>3. Data Analysis &amp; SAP</Head>
        <Row label={
          m.subgroupVarAdd > 0
            ? <>3.1 Descriptive — {m.baseVars} base {m.subgroupVarAdd > 0 ? <>+ {m.subgroupVarAdd} subgroup = <strong>{m.totalVars}</strong></> : null} variables → band</>
            : <>3.1 Descriptive — {m.totalVars} variables → band</>
        } value={thb(m.descCost)} />
        {m.subgroupVarAdd > 0 && (
          <div style={{ margin: '2px 0 6px 14px', padding: '6px 10px', background: 'var(--surface-alt)', borderRadius: 6, fontSize: 11 }}>
            <div style={{ color: 'var(--text-3)', marginBottom: 3 }}>Subgroup → variable rule: <strong>{modeLabel}</strong>. Reported as {m.baseVars}(+{m.subgroupVarAdd}).</div>
            {(m.subgroupDetail || []).map((d: any, i: number) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-2)' }}>
                <span>“{d.modifier}” on {d.outcome || 'outcome'} · {d.categories} categories</span>
                <span className="mono">+{d.add} var{d.add === 1 ? '' : 's'}</span>
              </div>
            ))}
          </div>
        )}
        <Row label={<>3.2 Objective — ({m.totalSimple}×{form.minutesPerSimple}m + {m.totalComplex}×{form.minutesPerComplex}m)/60 × ฿{Number(form.hourlyRate).toLocaleString()}/h × {m.complexMultiplier}</>} value={thb(m.objectiveCost)} />
        <Row label={<>3.3 Sample-size top-up — {m.topupPct}% × (desc + obj = {thb(m.topupBase)})</>} value={thb(m.topupCost)} />
        {m.sensitivityCount > 0 && <Row label={`Sensitivity analyses — ${m.sensitivityCount} check${m.sensitivityCount === 1 ? '' : 's'}`} value={thb(m.sensitivityCost)} />}
        {m.extraStudyCount > 0 && <Row label={`Study-level extra analyses — ${m.extraStudyCount} item${m.extraStudyCount === 1 ? '' : 's'}`} value={thb(m.extraStudyCost)} />}
        <Row label="= Analysis subtotal" value={thb(m.analysisTotal)} strong />

        <Head>4. Summary Report to Sponsor</Head>
        <Row label={reportFormula} value={thb(m.reportTotal)} />

        {(() => {
          // Reconcile the pure-formula rows above against the live grand total. They
          // differ only when a PM has MANUALLY overridden a line price in this step.
          const formulaGrand = (m.protoCost || 0) + (m.dmTotal || 0) + (m.analysisTotal || 0) + (m.reportTotal || 0);
          const delta = (quotation.grandTotal || 0) - formulaGrand;
          return (
            <div style={{ marginTop: 14, paddingTop: 8, borderTop: '2px solid var(--border)' }}>
              {delta !== 0 && <Row label="Manual price overrides (Δ)" value={`${delta > 0 ? '+' : ''}${thb(delta)}`} />}
              <Row label="Grand total" value={thb(quotation.grandTotal)} strong />
            </div>
          );
        })()}
      </div>
    </Modal>
  );
}

function StepReview({ form, set, quotation, currentUserId, onContinueToPhase2, canContinue }: any) {
  const D = useData();
  const versions = form.quotationVersions || [];
  const activeVer = versions.find((v: any) => v.id === form.activeQuotationVersionId);
  const nextVerNum = (versions.length || 0) + 1;
  const [changeNote, setChangeNote] = useState('');
  // Per-section expand/collapse for the quotation table. Every section starts
  // OPEN — for Data Management that means its 2.1 / 2.2 SUB-GROUP rows are
  // visible by default, while each sub-group's raw item list stays collapsed
  // (the two-level expand). Click headers to collapse / drill in.
  const [secOpen, setSecOpen] = useState<Record<string, boolean>>({});
  const isSecOpen = (s: any) => secOpen[s.title] ?? true;
  const toggleSec = (s: any) => setSecOpen((o) => ({ ...o, [s.title]: !isSecOpen(s) }));
  const [subOpen, setSubOpen] = useState<Record<string, boolean>>({});
  const isSubOpen = (k: string) => subOpen[k] ?? false;
  const toggleSub = (k: string) => setSubOpen((o) => ({ ...o, [k]: !isSubOpen(k) }));
  const dmSubGroups = dmSubGroupsOf;
  const [exportOpen, setExportOpen] = useState(false);
  // Cost-formula popup (senior request): a read-only breakdown of how every
  // section total is derived, for the PM to check/recheck. NOT in the PDF/export.
  const [formulaOpen, setFormulaOpen] = useState(false);

  // Manual override of item price / text (brief #2). `quoteOverrides` is a
  // map keyed by the item's display code; buildQuotation applies it, so edits
  // here flow into the live figures, the saved version and the Quotation page.
  const [editMode, setEditMode] = useState(false);
  const overrides: Record<string, { name?: string; total?: number }> = form.quoteOverrides || {};
  const overrideCount = Object.keys(overrides).length;
  const setOverride = (code: string, patch: { name?: string; total?: number }) =>
    set('quoteOverrides', { ...overrides, [code]: { ...overrides[code], ...patch } });
  const clearOverride = (code: string) => { const next = { ...overrides }; delete next[code]; set('quoteOverrides', next); };
  // A render FUNCTION (not a nested component) so the inputs keep focus across
  // re-renders — a `<Comp/>` defined in render would remount on every keystroke.
  const renderItemRow = (it: any, key: any, pad: number) => {
    const code: string | undefined = it.code;
    const isOv = !!(code && overrides[code]);
    if (!editMode) {
      return (
        <tr key={key}>
          <td style={{ paddingLeft: pad, fontSize: 12 }}>{code ? `${code} ` : ''}{it.name}{isOv && <span title="Manually overridden" style={{ marginLeft: 6, color: 'var(--accent)', fontSize: 10 }}>• edited</span>}</td>
          <td className="num tabular mono" style={{ fontSize: 12, color: isOv ? 'var(--accent)' : 'var(--text-2)' }}>{fmt.thb(it.total)}</td>
        </tr>
      );
    }
    return (
      <tr key={key}>
        <td style={{ paddingLeft: pad, fontSize: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {code && <span className="mono" style={{ color: 'var(--text-3)', fontSize: 11 }}>{code}</span>}
            <input value={it.name} disabled={!code} title={code ? 'Edit item text' : 'This line has no editable code'} onChange={(e) => code && setOverride(code, { name: e.target.value })} style={{ flex: 1, fontSize: 12, padding: '2px 6px' }} />
          </div>
        </td>
        <td className="num">
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
            <input type="number" className="tabular mono" value={it.total} disabled={!code} onChange={(e) => code && setOverride(code, { total: +e.target.value || 0 })} style={{ width: 96, textAlign: 'right', fontSize: 12, padding: '2px 6px' }} />
            {isOv && <button type="button" className="btn ghost icon sm" title="Reset to calculated value" onClick={() => code && clearOverride(code)}>↺</button>}
          </div>
        </td>
      </tr>
    );
  };

  const saveAsVersion = () => {
    const now = new Date().toISOString().slice(0, 16).replace('T', ' ');
    const designSnapshot = {
      code: form.code, title: form.title, shortTitle: form.shortTitle,
      design: form.design, area: form.area, sponsor: form.sponsor,
      pm: form.pm, piName: form.piName, coIsName: form.coIsName,
      startDate: form.startDate, endDate: form.endDate,
      fundingSource: form.fundingSource, ethics: form.ethics, ethicsRef: form.ethicsRef,
      registry: form.registry, redcap: form.redcap, members: form.members,
      arms: form.arms, intervention: form.intervention, comparator: form.comparator,
      population: form.population, armGroups: JSON.parse(JSON.stringify(form.armGroups || [])),
      randomise: form.randomise, blinding: form.blinding, sampleSize: form.sampleSize,
      recruitMonths: form.recruitMonths, treatmentMonths: form.treatmentMonths, followupMonths: form.followupMonths,
      protocolLevel: form.protocolLevel,
      dmItems: JSON.parse(JSON.stringify(form.dmItems || [])),
      visits: JSON.parse(JSON.stringify(form.visits)),
      labs: JSON.parse(JSON.stringify(form.labs)),
      labSelections: Object.fromEntries(Object.entries(form.labSelections || {}).map(([k, v]: any) => [k, [...v]])),
      nonLabSelected: [...(form.nonLabSelected || new Set())],
      nonLabAnalyzed: [...(form.nonLabAnalyzed || form.nonLabSelected || new Set())],
      outcomes: JSON.parse(JSON.stringify(form.outcomes)),
      minutesPerSimple: form.minutesPerSimple, minutesPerComplex: form.minutesPerComplex,
      hourlyRate: form.hourlyRate, complexMultiplier: form.complexMultiplier,
      summaryReportMode: form.summaryReportMode,
      // Pricing drivers that affect section 3/4 totals MUST round-trip on version
      // restore (else restoring a saved version silently re-prices it).
      summaryReportBasis: form.summaryReportBasis, summaryReportPct: form.summaryReportPct, summaryReportAmount: form.summaryReportAmount,
      subgroupCountMode: form.subgroupCountMode, sensitivityPrice: form.sensitivityPrice,
      extraAnalyses: JSON.parse(JSON.stringify(form.extraAnalyses || [])),
      quoteOverrides: JSON.parse(JSON.stringify(form.quoteOverrides || {})),
    };
    const newV = {
      id: 'qv' + Date.now(), label: `v${nextVerNum}.0`, status: 'Draft', createdAt: now, createdBy: currentUserId || 'u1',
      changeNote: changeNote || (versions.length === 0 ? 'Initial quotation' : 'Revised quotation'),
      grandTotal: quotation.grandTotal, meta: quotation.meta,
      snapshot: { protocolLevel: form.protocolLevel, sampleSize: form.sampleSize, arms: form.arms, visits: form.visits.length, outcomes: form.outcomes.length, labCount: form.labs.length, totalVars: quotation.meta.totalVars },
      sections: quotation.sections, designSnapshot,
    };
    const updated = [newV, ...versions.map((v: any) => ({ ...v, status: v.status === 'Active' ? 'Superseded' : v.status }))];
    set('quotationVersions', updated);
    set('activeQuotationVersionId', newV.id);
    setChangeNote('');
  };

  const restoreVersion = (v: any) => {
    // Migrate any legacy day/month/year durations in an older snapshot to weeks.
    const d = migrateDurationsToWeeks(v.designSnapshot);
    if (!d) return;
    Object.keys(d).forEach((k) => {
      if (k === 'labSelections') {
        const restored = Object.fromEntries(Object.entries(d[k]).map(([lid, arr]: any) => [lid, new Set(arr)]));
        set('labSelections', restored);
      } else if (k === 'nonLabSelected' || k === 'nonLabAnalyzed') {
        set(k, new Set(d[k]));
      } else {
        set(k, d[k]);
      }
    });
  };

  const markStatus = (vid: string, status: string) => set('quotationVersions', versions.map((v: any) => (v.id === vid ? { ...v, status } : v)));
  const selectActive = (vid: string) => set('activeQuotationVersionId', vid);
  const statusTone = (s: string): string => (({ Draft: 'slate', Sent: 'amber', 'Sent for consideration': 'amber', Accepted: 'green', Active: 'green', Superseded: 'slate', Rejected: 'red' } as any)[s] || 'slate');

  const toast = useToast();
  const [pdfBusyId, setPdfBusyId] = useState<string | null>(null);
  // Export a SAVED version's own stored sections (guarded on !!v.sections).
  const exportVersionPdf = async (v: any) => {
    if (!v?.sections) return;
    setPdfBusyId(v.id);
    try {
      const subtotal = Math.round((v.sections || []).reduce((s: number, sec: any) => s + (sec.total || 0), 0));
      const blob = await apiClient.quotations.pdf({
        studyCode: form.code || '',
        studyTitle: form.title || '',
        quotationNumber: v.label,
        version: `${v.label} (${v.status})`,
        dateText: v.createdAt,
        currency: 'THB',
        grandTotal: Math.round(v.grandTotal || 0),
        subtotal,
        sections: (v.sections || []).map((s: any) => ({
          title: s.title,
          total: Math.round(s.total || 0),
          items: (s.items || []).filter((it: any) => it.included !== false).map((it: any) => ({
            code: it.code, name: it.name, qty: it.qty, unit: it.unit,
            price: Math.round(it.price || 0), total: Math.round(it.total || 0),
          })),
        })),
      });
      const code = String(form.code || 'study').replace(/[^a-z0-9-]+/gi, '_');
      const label = String(v.label || 'v').replace(/[^a-z0-9-]+/gi, '_');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Quotation_${code}_${label}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e: any) {
      const m = String(e?.message || '');
      toast(/not installed|Unavailable|503|pdflatex/i.test(m) ? 'Server LaTeX is unavailable — cannot generate the PDF.' : 'Quotation PDF failed — please try again.');
    } finally {
      setPdfBusyId(null);
    }
  };

  return (
    <>
      <div className="sub" style={{ marginBottom: 12 }}>
        Review the auto-computed quotation. Save it as a numbered version, export the PDF to send to sponsor / PI for consideration. If changes are needed, go back, edit the design, then save another version — every revision is logged.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>
              Working draft → <span className="mono" style={{ color: 'var(--accent)' }}>v{nextVerNum}.0</span>
              {versions.length > 0 && <span style={{ color: 'var(--text-2)', fontWeight: 400 }}> · revision {nextVerNum} of this project</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button className="btn ghost sm" onClick={() => setFormulaOpen(true)} title="Show how each section total is calculated (for PM check — not included in the PDF)"><span style={{ fontWeight: 800, fontFamily: 'serif' }}>ƒ</span> Cost formula</button>
              {editMode && overrideCount > 0 && (
                <button className="btn ghost sm" title="Clear all manual overrides" onClick={() => set('quoteOverrides', {})}>Reset all ({overrideCount})</button>
              )}
              <button className="btn ghost sm" onClick={() => setEditMode((v) => !v)} title="Manually override item prices and text — overrides carry into the saved version and the Quotation page">
                <Ico.edit /> {editMode ? 'Done editing' : 'Override prices / text'}
              </button>
            </div>
          </div>
          <table>
            <thead><tr><th>Description</th><th className="num">Total (฿)</th></tr></thead>
            <tbody>
              {quotation.sections.map((s: any, si: number) => {
                const open = isSecOpen(s);
                const subs = dmSubGroups(s);
                return (
                  <React.Fragment key={si}>
                    <tr style={{ background: 'var(--surface-alt)', cursor: 'pointer' }} onClick={() => toggleSec(s)} title={open ? 'Collapse section' : 'Expand section'}>
                      <td style={{ fontWeight: 700 }}>
                        <span style={{ display: 'inline-block', width: 14, color: 'var(--text-3)' }}>{open ? '▾' : '▸'}</span>
                        {s.title}
                        {!open && <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 400, color: 'var(--text-3)' }}>{s.items.length} item{s.items.length === 1 ? '' : 's'} — click to expand</span>}
                      </td>
                      <td className="num tabular mono" style={{ fontWeight: 700 }}>{fmt.thb(s.total)}</td>
                    </tr>
                    {open && subs && subs.map((g) => {
                      const gKey = s.title + '::' + g.key;
                      const gOpen = isSubOpen(gKey);
                      const gTotal = g.items.reduce((sum: number, it: any) => sum + (it.total || 0), 0);
                      return (
                        <React.Fragment key={g.key}>
                          <tr style={{ cursor: 'pointer' }} onClick={() => toggleSub(gKey)} title={gOpen ? 'Collapse' : 'Expand items'}>
                            <td style={{ paddingLeft: 28, fontSize: 12, fontWeight: 600 }}>
                              <span style={{ display: 'inline-block', width: 14, color: 'var(--text-3)' }}>{gOpen ? '▾' : '▸'}</span>
                              {g.code} {g.label}
                              {!gOpen && <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 400, color: 'var(--text-3)' }}>{g.items.length} item{g.items.length === 1 ? '' : 's'}</span>}
                            </td>
                            <td className="num tabular mono" style={{ fontSize: 12, fontWeight: 600 }}>{fmt.thb(gTotal)}</td>
                          </tr>
                          {gOpen && g.items.map((it: any, ii: number) => renderItemRow(it, ii, 56))}
                        </React.Fragment>
                      );
                    })}
                    {open && !subs && s.items.map((it: any, ii: number) => renderItemRow(it, ii, 36))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          <div className="card" style={{ marginTop: 12 }}>
            <div className="card-h"><h3>Save this quotation as v{nextVerNum}.0</h3></div>
            <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="field">
                <label>What changed in this revision?</label>
                <textarea value={changeNote} onChange={(e) => setChangeNote(e.target.value)} placeholder={versions.length === 0 ? 'Initial quotation' : 'e.g. Sponsor requested sample size 200 → 240; added subgroup analysis'} />
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button className="btn primary" onClick={saveAsVersion}><Ico.check /> Save as v{nextVerNum}.0</button>
                <button className="btn" onClick={() => setExportOpen(true)}><Ico.dl /> Export PDF / Excel…</button>
              </div>
              {exportOpen && <ExportQuotationModal form={form} quotation={quotation} version={`v${nextVerNum}.0`} onClose={() => setExportOpen(false)} />}
              {formulaOpen && <CostFormulaModal quotation={quotation} form={form} onClose={() => setFormulaOpen(false)} />}
            </div>
          </div>
        </div>

        <div>
          <div className="card">
            <div className="card-h"><h3>Snapshot</h3></div>
            <div className="card-b">
              <dl className="kv">
                <dt>Project</dt><dd className="mono">{form.code}</dd>
                <dt>Design</dt><dd>{form.design}</dd>
                <dt>Protocol level</dt><dd>{form.protocolLevel}</dd>
                <dt>Sample size</dt><dd className="tabular">{form.sampleSize}</dd>
                <dt>Visits</dt><dd>{form.visits.length}</dd>
                <dt>Total variables</dt><dd className="tabular">{quotation.meta.totalVars}</dd>
                <dt>Outcomes</dt><dd className="tabular">{form.outcomes.length}</dd>
              </dl>
              <hr className="rule" />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontWeight: 600 }}>Grand total</span>
                <span className="mono tabular" style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)' }}>{fmt.thb(quotation.grandTotal)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-h">
          <div>
            <h3>Quotation version history</h3>
            <div className="sub">All saved versions. Tick the one to carry forward into the Acceptance step.</div>
          </div>
          <span className="chip" data-tone="slate"><span className="dot" />{versions.length} version{versions.length === 1 ? '' : 's'}</span>
        </div>
        <div className="card-b flush">
          {versions.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-2)', fontSize: 12.5 }}>
              No versions saved yet. Click "Save as v1.0" above to create the first version.
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ width: 30 }}>Active</th>
                  <th style={{ width: 70 }}>Version</th>
                  <th>Change note</th>
                  <th>Snapshot</th>
                  <th className="num">Grand total</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {versions.map((v: any) => {
                  const u = D.team.find((x) => x.id === v.createdBy);
                  const isActive = v.id === form.activeQuotationVersionId;
                  return (
                    <tr key={v.id} style={{ background: isActive ? 'rgba(74,138,85,0.05)' : undefined }}>
                      <td><input type="radio" checked={isActive} onChange={() => selectActive(v.id)} title="Use this version for acceptance" /></td>
                      <td className="mono" style={{ fontWeight: 600 }}>{v.label}</td>
                      <td style={{ fontSize: 12 }}>{v.changeNote}</td>
                      <td style={{ fontSize: 11, color: 'var(--text-2)' }}>
                        N={v.snapshot.sampleSize} · {v.snapshot.visits} visits · {v.snapshot.outcomes} outcomes · {v.snapshot.totalVars} vars
                      </td>
                      <td className="num tabular mono" style={{ fontWeight: 600 }}>{fmt.thb(v.grandTotal)}</td>
                      <td><span className="chip" data-tone={statusTone(v.status)}><span className="dot" />{v.status}</span></td>
                      <td style={{ fontSize: 11, color: 'var(--text-2)' }}>
                        <div className="mono">{v.createdAt}</div>
                        <div>{u?.name}</div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          {!!v.sections && <button className="btn ghost icon sm" title="Export PDF" disabled={pdfBusyId === v.id} onClick={() => void exportVersionPdf(v)}><Ico.dl /></button>}
                          {!isActive && !!v.designSnapshot && <button className="btn ghost sm" onClick={() => restoreVersion(v)} title="Restore this design — copies design back into the editor">↺ Restore</button>}
                          {v.status === 'Draft' && <button className="btn ghost sm" onClick={() => markStatus(v.id, 'Sent for consideration')}>Mark as sent</button>}
                          {v.status === 'Sent for consideration' && <button className="btn ghost sm" onClick={() => markStatus(v.id, 'Accepted')} style={{ color: '#2E6A3C' }}>Mark accepted</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div style={{ marginTop: 14, padding: 12, background: form.activeQuotationVersionId ? 'rgba(74,138,85,0.06)' : 'var(--surface-alt)', border: '1px solid ' + (form.activeQuotationVersionId ? '#4a8a55' : 'var(--border)'), borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, fontSize: 12, minWidth: 240 }}>
          {form.activeQuotationVersionId ? (
            canContinue ? (
              <>✓ <strong>{activeVer?.label}</strong> selected for acceptance — ready to proceed to Phase 2.</>
            ) : (
              <span style={{ color: 'var(--accent)' }}>
                ⚠ Before continuing: fill the required <strong>Basics</strong> fields (project title, code, design, project manager) — go back to step 1.
              </span>
            )
          ) : (
            <>⚠ Save at least one version and select it (radio) before continuing to acceptance.</>
          )}
        </div>
        {form.activeQuotationVersionId && (
          <button className="btn primary" disabled={!canContinue} onClick={onContinueToPhase2} title={!canContinue ? 'Fill required Basics fields first' : ''}>
            Continue to Phase 2 — Acceptance <Ico.arrowR />
          </button>
        )}
      </div>
    </>
  );
}

function StepAccept({ form, set, quotation }: any) {
  const D = useData();
  const toast = useToast();
  const ag = form.agreement;
  const updAg = (k: string, v: any) => set('agreement', { ...ag, [k]: v });
  const nowStamp = () => new Date().toISOString().slice(0, 16).replace('T', ' ');
  const toggleConfirm = (key: string, checked: boolean) => set('agreement', { ...ag, [key]: checked, [key + 'At']: checked ? nowStamp() : '' });
  const [emailOpen, setEmailOpen] = useState(false);
  // Sponsor acceptance needs verifiable evidence: an attached reply snapshot OR an explicit override reason.
  const sponsorEvidenceOk = !!ag.evidenceImage || !!(ag.sponsorOverrideReason && ag.sponsorOverrideReason.trim());
  const allConfirmed = ag.piConfirm && ag.pmConfirm && ag.sponsorConfirm && sponsorEvidenceOk;

  const activeVer = (form.quotationVersions || []).find((v: any) => v.id === form.activeQuotationVersionId);
  const shownTotal = activeVer?.grandTotal ?? quotation.grandTotal;
  const shownSections = activeVer?.sections ?? quotation.sections;
  const shownLabel = activeVer?.label ?? `v${(form.quotationVersions?.length || 0) + 1}.0`;

  const handleAccept = () => set('agreement', { ...ag, accepted: true, acceptedAt: nowStamp() });
  const handleRevoke = () => set('agreement', { ...ag, accepted: false, acceptedAt: '' });

  // --- Sponsor acceptance email template (generate + copy / mailto; no server send) ---
  const pmName = (D.team.find((u) => u.id === form.pm) || ({} as any)).name || 'HEROs Project Manager';
  const emailSubject = `[${form.code}] Quotation ${shownLabel} for your acceptance — ${form.shortTitle || form.title}`;
  const emailBody = [
    `Dear ${form.sponsor || 'Sponsor'},`,
    '',
    `Please find below a summary of our quotation ${shownLabel} for the study "${form.title}" (${form.code}).`,
    '',
    `• Study design: ${form.design}`,
    `• Sample size: ${form.sampleSize}`,
    `• Visits: ${form.visits.length}`,
    `• Variables analysed: ${quotation.meta.totalVars}`,
    `• Total quotation: ${fmt.thb(shownTotal)}`,
    '',
    'If the scope and pricing are acceptable, kindly reply to this email confirming your acceptance so we may proceed.',
    '',
    'Best regards,',
    pmName,
    'HEROs Clinical Trials Unit',
  ].join('\n');
  const mailtoHref = `mailto:${ag.sponsorEmail || ''}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
  const copyEmail = async () => {
    try { await navigator.clipboard.writeText(`Subject: ${emailSubject}\n\n${emailBody}`); toast('Acceptance email copied to clipboard'); }
    catch { toast('Could not copy — select the text manually'); }
  };

  // --- Acceptance evidence snapshot (held in the form; uploaded as a project document on activation) ---
  // The screenshot is COMPRESSED before it enters the form: a raw phone/desktop
  // screenshot is 1–5 MB, and as base64 it blew past the API body limit at
  // activation (HTTP 413 — "cannot activate"). ≤1400px JPEG keeps it readable
  // at a few hundred KB. Falls back to the raw file only if canvas fails.
  const compressEvidence = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const maxPx = 1400;
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Canvas unsupported'));
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read image')); };
    img.src = url;
  });
  const onEvidence = (file?: File) => {
    if (!file) return;
    compressEvidence(file)
      // Compression re-encodes as JPEG, so the stored name must say .jpg too —
      // the server rejects uploads whose bytes don't match their extension.
      .then((dataUrl) => set('agreement', { ...ag, evidenceImage: dataUrl, evidenceName: file.name.replace(/\.[^.]+$/, '') + '.jpg', evidenceAt: nowStamp() }))
      .catch(() => {
        // Raw fallback keeps the original bytes, so the original name is correct.
        const reader = new FileReader();
        reader.onload = () => set('agreement', { ...ag, evidenceImage: reader.result, evidenceName: file.name, evidenceAt: nowStamp() });
        reader.readAsDataURL(file);
      });
  };
  const removeEvidence = () => set('agreement', { ...ag, evidenceImage: '', evidenceName: '', evidenceAt: '' });

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>
      <div>
        <div className="sub" style={{ marginBottom: 10 }}>
          The project manager records the three confirmations below. Once all three are ticked, click <strong>Accept agreement</strong> to unlock Phase 2 timeline planning. Until then, the project sits in <strong>Quotation preparation</strong> status.
        </div>

        <div className="card">
          <div className="card-h"><h3>Confirmations</h3><span className="chip" data-tone={allConfirmed ? 'green' : 'amber'}><span className="dot" />{allConfirmed ? 'All confirmed' : 'Pending'}</span></div>
          <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ padding: 12, background: ag.sponsorConfirm ? 'rgba(74,138,85,0.06)' : 'var(--surface-alt)', borderRadius: 6, border: '1px solid ' + (ag.sponsorConfirm ? '#4a8a55' : 'var(--border)'), display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!ag.sponsorConfirm} onChange={(e) => toggleConfirm('sponsorConfirm', e.target.checked)} style={{ marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600 }}>Sponsor has accepted the quotation</div>
                  <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>{form.sponsor || 'Sponsor not specified'} — confirms scope &amp; pricing of {shownLabel}</div>
                  {ag.sponsorConfirm && ag.sponsorConfirmAt && <div className="mono" style={{ fontSize: 10.5, color: '#2E6A3C', marginTop: 2 }}>✓ Confirmed {ag.sponsorConfirmAt}</div>}
                </div>
                <button type="button" className="btn ghost xs" onClick={(e) => { e.preventDefault(); setEmailOpen((o) => !o); }}>✉ Email sponsor</button>
              </label>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                {ag.evidenceImage ? (
                  <>
                    <img src={ag.evidenceImage} alt="acceptance evidence" style={{ height: 44, borderRadius: 4, border: '1px solid var(--border)' }} />
                    <div style={{ fontSize: 10.5, color: 'var(--text-2)' }}>{ag.evidenceName || 'snapshot'}{ag.evidenceAt && <><br /><span className="mono">added {ag.evidenceAt}</span></>}</div>
                    <button type="button" className="btn ghost xs" onClick={removeEvidence}>Remove</button>
                  </>
                ) : (
                  <label className="btn ghost sm" style={{ cursor: 'pointer' }}>
                    📎 Attach sponsor's reply snapshot
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => onEvidence(e.target.files?.[0])} />
                  </label>
                )}
              </div>

              {!ag.evidenceImage && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>No screenshot? Record how acceptance was confirmed:</span>
                  <select value={ag.sponsorOverrideReason || ''} onChange={(e) => updAg('sponsorOverrideReason', e.target.value)} style={{ fontSize: 11 }}>
                    <option value="">— select —</option>
                    <option value="Confirmed verbally (phone / meeting)">Confirmed verbally (phone / meeting)</option>
                    <option value="Signed paper agreement on file">Signed paper agreement on file</option>
                    <option value="Confirmed by email (no screenshot attached)">Confirmed by email (no screenshot)</option>
                  </select>
                </div>
              )}

              {ag.sponsorConfirm && !sponsorEvidenceOk && (
                <div style={{ fontSize: 10.5, color: 'var(--accent)', fontWeight: 600 }}>⚠ Attach the sponsor's reply snapshot or record an override reason before you can accept.</div>
              )}

              {emailOpen && (
                <div style={{ padding: 10, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Acceptance request email (edit your client after sending)</div>
                  <input value={emailSubject} readOnly className="mono" style={{ width: '100%', fontSize: 10.5, marginBottom: 6 }} />
                  <textarea value={emailBody} readOnly rows={9} style={{ width: '100%', fontSize: 10.5 }} />
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    <button type="button" className="btn sm" onClick={copyEmail}>Copy</button>
                    <a className="btn primary sm" href={mailtoHref}>Open in mail client</a>
                    <button type="button" className="btn ghost sm" style={{ marginLeft: 'auto' }} onClick={() => setEmailOpen(false)}>Close</button>
                  </div>
                </div>
              )}
            </div>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 12, background: ag.piConfirm ? 'rgba(74,138,85,0.06)' : 'var(--surface-alt)', borderRadius: 6, cursor: 'pointer', border: '1px solid ' + (ag.piConfirm ? '#4a8a55' : 'var(--border)') }}>
              <input type="checkbox" checked={!!ag.piConfirm} onChange={(e) => toggleConfirm('piConfirm', e.target.checked)} style={{ marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600 }}>Principal Investigator confirmed</div>
                <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>{form.piName || 'PI name not set'} — confirms scope &amp; design</div>
                {ag.piConfirm && ag.piConfirmAt && <div className="mono" style={{ fontSize: 10.5, color: '#2E6A3C', marginTop: 2 }}>✓ Confirmed {ag.piConfirmAt}</div>}
              </div>
            </label>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 12, background: ag.pmConfirm ? 'rgba(74,138,85,0.06)' : 'var(--surface-alt)', borderRadius: 6, cursor: 'pointer', border: '1px solid ' + (ag.pmConfirm ? '#4a8a55' : 'var(--border)') }}>
              <input type="checkbox" checked={!!ag.pmConfirm} onChange={(e) => toggleConfirm('pmConfirm', e.target.checked)} style={{ marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600 }}>Project Manager confirmed</div>
                <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>{(D.team.find((u) => u.id === form.pm) || ({} as any)).name} — commits HEROs team to deliver</div>
                {ag.pmConfirm && ag.pmConfirmAt && <div className="mono" style={{ fontSize: 10.5, color: '#2E6A3C', marginTop: 2 }}>✓ Confirmed {ag.pmConfirmAt}</div>}
              </div>
            </label>
            <div className="field">
              <label>Revision note (optional)</label>
              <textarea value={ag.revisionNote || ''} onChange={(e) => updAg('revisionNote', e.target.value)} placeholder="Capture any sponsor-requested adjustments or special conditions" />
            </div>
            <div style={{ fontSize: 10.5, color: 'var(--text-3)', padding: '4px 2px' }}>
              ℹ All three boxes are ticked by the Project Manager once each party has confirmed (verbally, by email, or via signed paper).
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 14, marginTop: 12, borderRadius: 8, background: ag.accepted ? 'rgba(74,138,85,0.08)' : 'var(--surface-alt)', border: '1px solid ' + (ag.accepted ? '#4a8a55' : 'var(--border)') }}>
          {ag.accepted ? (
            <>
              <div style={{ fontSize: 22 }}>✓</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#2E6A3C' }}>Agreement accepted</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-2)' }} className="mono">Recorded {ag.acceptedAt}</div>
              </div>
              <button className="btn ghost sm" style={{ color: 'var(--accent)' }} onClick={handleRevoke}>Revoke</button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 22, color: 'var(--text-3)' }}>○</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>Not yet accepted</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-2)' }}>Tick all three confirmations — the sponsor box needs a reply snapshot or an override reason — to enable acceptance.</div>
              </div>
              <button className="btn primary" disabled={!allConfirmed} onClick={handleAccept}><Ico.check /> Accept agreement</button>
            </>
          )}
        </div>
      </div>

      <div>
        <div className="card">
          <div className="card-h"><h3>Quotation summary</h3><span className="chip" data-tone={ag.accepted ? 'green' : 'amber'}><span className="dot" />{shownLabel}{ag.accepted ? ' · Accepted' : ''}</span></div>
          <div className="card-b">
            <dl className="kv">
              <dt>Project</dt><dd className="mono">{form.code}</dd>
              <dt>Sponsor</dt><dd>{form.sponsor || '—'}</dd>
              <dt>Design</dt><dd>{form.design}</dd>
              <dt>Sample size</dt><dd className="tabular">{form.sampleSize}</dd>
              <dt>First encounter → expected end</dt><dd>{form.startDate} → {form.endDate}</dd>
            </dl>
            <hr className="rule" />
            {shownSections ? shownSections.map((s: any, si: number) => (
              <div key={si} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, padding: '3px 0' }}>
                <span style={{ color: 'var(--text-2)' }}>{s.title.replace(/^\d+\.\s*/, '')}</span>
                <span className="mono tabular">{fmt.thb(s.total)}</span>
              </div>
            )) : (
              <div style={{ fontSize: 11, color: 'var(--text-2)' }}>Active quotation snapshot — {shownLabel}</div>
            )}
            <hr className="rule" />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontWeight: 600 }}>Grand total</span>
              <span className="mono tabular" style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy)' }}>{fmt.thb(shownTotal)}</span>
            </div>
            {activeVer && (
              <div style={{ marginTop: 8, padding: 8, background: 'var(--surface-alt)', borderRadius: 4, fontSize: 10.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
                Saved {activeVer.createdAt} · {(D.team.find((u) => u.id === activeVer.createdBy) || ({} as any)).name}<br />
                <em>"{activeVer.changeNote}"</em>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const TIMELINE_COLS_KEY = 'heros.timelineColWidths';
const DEFAULT_TL_COLS = { phase: 170, type: 104, category: 130, start: 120, weeks: 64, end: 120, status: 96 };
const TL_COL_MIN = { phase: 110, type: 92, category: 100, start: 100, weeks: 52, end: 100, status: 80 } as const;

function StepTimeline({ form, set, currentProjectId }: any) {
  const D = useData();
  const items = form.timelineItems;
  const { cols, activeCol, startResize, resetCol } = useColWidths(TIMELINE_COLS_KEY, DEFAULT_TL_COLS, TL_COL_MIN);

  const isMilestone = (it: any) => it.rowType === 'milestone';
  const isPhase = (it: any) => it.rowType === 'phase';
  const normalize = (it: any) => (isMilestone(it) || isPhase(it) || it.splits ? it : { ...it, splits: [{ user: it.user || form.pm, pct: 100 }] });

  // Row reorder (▲▼ — persisted as array order), duplicate, and Task/Milestone type.
  const moveItem = (idx: number, dir: number) => {
    const arr = [...items]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]]; set('timelineItems', arr);
  };
  const dupItem = (it: any) => {
    const i = items.findIndex((x: any) => x.id === it.id);
    const copy = { ...normalize(it), id: 'tb' + Date.now(), name: (it.name || 'Phase') + ' (copy)' };
    const arr = [...items]; arr.splice(i + 1, 0, copy); set('timelineItems', arr);
  };
  const setRowType = (id: string, rowType: string) =>
    set('timelineItems', items.map((x: any) => (x.id === id ? { ...x, rowType, ...(rowType === 'task' ? { splits: x.splits || [{ user: x.user || form.pm, pct: 100 }] } : { splits: [], user: '' }) } : x)));

  // Drag-to-reorder via the shared useRowDrag hook (same robust behaviour as Labs /
  // Outcomes). The ▲▼ buttons remain as a keyboard/click fallback.
  const { rowProps: tlRowProps, gripProps: tlGripProps } = useRowDrag(items, (next: any[]) => set('timelineItems', next));

  const upd = (id: string, k: string, v: any) =>
    set('timelineItems', (() => {
      const idx = items.findIndex((x: any) => x.id === id);
      if (idx < 0) return items;
      const row: any = { ...normalize(items[idx]), [k]: v };
      // Duration drives the End date; moving the Start KEEPS the duration and
      // shifts the End along (less clicking); editing End recomputes duration.
      if (k === 'startDate') {
        const weeks = Math.max(1, row.durationWeeks || weeksBetween(items[idx].startDate, items[idx].endDate) || 1);
        row.durationWeeks = weeks;
        row.endDate = addDaysISO(v, weeks * 7);
        row.start = monthIdxOf(v);
        row.end = monthIdxOf(row.endDate);
      } else if (k === 'endDate') {
        // Reject an end typed BEFORE the start (the picker blocks it but the
        // keyboard doesn't) — otherwise the row stores inverted dates while
        // the duration silently clamps to 1.
        if (/^\d{4}-\d{2}-\d{2}/.test(v) && new Date(v) < new Date(row.startDate)) return items;
        row.end = monthIdxOf(v);
        row.durationWeeks = weeksBetween(row.startDate, v);
      } else if (k === 'durationWeeks') {
        const weeks = Math.max(1, Math.round(+v) || 1);
        row.durationWeeks = weeks;
        row.endDate = addDaysISO(row.startDate, weeks * 7);
        row.end = monthIdxOf(row.endDate);
      }
      const next = items.slice();
      next[idx] = row;
      // Auto-chain (senior request): a date/duration edit shifts every DOWNSTREAM
      // row so each starts where the previous ends, preserving its own duration.
      // (The Re-sequence button still rebuilds the WHOLE list from the start date.)
      if (k === 'startDate' || k === 'endDate' || k === 'durationWeeks') {
        let cursor = row.endDate;
        for (let i = idx + 1; i < next.length; i++) {
          const it: any = { ...normalize(next[i]) };
          // A point milestone / phase divider keeps its 0-week duration (no 1-week inflation).
          const weeks = (it.rowType === 'milestone' || it.rowType === 'phase')
            ? (it.durationWeeks ?? 0)
            : Math.max(1, it.durationWeeks || weeksBetween(it.startDate, it.endDate) || 1);
          it.startDate = cursor;
          it.endDate = addDaysISO(cursor, weeks * 7);
          it.durationWeeks = weeks;
          it.start = monthIdxOf(cursor);
          it.end = monthIdxOf(it.endDate);
          cursor = it.endDate;
          next[i] = it;
        }
      }
      return next;
    })());

  // Auto-sequence (brief Feature 8): re-chain every row in its current order so each
  // task starts where the previous ends (start = prev end; end = start + durationWeeks).
  // Changing a duration (by editing a row's End date) then re-sequencing shifts downstream.
  const resequence = () => {
    let cursor = validStartISO(form.startDate);
    set('timelineItems', items.map((it: any) => {
      // Milestone / phase-divider rows are 0-week points — keep them at the cursor
      // (no 1-week inflation, no downstream drift), matching the upd auto-chain.
      const weeks = (it.rowType === 'milestone' || it.rowType === 'phase')
        ? (it.durationWeeks ?? 0)
        : Math.max(1, it.durationWeeks || weeksBetween(it.startDate, it.endDate) || 1);
      const sDate = cursor;
      const eDate = addDaysISO(sDate, weeks * 7);
      cursor = eDate;
      return { ...it, startDate: sDate, endDate: eDate, start: monthIdxOf(sDate), end: monthIdxOf(eDate), durationWeeks: weeks };
    }));
  };

  const updSplit = (id: string, splitIdx: number, key: string, value: any) =>
    set('timelineItems', items.map((x: any) => {
      if (x.id !== id) return x;
      const cur = normalize(x);
      const splits = cur.splits.map((s: any, i: number) => (i === splitIdx ? { ...s, [key]: value } : s));
      return { ...cur, splits, user: splits[0]?.user || cur.user };
    }));

  const addSplit = (id: string) =>
    set('timelineItems', items.map((x: any) => {
      if (x.id !== id) return x;
      const cur = normalize(x);
      const usedIds = cur.splits.map((s: any) => s.user);
      const nextUser = D.team.find((u) => !usedIds.includes(u.id))?.id || D.team[0].id;
      return { ...cur, splits: [...cur.splits, { user: nextUser, pct: 0 }] };
    }));

  const rmSplit = (id: string, splitIdx: number) =>
    set('timelineItems', items.map((x: any) => {
      if (x.id !== id) return x;
      const cur = normalize(x);
      if (cur.splits.length <= 1) return cur;
      const splits = cur.splits.filter((_: any, i: number) => i !== splitIdx);
      return { ...cur, splits, user: splits[0]?.user || cur.user };
    }));

  const distributeEvenly = (id: string) =>
    set('timelineItems', items.map((x: any) => {
      if (x.id !== id) return x;
      const cur = normalize(x);
      const n = cur.splits.length;
      const each = Math.floor(100 / n);
      const rem = 100 - each * n;
      const splits = cur.splits.map((s: any, i: number) => ({ ...s, pct: each + (i === 0 ? rem : 0) }));
      return { ...cur, splits };
    }));

  const add = () => {
    // Suggest start = where the LAST phase ends (chained), default 2 weeks long
    // — the user only adjusts what differs instead of clicking every date.
    const last = items[items.length - 1];
    const sDate = validStartISO(last?.endDate || form.startDate);
    const weeks = 2;
    const eDate = addDaysISO(sDate, weeks * 7);
    set('timelineItems', [...items, {
      id: 'tb' + Date.now(), name: 'New phase', category: 'Admin', splits: [{ user: form.pm, pct: 100 }], user: form.pm,
      durationWeeks: weeks,
      startDate: sDate, endDate: eDate, start: monthIdxOf(sDate), end: monthIdxOf(eDate), status: 'planned',
    }]);
  };
  const remove = (id: string) => set('timelineItems', items.filter((x: any) => x.id !== id));
  const regen = () => set('timelineItems', generateDefaultTimeline(form, D));

  const counts: Record<string, any> = {};
  items.forEach((it: any) => {
    if (isMilestone(it) || isPhase(it)) return; // timeline-only rows carry no responsible/compensation
    const splits = normalize(it).splits;
    splits.forEach((s: any) => {
      if (!counts[s.user]) counts[s.user] = { tasks: 0, weighted: 0 };
      counts[s.user].tasks += 1;
      counts[s.user].weighted += s.pct / 100;
    });
  });

  // --- Burden engine (peak concurrent load per member across a phase's month window) ---
  // Combines every OTHER live project (D.timeline) with this draft's own phases, so the
  // PM is warned about double-booking they create within this very project too.
  const projectsById = useMemo(() => Object.fromEntries(D.projects.map((p: any) => [p.id, p])), [D.projects]);
  const [burdenPop, setBurdenPop] = useState<string | null>(null); // `${itemId}:${splitIdx}`
  const bLabel = (c: number) => (!c ? 'Free' : c <= 2 ? 'Light' : c <= 4 ? 'Moderate' : 'Heavy');
  const bTone = (c: number) => (c >= 5
    ? { bg: 'rgba(158,43,37,0.12)', fg: '#9E2B25', bd: '#9E2B25' }
    : c >= 3 ? { bg: 'rgba(194,138,30,0.14)', fg: '#8a5d12', bd: '#caa24a' }
    : c >= 1 ? { bg: 'var(--surface-alt)', fg: 'var(--text-2)', bd: 'var(--border)' }
    : { bg: 'rgba(74,138,85,0.12)', fg: '#2E6A3C', bd: '#9fc7a8' });

  const memberBurden = (userId: string, s: number, e: number) => {
    const lo = Math.max(0, Math.min(s | 0, e | 0));
    const hi = Math.min(11, Math.max(s | 0, e | 0));
    const perMonth: { m: number; tasks: any[] }[] = [];
    let peak = 0;
    for (let m = lo; m <= hi; m++) {
      const tasks: any[] = [];
      D.timeline.forEach((t: any) => {
        // Skip THIS project's own saved rows — when re-editing an already-live
        // project they're also present in D.timeline, and the draft `items`
        // loop below re-adds them as "(this project)". Counting both ghosts
        // every task twice and inflates the burden peak. The draft is the
        // authoritative copy here, so the saved copy is dropped.
        if (currentProjectId && t.project === currentProjectId) return;
        if (t.user === userId && t.start <= m && m <= t.end) tasks.push({ name: t.name, code: projectsById[t.project]?.code || t.project, draft: false });
      });
      items.forEach((it: any) => {
        if (isMilestone(it) || isPhase(it)) return;
        const n = normalize(it);
        if (n.splits.some((sp: any) => sp.user === userId) && n.start <= m && m <= n.end) tasks.push({ name: n.name || 'Untitled phase', code: form.code, draft: true });
      });
      if (tasks.length > peak) peak = tasks.length;
      perMonth.push({ m, tasks });
    }
    return { peak, perMonth };
  };

  const lighterSuggestions = (s: number, e: number, excludeIds: string[]) =>
    D.team
      .filter((u: any) => !excludeIds.includes(u.id))
      .map((u: any) => ({ u, peak: memberBurden(u.id, s, e).peak }))
      .filter((x: any) => x.peak <= 2)
      .sort((a: any, b: any) => a.peak - b.peak)
      .slice(0, 3);

  return (
    <>
      <div className="sub" style={{ marginBottom: 10 }}>
        With the sponsor's agreement in hand, lay out the monthly phases that will run this project. <strong>Each phase can be split across multiple team members</strong> with % allocation — these sync into the compensation engine. Phases appear on the <strong>Monthly Team Dashboard</strong> immediately after you finalise.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 12 }}>
        <div className="stat"><div className="label">Phases</div><div className="v tabular">{items.length}</div></div>
        <div className="stat"><div className="label">Team members involved</div><div className="v tabular">{Object.keys(counts).length}</div></div>
        <div className="stat"><div className="label">Span</div><div className="v" style={{ fontSize: 14 }}>{form.startDate} → {form.endDate}</div></div>
        <div className="stat" style={{ borderColor: 'var(--accent)' }}>
          <div className="label">Heaviest contributor</div>
          <div className="v" style={{ fontSize: 14 }}>{(() => {
            const top = Object.entries(counts).sort((a: any, b: any) => b[1].weighted - a[1].weighted)[0];
            if (!top) return '—';
            const u = D.team.find((x) => x.id === top[0]);
            return `${u?.name || '—'} (${(top[1] as any).weighted.toFixed(1)} FTE-phase)`;
          })()}</div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn sm" onClick={regen} title="Rebuild the 13-step clinical-research workflow from the design durations">↻ Regenerate (13-step order)</button>
          <button className="btn sm" onClick={resequence} title="Re-chain dates in row order: each task starts when the previous ends. Edit a row's End date to change its duration, then re-sequence to shift everything downstream.">⇄ Re-sequence dates</button>
          <button className="btn primary sm" onClick={add}><Ico.plus /> Add phase</button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-2)' }}>{items.length} phase{items.length === 1 ? '' : 's'} · weeks-only · drag ⠿ to reorder rows</div>
      </div>

      {/* The table is wider than the card on purpose — scroll ←→ instead of
          squeezing the inputs; the Responsible column keeps a workable width. */}
      <div className="scroll-x" style={{ overflowX: 'auto' }}>
        <table style={{ minWidth: 1280, tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <col style={{ width: cols.phase }} />
            <col style={{ width: cols.type }} />
            <col style={{ width: cols.category }} />
            <col style={{ width: cols.start }} />
            <col style={{ width: cols.weeks }} />
            <col style={{ width: cols.end }} />
            <col />
            <col style={{ width: cols.status }} />
            <col style={{ width: 96 }} />
          </colgroup>
          <thead>
            <tr>
              <ResizableTh label="Phase" active={activeCol === 'phase'} onMouseDown={startResize('phase')} onDoubleClick={resetCol('phase')} />
              <ResizableTh label="Type" active={activeCol === 'type'} onMouseDown={startResize('type')} onDoubleClick={resetCol('type')} />
              <ResizableTh label="Category" active={activeCol === 'category'} onMouseDown={startResize('category')} onDoubleClick={resetCol('category')} />
              <ResizableTh label="Start" active={activeCol === 'start'} onMouseDown={startResize('start')} onDoubleClick={resetCol('start')} />
              <ResizableTh label="Wk" active={activeCol === 'weeks'} onMouseDown={startResize('weeks')} onDoubleClick={resetCol('weeks')} />
              <ResizableTh label="End" active={activeCol === 'end'} onMouseDown={startResize('end')} onDoubleClick={resetCol('end')} />
              <th>Responsible (split %)</th>
              <ResizableTh label="Status" active={activeCol === 'status'} onMouseDown={startResize('status')} onDoubleClick={resetCol('status')} />
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((rawIt: any, idx: number) => {
              const it = normalize(rawIt);
              const milestone = isMilestone(it);
              const sumPct = milestone ? 100 : it.splits.reduce((s: number, sp: any) => s + (+sp.pct || 0), 0);
              const valid = sumPct === 100;
              return (
                <tr key={it.id} {...tlRowProps(idx)}>
                  <td><input value={it.name} onChange={(e) => upd(it.id, 'name', e.target.value)} /></td>
                  <td>
                    <select value={it.rowType || 'task'} onChange={(e) => setRowType(it.id, e.target.value)} title="Task rows need a responsible person; Milestone & Phase-divider rows are timeline-only">
                      <option value="task">Task</option>
                      <option value="milestone">Milestone</option>
                      <option value="phase">Phase divider</option>
                    </select>
                  </td>
                  {isPhase(it) ? (
                    <td colSpan={6} style={{ background: 'var(--surface-alt)', color: 'var(--text-3)', fontSize: 11, fontStyle: 'italic' }}>
                      Phase divider — groups the tasks below (no owner, dates or compensation)
                    </td>
                  ) : (<>
                  <td>
                    <select value={it.category} onChange={(e) => upd(it.id, 'category', e.target.value)}>
                      {Object.keys(D.categories).map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </td>
                  <td><input type="date" value={it.startDate} onChange={(e) => upd(it.id, 'startDate', e.target.value)} title="Moving the start keeps the duration and shifts the end" /></td>
                  <td><input type="number" min="1" className="tabular mono" style={{ textAlign: 'right' }} value={it.durationWeeks ?? weeksBetween(it.startDate, it.endDate)} onChange={(e) => upd(it.id, 'durationWeeks', e.target.value)} title="Duration in weeks — the end date follows automatically" /></td>
                  <td><input type="date" value={it.endDate} min={it.startDate} onChange={(e) => upd(it.id, 'endDate', e.target.value)} /></td>
                  <td>
                    {milestone ? (
                      <span style={{ fontSize: 11, color: 'var(--text-3)' }}>— timeline only (no responsible person)</span>
                    ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {it.splits.map((sp: any, si: number) => {
                        const u = D.team.find((x) => x.id === sp.user);
                        const b = memberBurden(sp.user, it.start, it.end);
                        const tone = bTone(b.peak);
                        const popKey = it.id + ':' + si;
                        const open = burdenPop === popKey;
                        return (
                          <div key={si} style={{ position: 'relative', display: 'grid', gridTemplateColumns: '22px 1fr 58px auto 22px', gap: 6, alignItems: 'center' }}>
                            <button type="button" onClick={() => setBurdenPop(open ? null : popKey)} title="Show this member's workload for this phase" style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}><Avatar user={u} size="sm" /></button>
                            <select value={sp.user} onChange={(e) => updSplit(it.id, si, 'user', e.target.value)} style={{ padding: '2px 4px', fontSize: 11.5 }}>
                              {D.team.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <input type="number" min="0" max="100" value={sp.pct} className="tabular mono" style={{ textAlign: 'right', padding: '2px 4px', fontSize: 11.5, width: 40 }} onChange={(e) => updSplit(it.id, si, 'pct', +e.target.value || 0)} />
                              <span style={{ fontSize: 11, color: 'var(--text-3)' }}>%</span>
                            </div>
                            <button type="button" onClick={() => setBurdenPop(open ? null : popKey)} title="Workload in this phase window — click for detail" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 9.5, fontWeight: 700, padding: '2px 6px', borderRadius: 10, background: tone.bg, color: tone.fg, border: '1px solid ' + tone.bd, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              {b.peak >= 5 && <span>⚠</span>}{bLabel(b.peak)} · {b.peak}
                            </button>
                            <button className="btn ghost icon sm" onClick={() => rmSplit(it.id, si)} disabled={it.splits.length <= 1} title="Remove person"><Ico.close /></button>
                            {open && (
                              <div style={{ position: 'absolute', zIndex: 40, top: '100%', left: 0, marginTop: 4, width: 300, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 10px 30px rgba(0,0,0,0.20)', padding: 10 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                  <strong style={{ fontSize: 12 }}>{u?.name || '—'} · {bLabel(b.peak)} ({b.peak} max)</strong>
                                  <button className="btn ghost icon sm" onClick={() => setBurdenPop(null)}><Ico.close /></button>
                                </div>
                                {b.peak >= 5 && <div style={{ fontSize: 11, color: '#9E2B25', marginBottom: 6 }}>⚠ Overloaded during this phase — consider a lighter teammate below.</div>}
                                <div style={{ maxHeight: 150, overflowY: 'auto' }}>
                                  {b.perMonth.map((pm: any) => (
                                    <div key={pm.m} style={{ marginBottom: 4 }}>
                                      <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-2)' }}>{D.months2026[pm.m]} · {pm.tasks.length} task{pm.tasks.length === 1 ? '' : 's'}</div>
                                      {pm.tasks.length === 0
                                        ? <div style={{ fontSize: 10.5, color: 'var(--text-3)', paddingLeft: 8 }}>• free</div>
                                        : pm.tasks.map((t: any, ti: number) => <div key={ti} style={{ fontSize: 10.5, color: 'var(--text-3)', paddingLeft: 8 }}>• {t.code} — {t.name}{t.draft ? ' (this project)' : ''}</div>)}
                                    </div>
                                  ))}
                                </div>
                                {b.peak >= 3 && (() => {
                                  const sugg = lighterSuggestions(it.start, it.end, it.splits.map((x: any) => x.user));
                                  return sugg.length ? (
                                    <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border)' }}>
                                      <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-2)', marginBottom: 4 }}>Lighter this window — click to reassign:</div>
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                        {sugg.map(({ u: su, peak }: any) => (
                                          <button key={su.id} className="btn ghost xs" onClick={() => { updSplit(it.id, si, 'user', su.id); setBurdenPop(null); }}>{su.name} · {bLabel(peak)}</button>
                                        ))}
                                      </div>
                                    </div>
                                  ) : null;
                                })()}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {it.splits.length === 0 && (
                        <span style={{ fontSize: 10.5, color: 'var(--text-3)', fontStyle: 'italic' }}>⚠ Unassigned — add the responsible person</span>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                        <button className="btn ghost xs" onClick={() => addSplit(it.id)} disabled={it.splits.length >= D.team.length}><Ico.plus /> Add</button>
                        {it.splits.length > 1 && <button className="btn ghost xs" onClick={() => distributeEvenly(it.id)}>Even split</button>}
                        <span style={{ marginLeft: 'auto', fontSize: 10.5, color: valid ? 'var(--text-3)' : 'var(--accent)', fontWeight: valid ? 400 : 600 }}>
                          {valid ? <>✓ 100%</> : <>⚠ {sumPct}%</>}
                        </span>
                      </div>
                    </div>
                    )}
                  </td>
                  <td>
                    <select value={it.status} onChange={(e) => upd(it.id, 'status', e.target.value)}>
                      {Object.keys(STATUS_META).map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </td>
                  </>)}
                  <td>
                    <div style={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <span {...tlGripProps(idx)}>⠿</span>
                      <button className="btn ghost icon sm" title="Move up" disabled={idx === 0} onClick={() => moveItem(idx, -1)}>▲</button>
                      <button className="btn ghost icon sm" title="Move down" disabled={idx === items.length - 1} onClick={() => moveItem(idx, 1)}>▼</button>
                      <button className="btn ghost icon sm" title="Duplicate row" onClick={() => dupItem(rawIt)}>⧉</button>
                      <button className="btn ghost icon sm" title="Delete row" onClick={() => remove(it.id)}><Ico.trash /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 14, padding: 12, background: 'rgba(74,138,85,0.06)', border: '1px solid #4a8a55', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 18 }}>✓</div>
        <div style={{ flex: 1, fontSize: 12 }}>Ready to finalise. Click <strong>Finalise &amp; save</strong> in the header to add this project to the workspace.</div>
      </div>
    </>
  );
}

function StepStudyFlow({ form, set }: any) {
  const toast = useToast();
  // Editable CONSORT box labels (senior request): persisted in form.flowBoxOverrides,
  // they drive BOTH this on-page diagram and the HTML/Word/PDF exports.
  const setBox = (id: string, v: string) => set('flowBoxOverrides', { ...(form.flowBoxOverrides || {}), [id]: v });
  const BOX_LABEL_FIELDS: { id: string; label: string; def: string }[] = [
    { id: 'screened', label: 'Top box (eligibility)', def: 'Assessed for eligibility' },
    { id: 'excluded', label: 'Excluded box', def: 'Excluded' },
    { id: 'enrolled', label: form.design?.toLowerCase().includes('cohort') ? 'Enrolled box' : 'Randomised box', def: form.design?.toLowerCase().includes('cohort') ? 'Enrolled' : 'Randomised' },
    { id: 'analysed', label: 'Analysis box', def: 'Analysed for primary outcome' },
  ];
  const [pdfBusy, setPdfBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  // The on-screen preview is the same flow content rendered as HTML (no server
  // round-trip), so the PI sees exactly what they're about to download.
  const previewHtml = useMemo(() => (previewOpen ? consortFlowHtml(form) : ''), [previewOpen, form]);
  const makePdf = async () => {
    setPdfBusy(true);
    try { await downloadConsortPdf(form); setPreviewOpen(false); }
    catch (e: any) {
      const m = String(e?.message || '');
      toast(/not installed|Unavailable|503/i.test(m) ? 'Server LaTeX is unavailable — use the Word .docx or HTML export instead.' : 'CONSORT PDF failed — try the Word .docx or HTML export.');
    } finally { setPdfBusy(false); }
  };
  const isCohort = form.design.toLowerCase().includes('cohort');
  const N = form.sampleSize || 0;
  const arms = Math.max(1, isCohort ? 1 : (form.arms || 2));
  const { screened, excluded } = consortCounts(form);
  const perArm = Math.floor(N / arms);

  const armLabels = isCohort
    ? ['Cohort']
    : synthArmGroups(form).map((a: any, i: number) => a.label || `Arm ${i + 1}`);

  const flowVisits = form.visits.filter((v: any) => visitTypeList(v).some((t: string) => t !== 'Screening' && t !== 'Randomisation')).sort((a: any, b: any) => (a.week ?? 0) - (b.week ?? 0));

  // Planning figures: every arm carries its target N at every visit. We do NOT
  // invent attrition — the old `perArm - visitIdx - armIdx` formula fabricated
  // "withdrawn/lost" numbers out of nowhere. Real dropouts are recorded against
  // actuals as the trial runs (the CONSORT export shows "(n = 0) — replace").
  const armN = (_armIdx: number, _visitIdx: number) => perArm;

  const itemsAtVisit = (visitId: string) => {
    const labs = (form.labs || []).filter((lab: any) => !lab.isCompensation && (form.labSelections[lab.id] || new Set()).has(visitId));
    return labs.map((l: any) => l.name);
  };

  const armTones = ['blue', 'amber', 'pink', 'green'];

  const VisitRow = ({ children }: { children: React.ReactNode }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '160px ' + armLabels.map(() => '1fr').join(' ') + ' 160px', gap: 12, alignItems: 'center', minHeight: 76 }}>
      {children}
    </div>
  );

  const ArmBox = ({ tone, title, n, sub, extras }: any) => (
    <div style={{ border: `2px solid var(--t-${tone}-fg)`, background: '#fff', borderRadius: 6, padding: '8px 12px', textAlign: 'center', fontSize: 12 }}>
      <div style={{ fontWeight: 700, color: 'var(--text)' }}>{title}</div>
      {n != null && <div className="mono tabular" style={{ fontSize: 15, fontWeight: 700, color: `var(--t-${tone}-fg)` }}>N = {n}</div>}
      {sub && <div style={{ fontSize: 10.5, color: 'var(--text-2)' }}>{sub}</div>}
      {extras && extras.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', marginTop: 6, paddingTop: 6, borderTop: '1px dashed var(--border)' }}>
          {extras.map((x: string, i: number) => <span key={i} className="chip" data-tone="slate" style={{ fontSize: 9.5, padding: '0 6px' }}>{x}</span>)}
        </div>
      )}
    </div>
  );

  const SideBox = ({ text, align }: any) => (
    <div style={{ background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: 4, padding: '6px 10px', fontSize: 10.5, color: 'var(--text-2)', textAlign: align || 'left', lineHeight: 1.4 }}>
      {text}
    </div>
  );

  const DownArrow = () => (
    <div style={{ display: 'grid', gridTemplateColumns: '160px ' + armLabels.map(() => '1fr').join(' ') + ' 160px', gap: 12 }}>
      <div />
      {armLabels.map((_: any, i: number) => (
        <div key={i} style={{ display: 'grid', placeItems: 'center', padding: '4px 0' }}>
          <svg width="14" height="22" viewBox="0 0 14 22">
            <line x1="7" y1="0" x2="7" y2="16" stroke="#9E2B25" strokeWidth="2" />
            <polygon points="3,16 11,16 7,22" fill="#9E2B25" />
          </svg>
        </div>
      ))}
      <div />
    </div>
  );

  const totalSimple = form.outcomes.reduce((s: number, o: any) => s + (+o.simpleUnits || 0), 0);
  const totalComplex = form.outcomes.reduce((s: number, o: any) => s + (+o.complexUnits || 0), 0);

  return (
    <>
      <div className="sub" style={{ marginBottom: 14 }}>
        A simple CONSORT-style study flow you can screenshot or print for PI meetings. Time points run top→bottom; arms run side-by-side. Each visit shows what's collected.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ background: '#FFFFFF', border: '1px solid var(--border)', borderRadius: 10, padding: 24, boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>{form.code || 'New project'} — Study Flow</div>
            <div style={{ fontSize: 11, color: 'var(--text-2)' }}>{form.design} · N = {N}</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 160px', gap: 12, marginBottom: 0 }}>
            <SideBox text={<>{flowBoxText(form, 'excluded', 'Excluded')}<br />(failed eligibility, declined) · n = {excluded}</>} align="right" />
            <div style={{ border: '2px solid #1A1F2E', background: '#fff', borderRadius: 6, padding: '10px 14px', textAlign: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{flowBoxText(form, 'enrolled', isCohort ? 'Enrolled' : 'Randomised')}</div>
              <div className="mono tabular" style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy)' }}>N = {N}</div>
              <div style={{ fontSize: 10.5, color: 'var(--text-2)' }}>{isCohort ? 'cohort' : (form.randomise ? `1:${arms - 1} stratified · ${form.blinding}` : form.blinding)}</div>
            </div>
            <div style={{ fontSize: 10.5, color: 'var(--text-2)', alignSelf: 'center' }}>
              {flowBoxText(form, 'screened', 'Assessed for eligibility')} ≈ <span className="mono tabular">{screened}</span>
            </div>
          </div>

          <DownArrow />

          <VisitRow>
            <div />
            {armLabels.map((arm: string, i: number) => (
              <ArmBox key={i} tone={armTones[i % armTones.length]} title={arm} n={perArm} sub={isCohort ? 'cohort' : 'allocated'} />
            ))}
            <div />
          </VisitRow>

          {flowVisits.map((v: any, vi: number) => {
            const collected = itemsAtVisit(v.id);
            return (
              <React.Fragment key={v.id}>
                <DownArrow />
                <VisitRow>
                  <SideBox text={(
                    <>
                      Withdrawn / lost<br />
                      <span style={{ fontStyle: 'italic', color: 'var(--text-3)' }}>(record actuals)</span>
                    </>
                  )} align="right" />
                  {armLabels.map((_: any, ai: number) => (
                    <ArmBox
                      key={ai}
                      tone={armTones[ai % armTones.length]}
                      title={v.name}
                      n={armN(ai, vi)}
                      sub={`${visitTimeLabel(v)}${v.type === 'Primary outcome' ? ' · Primary' : v.type === 'Safety follow-up' ? ' · Safety' : ''}`}
                      extras={collected}
                    />
                  ))}
                  <SideBox text={
                    collected.length === 0
                      ? <span style={{ color: 'var(--text-3)' }}>—</span>
                      : <><div style={{ fontWeight: 600, color: 'var(--text)' }}>Collected</div>{collected.slice(0, 4).map((c: string) => <div key={c}>{c}</div>)}{collected.length > 4 && <div className="mono">+{collected.length - 4} more</div>}</>
                  } />
                </VisitRow>
              </React.Fragment>
            );
          })}

          <DownArrow />

          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 160px', gap: 12 }}>
            <div />
            <div style={{ border: '2px solid var(--accent)', background: 'var(--accent-soft)', borderRadius: 6, padding: '10px 14px', textAlign: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--accent)' }}>Analysis &amp; Summary Report</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-2)', marginTop: 2 }}>{totalSimple} simple + {totalComplex} complex analysis units</div>
              <div style={{ fontSize: 11, color: 'var(--text-2)' }}>{form.outcomes.length} outcome{form.outcomes.length === 1 ? '' : 's'} assessed</div>
            </div>
            <div />
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="card">
            <div className="card-h"><h3>Design at a glance</h3></div>
            <div className="card-b">
              <dl className="kv">
                <dt>Design</dt><dd>{form.design}</dd>
                <dt>Arms</dt><dd>{arms}</dd>
                <dt>Sample size</dt><dd className="tabular">{N}</dd>
                <dt>Blinding</dt><dd>{form.blinding}</dd>
                <dt>Time points</dt><dd>{flowVisits.length}</dd>
                <dt>Recruitment</dt><dd>{monthsToWeeks(form.recruitMonths)} weeks</dd>
                <dt>Treatment</dt><dd>{monthsToWeeks(form.treatmentMonths)} weeks</dd>
                <dt>Follow-up</dt><dd>{monthsToWeeks(form.followupMonths)} weeks</dd>
                <dt>Primary outcome</dt><dd>{(form.outcomes.find((o: any) => o.kind === 'Primary') || {}).name || '—'}</dd>
              </dl>
            </div>
          </div>
          <div className="card">
            <div className="card-h"><h3>Edit diagram text</h3></div>
            <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="field">
                <label>% screening failure</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="number" min="0" max="95" className="tabular mono" style={{ width: 90 }}
                    value={form.screenFailPct ?? ''} placeholder="33"
                    onChange={(e) => set('screenFailPct', e.target.value === '' ? undefined : Math.min(95, Math.max(0, +e.target.value || 0)))} />
                  <span style={{ fontSize: 11, color: 'var(--text-2)' }}>of those screened fail → Assessed n = <strong className="mono">{screened}</strong>, Excluded n = <strong className="mono">{excluded}</strong></span>
                </div>
              </div>
              <div style={{ fontSize: 10.5, color: 'var(--text-3)' }}>Rename any CONSORT box — the change shows here and in the PDF/Word/HTML exports:</div>
              {BOX_LABEL_FIELDS.map((b) => (
                <div className="field" key={b.id}>
                  <label>{b.label}</label>
                  <input maxLength={120} value={form.flowBoxOverrides?.[b.id] ?? b.def} onChange={(e) => setBox(b.id, e.target.value)} placeholder={b.def} />
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <div className="card-h"><h3>Share with PI</h3></div>
            <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--text-2)', marginBottom: 2 }}>
                A paper-ready <strong>CONSORT 2025</strong> participant-flow diagram for this design ({armLabels.length}-arm — works for 2, 3, or more groups), typeset with LaTeX/TikZ on the server.
              </div>
              <button className="btn primary" onClick={() => setPreviewOpen(true)}><Ico.print /> Preview &amp; download (PDF)</button>
              <button className="btn" onClick={() => downloadConsortDocx(form)}><Ico.dl /> Download (Word .docx)</button>
              <button className="btn" onClick={() => downloadConsort(form, 'html')}><Ico.dl /> Download (.html)</button>
              <div style={{ fontSize: 10.5, color: 'var(--text-3)', lineHeight: 1.5, marginTop: 4 }}>
                Filled with planned/target counts (ratio {synthArmGroups(form).map((a: any) => a.ratio || 1).join(':')}); the PI edits actual numbers as the trial runs. The <strong>PDF</strong> is the official-style LaTeX render; Word/HTML are offline fallbacks.
              </div>
            </div>
          </div>
        </div>
      </div>

      {previewOpen && (
        <Modal
          title="CONSORT 2025 flow — preview"
          wide
          onClose={() => setPreviewOpen(false)}
          footer={
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', width: '100%' }}>
              <span style={{ fontSize: 11, color: 'var(--text-3)', marginRight: 'auto' }}>Preview is the HTML render — the PDF is the same content typeset with LaTeX.</span>
              <button className="btn" onClick={() => setPreviewOpen(false)}>Cancel</button>
              <button className="btn" onClick={() => downloadConsortDocx(form)}><Ico.dl /> Word .docx</button>
              <button className="btn primary" disabled={pdfBusy} onClick={makePdf}><Ico.dl /> {pdfBusy ? 'Generating PDF…' : 'Confirm & download PDF'}</button>
            </div>
          }
        >
          <iframe title="CONSORT preview" srcDoc={previewHtml} style={{ width: '100%', height: '60vh', border: '1px solid var(--border)', borderRadius: 6, background: '#fff' }} />
        </Modal>
      )}
    </>
  );
}
